import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  Upload, Layers, Eye, EyeOff, Clock, CheckCircle, AlertTriangle,
  FileImage, ChevronRight, ChevronDown, ChevronLeft, X, History, Maximize2,
  MousePointer, ZoomIn, ZoomOut, Grid, Ruler, Crosshair, Trash2,
  Pencil, Save, PenTool, Plus, RotateCcw, ArrowLeft, List, Scissors, Loader2, Download
} from 'lucide-react';
import axios from 'axios';
import { ProjectListView, ProjectDetailView } from './ProjectList';
import ElementLists from './ElementLists';
import { useAuth } from './AuthContext';
import { useModal } from './components/CustomModal';
import RoomMiniLV from './RoomMiniLV';
import LVEditorModal from './LVEditorModal';
import API_URL from './apiConfig';

axios.defaults.withCredentials = true;

// Safe value extraction - handles {value, basis, note} objects from API
function safeNum(v) { return (v && typeof v === 'object' && 'value' in v) ? Number(v.value) || 0 : Number(v) || 0; }
function safeStr(v) { return (v && typeof v === 'object') ? (v.value != null ? String(v.value) : '') : (v != null ? String(v) : ''); }

// ─── CONSTANTS ───────────────────────────────────────────────────
const ROOM_NAME_DE = {
  'Kitchen': 'Küche', 'Living Room': 'Wohnzimmer', 'Bedroom': 'Schlafzimmer',
  'Bath': 'Bad', 'Entry': 'Flur', 'Storage': 'Abstellraum', 'Garage': 'Garage',
  'Railing': 'Geländer', 'Undefined': 'Undefiniert', 'Outdoor': 'Außen',
};
const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const translateRoom = (name) => ROOM_NAME_DE[name] || name;

// ─── API SCALE HELPERS ───────────────────────────────────────
/** Convert polygon pct coords (0-100) to real-world meter coords using API scale */
// ─── MEASUREMENT FORMATTING ──────────────────────────────────
function fmtAreaValue(room) {
  if (room.area_m2_override) return `${room.area_m2_override} m²`;
  if (room.area_m2) return `${room.area_m2} m²`;
  if (room.area_m2_calibrated) return `${room.area_m2_calibrated} m²`;
  if (room.area_text) return room.area_text;
  return '';
}

// Dynamic area from polygon coordinates using Shoelace formula + ppm calibration
// ppm_x/ppm_y are PERCENTAGE POINTS per meter (not pixels!)
// So: meters = pct / ppm. No pixel conversion needed.

// ─── WANDFLUCHT (Wall Alignment) Detection ──────────────────────
function detectWandflucht(clickedWall, allWalls, allOpenings) {
  const poly = clickedWall.polygon;
  if (!poly || poly.length < 4) return null;

  // Determine wall orientation
  const xs = poly.map(p => p[0]), ys = poly.map(p => p[1]);
  const xSpan = Math.max(...xs) - Math.min(...xs);
  const ySpan = Math.max(...ys) - Math.min(...ys);
  const isHorizontal = xSpan > ySpan;

  // Center on perpendicular axis
  const centerPerp = isHorizontal
    ? (Math.min(...ys) + Math.max(...ys)) / 2
    : (Math.min(...xs) + Math.max(...xs)) / 2;

  const wallThickPx = isHorizontal ? ySpan : xSpan;
  const PERP_TOL = Math.max(wallThickPx * 1.5, 5);

  // Get primary axis extent of an element [min, max]
  function getExtent(el) {
    if (el.polygon && el.polygon.length >= 2) {
      const coords = el.polygon.map(p => isHorizontal ? p[0] : p[1]);
      return [Math.min(...coords), Math.max(...coords)];
    }
    if (el.from_pt && el.to_pt) {
      const c1 = isHorizontal ? el.from_pt[0] : el.from_pt[1];
      const c2 = isHorizontal ? el.to_pt[0] : el.to_pt[1];
      return [Math.min(c1, c2), Math.max(c1, c2)];
    }
    return null;
  }

  function getPerpCenter(el) {
    if (el.polygon && el.polygon.length >= 2) {
      const coords = el.polygon.map(p => isHorizontal ? p[1] : p[0]);
      return (Math.min(...coords) + Math.max(...coords)) / 2;
    }
    if (el.from_pt && el.to_pt) {
      const c1 = isHorizontal ? el.from_pt[1] : el.from_pt[0];
      const c2 = isHorizontal ? el.to_pt[1] : el.to_pt[0];
      return (c1 + c2) / 2;
    }
    return null;
  }

  function getOrientation(el) {
    if (el.polygon && el.polygon.length >= 4) {
      const exs = el.polygon.map(p => p[0]), eys = el.polygon.map(p => p[1]);
      return (Math.max(...exs) - Math.min(...exs)) > (Math.max(...eys) - Math.min(...eys));
    }
    if (el.from_pt && el.to_pt) {
      return Math.abs(el.to_pt[0] - el.from_pt[0]) > Math.abs(el.to_pt[1] - el.from_pt[1]);
    }
    return null;
  }

  // Gather all candidate elements on same axis (same orientation + perpendicular match)
  const GAP_TOL = wallThickPx * 2.5; // max gap between adjacent elements
  const candidates = []; // {el, extent, type}

  for (const w of allWalls) {
    if (w.wall_type !== clickedWall.wall_type) continue;
    if (getOrientation(w) !== isHorizontal) continue;
    const pc = getPerpCenter(w);
    if (pc === null || Math.abs(pc - centerPerp) > PERP_TOL) continue;
    const ext = getExtent(w);
    if (ext) candidates.push({ el: w, extent: ext, kind: 'wall' });
  }
  for (const o of allOpenings) {
    if (!o.from_pt || !o.to_pt) continue;
    if (getOrientation(o) !== isHorizontal) continue;
    const pc = getPerpCenter(o);
    if (pc === null || Math.abs(pc - centerPerp) > PERP_TOL) continue;
    const ext = getExtent(o);
    if (ext) candidates.push({ el: o, extent: ext, kind: 'opening' });
  }

  // Sort by primary axis position
  candidates.sort((a, b) => a.extent[0] - b.extent[0]);

  // Find the clicked wall in candidates
  const clickedExt = getExtent(clickedWall);
  if (!clickedExt) return null;
  let clickedIdx = candidates.findIndex(c => c.el.id === clickedWall.id);
  if (clickedIdx < 0) return null;

  // Walk left from clicked wall
  const connected = new Set([clickedIdx]);
  let i = clickedIdx - 1;
  while (i >= 0) {
    const gap = candidates[i + 1].extent[0] - candidates[i].extent[1];
    if (gap > GAP_TOL) break;
    connected.add(i);
    i--;
  }
  // Walk right from clicked wall
  i = clickedIdx + 1;
  while (i < candidates.length) {
    const gap = candidates[i].extent[0] - candidates[i - 1].extent[1];
    if (gap > GAP_TOL) break;
    connected.add(i);
    i++;
  }

  const fluchtWalls = [];
  const fluchtOpenings = [];
  for (const idx of connected) {
    const c = candidates[idx];
    if (c.kind === 'wall') fluchtWalls.push(c.el);
    else fluchtOpenings.push(c.el);
  }

  if (fluchtWalls.length <= 1 && fluchtOpenings.length === 0) return null;

  // Calculate metrics
  let totalSegLen = 0, sumH = 0, sumD = 0, hCnt = 0;
  for (const w of fluchtWalls) {
    const l = safeNum(w.length_m_final || w.length_m);
    const h = safeNum(w.height_m);
    const d = safeNum(w.thickness_m_final || w.thickness_m);
    totalSegLen += l;
    if (h > 0) { sumH += h; hCnt++; }
    if (d > 0) sumD += d;
  }
  const avgH = hCnt > 0 ? sumH / hCnt : 0;
  const avgD = hCnt > 0 ? sumD / hCnt : 0;

  let totalOpW = 0, totalOpArea = 0;
  for (const o of fluchtOpenings) {
    const w = safeNum(o.width_m_final || o.width_m);
    const h = safeNum(o.height_m_matched || o.height_m);
    totalOpW += w;
    if (w > 0 && h > 0) totalOpArea += w * h;
  }

  const totalRun = totalSegLen + totalOpW;
  const bruttoArea = avgH > 0 ? totalRun * avgH : 0;
  const nettoArea = Math.max(0, bruttoArea - totalOpArea);
  const nettoVol = avgD > 0 ? nettoArea * avgD : 0;

  return {
    wallIds: fluchtWalls.map(w => w.id),
    openingIds: fluchtOpenings.map(o => o.id),
    totalRun, nettoArea, nettoVol,
    wallCount: fluchtWalls.length,
    openingCount: fluchtOpenings.length,
  };
}


function computePolygonAreaM2(polygon, imgW, imgH, ppmX, ppmY) {
  if (!polygon || polygon.length < 3 || !ppmX || !ppmY) return null;
  const pts = polygon.map(([px, py]) => [px / ppmX, py / ppmY]);
  let area = 0;
  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length;
    area += pts[i][0] * pts[j][1] - pts[j][0] * pts[i][1];
  }
  return Math.round(Math.abs(area / 2) * 100) / 100;
}

// Derive correct color from element type (fixes stale DB colors after type change)
function resolveOpeningColor(element) {
  if (element.type === 'window') return '#22c55e';
  if (element.type === 'door') return '#f59e0b';
  return element.color || '#94a3b8';
}

function fmtLengthValue(line) {
  if (line.length_m) return `${line.length_m} m`;
  if (line.length_m_calibrated) return `${line.length_m_calibrated} m`;
  if (line.length_m_final) return `${line.length_m_final} m`;
  return '';
}

function fmtThicknessValue(wall) {
  if (wall.thickness_source === 'manual' || wall.thickness_source === 'propagated') {
    return wall.thickness_m ? `${(safeNum(wall.thickness_m) * 100).toFixed(1)} cm` : '';
  }
  const t = safeNum(wall.thickness_m_final || wall.thickness_m || wall.thickness_m_calibrated);
  if (t) return `${(t * 100).toFixed(1)} cm`;
  return '';
}

function fmtOpeningWidth(opening) {
  if (opening.width_source === 'manual' || opening.width_source === 'propagated') {
    return opening.width_m_matched ? `${opening.width_m_matched} m` : '';
  }
  if (opening.width_m_matched) return `${opening.width_m_matched} m`;
  if (opening.width_m) return `${opening.width_m} m`;
  if (opening.width_m_calibrated) return `${opening.width_m_calibrated} m`;
  return '';
}

/** Convert a polygon point from pct to meters using ppm */
function pctToMeters(pctX, pctY, ppmX, ppmY) {
  if (!ppmX || !ppmY) return null;
  return [pctX / ppmX, pctY / ppmY];
}
/** Compute shoelace area of a polygon given as [[x,y], ...] */
function shoelaceArea(points) {
  let area = 0;
  const n = points.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += points[i][0] * points[j][1];
    area -= points[j][0] * points[i][1];
  }
  return Math.abs(area) / 2;
}

/** Compute perimeter of a polygon given as [[x,y], ...] */
function polygonPerimeter(points) {
  let perimeter = 0;
  const n = points.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const dx = points[j][0] - points[i][0];
    const dy = points[j][1] - points[i][1];
    perimeter += Math.sqrt(dx * dx + dy * dy);
  }
  return perimeter;
}

// ─── TERMINAL LOADING ────────────────────────────────────────────
const LOADING_STEPS = [
  { text: '> Initializing LAIzyAnalyze analysis engine...', delay: 0 },
  { text: '> Uploading document to LAIzyAnalyze...', delay: 800 },
  { text: '> Rendering document pages...', delay: 3000 },
  { text: '> Detecting floor plans...', delay: 8000 },
  { text: '> Processing rooms & boundaries...', delay: 15000 },
  { text: '> Analyzing walls & openings...', delay: 25000 },
  { text: '> Extracting dimensions & scale...', delay: 35000 },
  { text: '> Generating polygon overlays...', delay: 45000 },
  { text: '> Finalizing report...', delay: 55000 },
];

function TerminalLoading({ startTime }) {
  const [visibleSteps, setVisibleSteps] = useState([]);
  const termRef = useRef(null);
  useEffect(() => {
    const timers = LOADING_STEPS.map((step, i) =>
      setTimeout(() => setVisibleSteps(prev => [...prev, step.text]), step.delay)
    );
    return () => timers.forEach(clearTimeout);
  }, []);
  useEffect(() => { if (termRef.current) termRef.current.scrollTop = termRef.current.scrollHeight; }, [visibleSteps]);
  const elapsed = startTime ? ((Date.now() - startTime) / 1000).toFixed(1) : '0.0';
  return (
    <div data-testid="terminal-loading" className="w-full max-w-2xl mx-auto">
      <div className="border border-zinc-700 bg-zinc-900">
        <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-700 bg-zinc-950">
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-500"></div>
            <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
          </div>
          <span className="font-mono text-xs text-zinc-500">blueprint-analyst</span>
          <span className="font-mono text-xs text-blue-400">{elapsed}s</span>
        </div>
        <div ref={termRef} className="p-4 h-64 overflow-y-auto font-mono text-xs leading-relaxed">
          {visibleSteps.map((step, i) => (
            <div key={i} className="text-zinc-400 mb-1" style={{animation: 'fadeIn 0.3s ease forwards'}}>
              {step}
              {i < visibleSteps.length - 1 && <span className="text-green-500 ml-2">OK</span>}
            </div>
          ))}
          {visibleSteps.length > 0 && <div className="text-zinc-500 cursor-blink mt-1"></div>}
        </div>
        <div className="px-4 py-2 border-t border-zinc-700">
          <div className="w-full bg-zinc-950 h-1.5 overflow-hidden">
            <div className="h-full bg-blue-500 transition-all duration-1000 ease-out"
              style={{ width: `${Math.min((visibleSteps.length / LOADING_STEPS.length) * 100, 95)}%` }}></div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── DROPZONE ────────────────────────────────────────────────────
function DropZone({ onFile }) {
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef(null);
  const handleDrop = useCallback((e) => { e.preventDefault(); setIsDragOver(false); if (e.dataTransfer.files[0]) onFile(e.dataTransfer.files[0]); }, [onFile]);
  return (
    <div data-testid="dropzone"
      onDrop={handleDrop}
      onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
      onDragLeave={() => setIsDragOver(false)}
      onClick={() => inputRef.current?.click()}
      className={`relative cursor-pointer border-2 border-dashed transition-all duration-200
        grid-bg p-12 md:p-20 flex flex-col items-center justify-center gap-6
        ${isDragOver ? 'border-blue-500 bg-blue-500/5 scale-[1.01]' : 'border-zinc-700 hover:border-zinc-500 hover:bg-zinc-800/50'}`}>
      <input ref={inputRef} type="file" accept="image/*,.pdf,.docx,.doc,.rtf,.odt,.html,.htm,.txt,.epub,.fb2"
        className="hidden" data-testid="file-input" onChange={(e) => e.target.files[0] && onFile(e.target.files[0])} />
      <div className={`p-4 border ${isDragOver ? 'border-blue-500' : 'border-zinc-700'} transition-colors`}>
        <Upload className={`w-8 h-8 ${isDragOver ? 'text-blue-500' : 'text-zinc-500'}`} />
      </div>
      <div className="text-center">
        <p className="font-mono text-sm text-zinc-300 mb-1">{isDragOver ? 'Loslassen zum Analysieren' : 'Grundriss hier ablegen'}</p>
        <p className="font-mono text-xs text-zinc-500">Bilder, PDF, DOCX, RTF, ODT & mehr</p>
      </div>
    </div>
  );
}

// ─── SVG INTERACTIVE VIEWPORT ────────────────────────────────────
function SVGViewport({ result, activeLayers, selectedElements, onSelectElement,
                       calibrationMode, calibrationPoints, onCalibrationClick, scale, scaleInfo, ppmX, ppmY,
                       editMode, onVertexMove, onEdgeMove, onPolygonMove, onAddVertex, onDeleteVertex, onDragStart, localSvgOverrides,
                       drawingMode, drawingPoints, onDrawingClick,
                       showElementLists, onToggleElementLists, darkMode }) {
  const tm = darkMode ?? true; // default dark
  const isElemSelected = useCallback((id) => selectedElements.some(el => el.id === id), [selectedElements]);
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [viewBox, setViewBox] = useState(null);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState(null);
  const [panMouseStart, setPanMouseStart] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [showGrid, setShowGrid] = useState(false);
  const [dragVertex, setDragVertex] = useState(null); // { elementId, vertexIndex } or { elementId, mode: 'edge', edgeIndex } or { elementId, mode: 'polygon', startPct }
  const [dragMousePct, setDragMousePct] = useState(null); // [pctX, pctY] for magnifier

  const svgData = result?.svg_data;
  const imgW = result?.image_size?.width || 800;
  const imgH = result?.image_size?.height || 600;
  useEffect(() => {
    setViewBox(`0 0 ${imgW} ${imgH}`);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  }, [imgW, imgH]);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom(prev => Math.max(0.5, Math.min(5, prev * delta)));
  }, []);

  const handleMouseDown = useCallback((e) => {
    if (calibrationMode || dragVertex) return;
    if (e.button === 0 && !drawingMode) {
      setPanMouseStart({ x: e.clientX, y: e.clientY });
      setPanStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
    }
  }, [offset, calibrationMode, dragVertex, drawingMode]);

  const getSvgPctFromEvent = useCallback((e) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgPt = pt.matrixTransform(svg.getScreenCTM().inverse());
    return [svgPt.x / imgW * 100, svgPt.y / imgH * 100];
  }, [imgW, imgH]);

  const handleMouseMove = useCallback((e) => {
    if (dragVertex) {
      const pct = getSvgPctFromEvent(e);
      if (pct) {
        if (dragVertex.mode === 'edge') {
          // Edge drag: move both vertices of the edge
          onEdgeMove(dragVertex.elementId, dragVertex.edgeIndex, pct, dragVertex.lastPct);
          setDragVertex(prev => ({ ...prev, lastPct: pct }));
        } else if (dragVertex.mode === 'polygon') {
          // Polygon drag: move all vertices
          onPolygonMove(dragVertex.elementId, pct, dragVertex.lastPct);
          setDragVertex(prev => ({ ...prev, lastPct: pct }));
        } else {
          // Single vertex drag (existing)
          onVertexMove(dragVertex.elementId, dragVertex.vertexIndex, pct);
        }
        setDragMousePct(pct);
      }
      return;
    }
    if (panStart && panMouseStart) {
      const dx = e.clientX - panMouseStart.x;
      const dy = e.clientY - panMouseStart.y;
      if (!isPanning && Math.sqrt(dx * dx + dy * dy) > 5) {
        setIsPanning(true);
      }
      if (isPanning || Math.sqrt(dx * dx + dy * dy) > 5) {
        setOffset({ x: e.clientX - panStart.x, y: e.clientY - panStart.y });
      }
    }
  }, [isPanning, panStart, panMouseStart, dragVertex, getSvgPctFromEvent, onVertexMove]);

  const handleMouseUp = useCallback((e) => {
    const wasPanning = isPanning;
    setIsPanning(false);
    setPanStart(null);
    setPanMouseStart(null);
    if (dragVertex) { setDragVertex(null); setDragMousePct(null); return; }
    // Set ref so the subsequent click event knows to suppress
    if (wasPanning) wasPanningRef.current = true;
  }, [dragVertex, isPanning]);
  const resetView = useCallback(() => { setZoom(1); setOffset({ x: 0, y: 0 }); }, []);

  const wasPanningRef = useRef(false);

  const handleSvgClick = useCallback((e) => {
    // If we just finished panning, ignore the click
    if (wasPanningRef.current) { wasPanningRef.current = false; return; }
    // Drawing mode: add point on click, finish on double-click
    if (drawingMode) {
      const pct = getSvgPctFromEvent(e);
      if (pct) onDrawingClick(pct);
      return;
    }
    if (!calibrationMode) {
      if (!editMode) onSelectElement(null, e);
      return;
    }
    const svg = svgRef.current;
    if (!svg) return;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgPt = pt.matrixTransform(svg.getScreenCTM().inverse());
    onCalibrationClick({ x: svgPt.x, y: svgPt.y });
  }, [calibrationMode, onCalibrationClick, onSelectElement, drawingMode, onDrawingClick, getSvgPctFromEvent, editMode]);

  if (!result) return null;

  const pctToSvg = (pctX, pctY) => [pctX / 100 * imgW, pctY / 100 * imgH];
  const polyToPath = (polygon) => {
    if (!polygon || polygon.length < 3) return '';
    const points = polygon.map(p => pctToSvg(p[0], p[1]));
    return `M ${points.map(p => `${p[0]},${p[1]}`).join(' L ')} Z`;
  };

  const fmtArea = (room, polygon) => {
    // If polygon is overridden (vertex dragged), compute area dynamically
    if (polygon && (!room.area_m2 && !room.area_m2_override && !room.area_m2_calibrated)) {
      const cal = result?.svg_data?.calibration || {};
      const area = computePolygonAreaM2(polygon, imgW, imgH, cal.ppm_x, cal.ppm_y);
      if (area != null) return `${area} m²`;
    }
    return fmtAreaValue(room);
  };
  const fmtLength = (line) => fmtLengthValue(line);

  // Door arc SVG path: circular arc from closed to open with center at hinge
  // Uses single radius (hinge→closed distance) and reprojects open_pt onto that circle
  const doorArcPath = (door) => {
    if (!door.hinge || !door.closed_pt || !door.open_pt) return null;
    const [hx, hy] = pctToSvg(door.hinge[0], door.hinge[1]);
    const [cx, cy] = pctToSvg(door.closed_pt[0], door.closed_pt[1]);
    const [ox, oy] = pctToSvg(door.open_pt[0], door.open_pt[1]);
    // Single radius from hinge→closed (the reliable door width measurement)
    const r = Math.sqrt((cx - hx) ** 2 + (cy - hy) ** 2);
    if (r < 1) return null;
    // Reproject open_pt onto circle of radius r (keep direction, fix distance)
    const odx = ox - hx, ody = oy - hy;
    const odist = Math.sqrt(odx * odx + ody * ody) || 1;
    const ox2 = hx + (odx / odist) * r;
    const oy2 = hy + (ody / odist) * r;
    return { hx, hy, cx, cy, ox: ox2, oy: oy2, r };
  };

  return (
    <div data-testid="svg-viewport" className="relative w-full h-full bg-zinc-950 overflow-hidden flex flex-col">
      {/* Toolbar */}
      <div className={`flex items-center gap-1 px-2 py-1.5 border-b flex-shrink-0 ${tm ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`}>
        <button onClick={() => setZoom(z => Math.min(5, z * 1.2))} data-testid="zoom-in-btn"
          className={`p-1.5 transition-colors ${tm ? 'hover:bg-zinc-800 text-zinc-400 hover:text-white' : 'hover:bg-zinc-100 text-zinc-500 hover:text-zinc-800'}`} title="Zoom In">
          <ZoomIn className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => setZoom(z => Math.max(0.5, z * 0.8))} data-testid="zoom-out-btn"
          className={`p-1.5 transition-colors ${tm ? 'hover:bg-zinc-800 text-zinc-400 hover:text-white' : 'hover:bg-zinc-100 text-zinc-500 hover:text-zinc-800'}`} title="Zoom Out">
          <ZoomOut className="w-3.5 h-3.5" />
        </button>
        <button onClick={resetView} data-testid="reset-view-btn"
          className={`p-1.5 transition-colors ${tm ? 'hover:bg-zinc-800 text-zinc-400 hover:text-white' : 'hover:bg-zinc-100 text-zinc-500 hover:text-zinc-800'}`} title="Ansicht zurücksetzen">
          <Maximize2 className="w-3.5 h-3.5" />
        </button>
        <div className={`w-px h-4 mx-1 ${tm ? 'bg-zinc-700' : 'bg-zinc-200'}`}></div>
        <button onClick={() => setShowGrid(g => !g)} data-testid="toggle-grid-btn"
          className={`p-1.5 transition-colors ${showGrid ? 'bg-blue-500/20 text-blue-500' : tm ? 'hover:bg-zinc-800 text-zinc-400 hover:text-white' : 'hover:bg-zinc-100 text-zinc-500 hover:text-zinc-800'}`}
          title="Gitter ein/aus">
          <Grid className="w-3.5 h-3.5" />
        </button>
        <button onClick={onToggleElementLists} data-testid="toggle-element-lists-btn"
          className={`p-1.5 transition-colors ${showElementLists ? 'bg-blue-500/20 text-blue-500' : tm ? 'hover:bg-zinc-800 text-zinc-400 hover:text-white' : 'hover:bg-zinc-100 text-zinc-500 hover:text-zinc-800'}`}
          title="Bauteilliste">
          <List className="w-3.5 h-3.5" />
        </button>
        <PdfExportButton analysisId={result?.analysis_id || result?.id} />
        <div className="flex-1"></div>
        {calibrationMode && (
          <span className="font-mono text-[10px] text-amber-400 px-2 py-0.5 bg-amber-500/10 border border-amber-500/30 mr-2">
            <Crosshair className="w-3 h-3 inline mr-1" />
            {calibrationMode === 'point1' ? 'Punkt 1 setzen' : 'Punkt 2 setzen'}
          </span>
        )}
        {editMode && (
          <span className="font-mono text-[10px] text-amber-400 px-2 py-0.5 bg-amber-500/10 border border-amber-500/30 mr-2">
            <Pencil className="w-3 h-3 inline mr-1" />
            Bearbeitungsmodus
          </span>
        )}
        {drawingMode && (
          <span className="font-mono text-[10px] text-purple-400 px-2 py-0.5 bg-purple-500/10 border border-purple-500/30 mr-2">
            <PenTool className="w-3 h-3 inline mr-1" />
            {drawingMode === 'wall' ? 'Wand zeichnen' : drawingMode === 'window' ? 'Fenster zeichnen' : drawingMode === 'door' ? 'Tür zeichnen' : 'Raum zeichnen'} ({drawingPoints.length} Punkte)
          </span>
        )}
        {scale && (
          <span className="font-mono text-[10px] text-green-400 mr-2" data-testid="scale-indicator">
            1px = {(1/scale).toFixed(4)}m
          </span>
        )}
        {!scale && scaleInfo && (
          <span className="font-mono text-[10px] text-cyan-400 mr-2" data-testid="api-scale-indicator">
            {ppmX ? `${ppmX.toFixed(2)} %/m` : 'API-Skala'}
          </span>
        )}
        <span className="font-mono text-[10px] text-zinc-500">{Math.round(zoom * 100)}%</span>
        <span className="font-mono text-[10px] text-zinc-600 ml-2">{imgW}x{imgH}px</span>
      </div>

      {/* SVG Canvas */}
      <div ref={containerRef} className={`flex-1 relative overflow-hidden ${tm ? 'bg-zinc-950' : 'bg-zinc-100'}`}
        onWheel={handleWheel} onMouseDown={handleMouseDown} onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}
        style={{ cursor: drawingMode ? 'crosshair' : calibrationMode ? 'crosshair' : (dragVertex ? 'grabbing' : isPanning ? 'grabbing' : editMode ? 'default' : 'grab') }}>
        <div style={{
          transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
          transformOrigin: 'center center',
          transition: isPanning ? 'none' : 'transform 0.15s ease-out',
          width: '100%', height: '100%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg ref={svgRef} viewBox={viewBox} className="max-w-full max-h-full"
            style={{ width: '100%', height: '100%' }}
            preserveAspectRatio="xMidYMid meet"
            onClick={handleSvgClick}>
            {/* Original floor plan image */}
            <image href={`data:image/png;base64,${result.original_image}`}
              x="0" y="0" width={imgW} height={imgH} />

            {/* Grid overlay */}
            {showGrid && (
              <g data-testid="grid-overlay" opacity="0.15">
                {Array.from({ length: Math.ceil(imgW / 50) }, (_, i) => (
                  <line key={`gv${i}`} x1={i * 50} y1={0} x2={i * 50} y2={imgH} stroke="#3b82f6" strokeWidth="0.5" />
                ))}
                {Array.from({ length: Math.ceil(imgH / 50) }, (_, i) => (
                  <line key={`gh${i}`} x1={0} y1={i * 50} x2={imgW} y2={i * 50} stroke="#3b82f6" strokeWidth="0.5" />
                ))}
              </g>
            )}

            {/* Bitmap layers (legacy) */}
            {activeLayers.walls && result.layers?.walls && (
              <image href={`data:image/png;base64,${result.layers.walls}`} x="0" y="0" width={imgW} height={imgH} opacity="0.6" />
            )}
            {activeLayers.rooms && result.layers?.rooms && (
              <image href={`data:image/png;base64,${result.layers.rooms}`} x="0" y="0" width={imgW} height={imgH} opacity="0.5" />
            )}
            {activeLayers.windows && result.layers?.windows && (
              <image href={`data:image/png;base64,${result.layers.windows}`} x="0" y="0" width={imgW} height={imgH} opacity="0.6" />
            )}
            {activeLayers.doors && result.layers?.doors && (
              <image href={`data:image/png;base64,${result.layers.doors}`} x="0" y="0" width={imgW} height={imgH} opacity="0.6" />
            )}
            {activeLayers.fixtures && result.layers?.fixtures && (
              <image href={`data:image/png;base64,${result.layers.fixtures}`} x="0" y="0" width={imgW} height={imgH} opacity="0.6" />
            )}

            {/* === WALL POLYGONS (filled shapes showing true wall thickness) === */}
            {activeLayers.wallPolygons && svgData?.wall_lines?.map((wall, i) => {
              if (!wall.polygon || wall.polygon.length < 3) return null;
              const path = polyToPath(wall.polygon);
              if (!path) return null;
              const wallId = wall.id || `wall_${i}`;
              const selected = isElemSelected(wallId);
              return (
                <path key={`wp${i}`} d={path}
                  fill={selected ? '#1e40af' : '#1e293b'} fillOpacity={selected ? 0.7 : 0.55}
                  stroke={selected ? '#3b82f6' : '#334155'} strokeWidth={selected ? 2 : 0.8}
                  onClick={(e) => {
                    if (calibrationMode || drawingMode) return;
                    e.stopPropagation();
                    const wallName = wall.name || `Wand ${i + 1}`;
                    onSelectElement({
                      ...wall, id: wallId, type: 'wall',
                      name: wallName, label: wallName, color: '#3b82f6',
                    }, e);
                  }}
                  style={{ cursor: (calibrationMode || drawingMode) ? 'crosshair' : 'pointer' }} />
              );
            })}


            {/* === WALL TYPE OVERLAY (Außenwand/Innenwand) === */}
            {activeLayers.wallTypes && svgData?.wall_lines?.map((wall, i) => {
              if (!wall.polygon || wall.polygon.length < 3 || !wall.wall_type) return null;
              const path = polyToPath(wall.polygon);
              if (!path) return null;
              const isExterior = wall.wall_type === 'exterior';
              return (
                <path key={`wt${i}`} d={path} data-testid={`wall-type-${wall.wall_type}-${i}`}
                  fill={isExterior ? '#ef4444' : '#3b82f6'} fillOpacity={0.35}
                  stroke={isExterior ? '#f87171' : '#60a5fa'} strokeWidth={1.5} strokeOpacity={0.8}
                  pointerEvents="none" />
              );
            })}

            {/* === ENVELOPE OVERLAY === */}
            {/* Envelope layer moved after all interactive elements for proper z-order – rendered above rooms/walls/openings */}


            {/* === ROOM POLYGONS (interactive, colored fills) === */}
            {activeLayers.roomPolygons && svgData?.rooms?.map((room) => {
              const selected = isElemSelected(room.id);
              const overridePoly = localSvgOverrides[room.id];
              const polygon = overridePoly || room.polygon;
              const path = polyToPath(polygon);
              if (!path) return null;
              // Use override-cleared room for area calc so it recalculates from polygon
              const roomForArea = overridePoly
                ? { ...room, polygon, area_m2_calibrated: null, area_m2: null, area_text: null, area_relative: null }
                : room;
              const areaStr = fmtArea(roomForArea, overridePoly ? polygon : null);
              const centroid = overridePoly && polygon.length >= 3
                ? [polygon.reduce((s, p) => s + p[0], 0) / polygon.length,
                   polygon.reduce((s, p) => s + p[1], 0) / polygon.length]
                : room.centroid;
              return (
                <g key={room.id} data-testid={`room-polygon-${room.id}`}
                  onClick={(e) => {
                    if (calibrationMode || drawingMode) return; // Let SVG handle drawing clicks
                    e.stopPropagation();
                    onSelectElement(room, e);
                  }}
                  style={{ cursor: (calibrationMode || drawingMode) ? 'crosshair' : 'pointer' }}>
                  <path d={path}
                    fill={room.color} fillOpacity={selected ? 0.40 : 0.15}
                    stroke={room.color} strokeWidth={selected ? 2.5 : 1.2} strokeOpacity={selected ? 1 : 0.5} />
                  {centroid && (() => {
                    const [cx, cy] = pctToSvg(centroid[0], centroid[1]);
                    const badgeScale = imgW / 1800 / zoom;
                    const label = areaStr ? `${room.letter}  ${areaStr}` : room.letter;
                    const tw = label.length * 5.8 + 14;
                    const hw = tw / 2;
                    return (
                      <g transform={`translate(${cx},${cy}) scale(${badgeScale})`}>
                        <rect x={-hw} y="-10" width={tw} height="20" rx="10"
                          fill="rgba(0,0,0,0.78)" stroke={selected ? room.color : 'rgba(255,255,255,0.10)'} strokeWidth="0.7" />
                        <text textAnchor="middle" y="4" fill="white"
                          fontSize="11" fontFamily="monospace" fontWeight="600">
                          <tspan fill="white" fontWeight="700">{room.letter}</tspan>
                          {areaStr && <tspan fill={room.color} fontWeight="600">{`  ${areaStr}`}</tspan>}
                        </text>
                      </g>
                    );
                  })()}
                </g>
              );
            })}

            {/* === WALL CENTER LINES (interactive) === */}
            {activeLayers.wallLines && svgData?.wall_lines?.map((line, i) => {
              const [x1, y1] = pctToSvg(line.start[0], line.start[1]);
              const [x2, y2] = pctToSvg(line.end[0], line.end[1]);
              const wallId = line.id || `wall_${i}`;
              const selected = isElemSelected(wallId);
              const wallName = line.name || `Wand ${i + 1}`;
              const lengthStr = fmtLength(line);
              return (
                <g key={`wl${i}`} data-testid={`wall-line-${i}`}
                  onClick={(e) => {
                    if (calibrationMode || drawingMode) return;
                    e.stopPropagation();
                    onSelectElement({
                      ...line, id: wallId, type: 'wall',
                      name: wallName, label: wallName, color: '#3b82f6',
                    }, e);
                  }}
                  style={{ cursor: (calibrationMode || drawingMode) ? 'crosshair' : 'pointer' }}>
                  <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="transparent" strokeWidth="8" />
                  <line x1={x1} y1={y1} x2={x2} y2={y2}
                    stroke={selected ? '#60a5fa' : '#475569'}
                    strokeWidth={selected ? 2.5 : 1}
                    strokeOpacity={selected ? 1 : 0.6}
                    strokeDasharray={selected ? 'none' : '3,2'} />
                  {selected && lengthStr && (
                    <g>
                      <rect x={(x1+x2)/2 - 30} y={(y1+y2)/2 - 18} width="60" height="16" rx="2"
                        fill="rgba(0,0,0,0.85)" />
                      <text x={(x1+x2)/2} y={(y1+y2)/2 - 6}
                        textAnchor="middle" fill="#60a5fa"
                        fontSize="9" fontFamily="monospace" fontWeight="bold">
                        {lengthStr}
                      </text>
                    </g>
                  )}
                </g>
              );
            })}

            {/* === WINDOWS (parallel lines between from and to) === */}
            {activeLayers.openings && svgData?.openings?.filter(o => o.type === 'window').map((win) => {
              const selected = isElemSelected(win.id);
              if (win.from_pt && win.to_pt) {
                const [fx, fy] = pctToSvg(win.from_pt[0], win.from_pt[1]);
                const [tx, ty] = pctToSvg(win.to_pt[0], win.to_pt[1]);
                const dx = tx - fx;
                const dy = ty - fy;
                const len = Math.sqrt(dx * dx + dy * dy);
                if (len < 1) return null;
                const nx = -dy / len * 6;
                const ny = dx / len * 6;
                return (
                  <g key={win.id} data-testid={`opening-${win.id}`}
                    onClick={(e) => {
                      if (calibrationMode || drawingMode) return;
                      e.stopPropagation();
                      onSelectElement(win, e);
                    }}
                    style={{ cursor: (calibrationMode || drawingMode) ? 'crosshair' : 'pointer' }}>
                    <line x1={fx} y1={fy} x2={tx} y2={ty} stroke="transparent" strokeWidth="18" />
                    <line x1={fx + nx*1.2} y1={fy + ny*1.2} x2={tx + nx*1.2} y2={ty + ny*1.2}
                      stroke={selected ? '#4ade80' : '#22c55e'} strokeWidth={selected ? 2.5 : 1.5} strokeOpacity={selected ? 1 : 0.8} />
                    <line x1={fx} y1={fy} x2={tx} y2={ty}
                      stroke={selected ? '#4ade80' : '#22c55e'} strokeWidth={selected ? 2.5 : 1.5} strokeOpacity={selected ? 1 : 0.8} />
                    <line x1={fx - nx*1.2} y1={fy - ny*1.2} x2={tx - nx*1.2} y2={ty - ny*1.2}
                      stroke={selected ? '#4ade80' : '#22c55e'} strokeWidth={selected ? 2.5 : 1.5} strokeOpacity={selected ? 1 : 0.8} />
                    <line x1={fx + nx*1.5} y1={fy + ny*1.5} x2={fx - nx*1.5} y2={fy - ny*1.5}
                      stroke={selected ? '#4ade80' : '#22c55e'} strokeWidth={selected ? 2 : 1} strokeOpacity={0.6} />
                    <line x1={tx + nx*1.5} y1={ty + ny*1.5} x2={tx - nx*1.5} y2={ty - ny*1.5}
                      stroke={selected ? '#4ade80' : '#22c55e'} strokeWidth={selected ? 2 : 1} strokeOpacity={0.6} />
                  </g>
                );
              }
              const [cx, cy] = pctToSvg(win.position[0], win.position[1]);
              return (
                <g key={win.id} data-testid={`opening-${win.id}`}
                  onClick={(e) => { if (!calibrationMode && !drawingMode) { e.stopPropagation(); onSelectElement(win, e); } }}
                  style={{ cursor: (calibrationMode || drawingMode) ? 'crosshair' : 'pointer' }}>
                  <circle cx={cx} cy={cy} r={5} fill={selected ? '#22c55e' : 'rgba(0,0,0,0.5)'}
                    stroke="#22c55e" strokeWidth={selected ? 2 : 1} />
                  <text x={cx} y={cy + 3} textAnchor="middle" fill="white" fontSize="6" fontFamily="monospace" fontWeight="bold">W</text>
                </g>
              );
            })}

            {/* === DOORS (quarter circle arcs showing swing direction) === */}
            {activeLayers.openings && svgData?.openings?.filter(o => o.type === 'door').map((door) => {
              const selected = isElemSelected(door.id);
              const arc = doorArcPath(door);
              if (arc) {
                const { hx, hy, cx, cy, ox, oy, r } = arc;
                const cross = (cx - hx) * (oy - hy) - (cy - hy) * (ox - hx);
                const sweep = cross > 0 ? 1 : 0;
                const arcPath = `M ${cx},${cy} A ${r},${r} 0 0 ${sweep} ${ox},${oy}`;
                return (
                  <g key={door.id} data-testid={`opening-${door.id}`}
                    onClick={(e) => {
                      if (calibrationMode || drawingMode) return;
                      e.stopPropagation();
                      onSelectElement(door, e);
                    }}
                    style={{ cursor: (calibrationMode || drawingMode) ? 'crosshair' : 'pointer' }}>
                    {/* Hit target: thick invisible stroke along the arc + hinge line only */}
                    <path d={arcPath}
                      fill="none" stroke="transparent" strokeWidth={12} />
                    <line x1={hx} y1={hy} x2={cx} y2={cy}
                      stroke="transparent" strokeWidth={10} />
                    <path d={arcPath}
                      fill="none"
                      stroke={selected ? '#fbbf24' : '#f59e0b'}
                      strokeWidth={selected ? 2.5 : 1.5}
                      strokeOpacity={selected ? 1 : 0.7}
                      strokeDasharray={selected ? 'none' : '4,2'} />
                    <line x1={hx} y1={hy} x2={cx} y2={cy}
                      stroke={selected ? '#fbbf24' : '#f59e0b'}
                      strokeWidth={selected ? 2.5 : 1.5}
                      strokeOpacity={selected ? 1 : 0.7} />
                    <circle cx={hx} cy={hy} r={selected ? 3 : 2}
                      fill={selected ? '#fbbf24' : '#f59e0b'} fillOpacity={0.9} />
                  </g>
                );
              }
              const [pcx, pcy] = pctToSvg(door.position[0], door.position[1]);
              return (
                <g key={door.id} data-testid={`opening-${door.id}`}
                  onClick={(e) => { if (!calibrationMode && !drawingMode) { e.stopPropagation(); onSelectElement(door, e); } }}
                  style={{ cursor: (calibrationMode || drawingMode) ? 'crosshair' : 'pointer' }}>
                  <circle cx={pcx} cy={pcy} r={6} fill={selected ? '#f59e0b' : 'rgba(0,0,0,0.5)'}
                    stroke="#f59e0b" strokeWidth={selected ? 2 : 1.5} />
                  <text x={pcx} y={pcy + 3} textAnchor="middle" fill="white" fontSize="7" fontFamily="monospace" fontWeight="bold">D</text>
                </g>
              );
            })}

            {activeLayers.dimensions && svgData?.dimensions?.map((dim, i) => {
              const [sx, sy] = pctToSvg(dim.start[0], dim.start[1]);
              const [ex, ey] = pctToSvg(dim.end[0], dim.end[1]);
              return (
                <g key={`dim${i}`} opacity="0.5">
                  <line x1={sx} y1={sy} x2={ex} y2={ey}
                    stroke="#94a3b8" strokeWidth="0.8" />
                  <circle cx={sx} cy={sy} r="1.5" fill="#94a3b8" />
                  <circle cx={ex} cy={ey} r="1.5" fill="#94a3b8" />
                </g>
              );
            })}

            {/* === BEMAESSUNGSGITTER (Dimension Grid Overlay) === */}
            {activeLayers.dimensionGrid && svgData?.dimension_grid && (() => {
              const dg = svgData.dimension_grid;
              const gridStroke = Math.max(imgW, imgH) * 0.0004;
              const segLabelSize = Math.max(imgW, imgH) * 0.002;
              const DEDUP_TOL = 1.0; // % tolerance for deduplicating close tick marks

              // Use ONLY the longest chain per axis (most complete dimension line)
              // x_chains are already sorted longest first
              const mainXChain = (dg.x_chains || [])[0];
              const mainYChain = (dg.y_chains || [])[0];

              // Compute tick positions from a single chain
              const getTicksFromChain = (chain) => {
                if (!chain || !chain.points) return [];
                return [...chain.points]
                  .sort((a, b) => a.pct - b.pct)
                  .filter((p, i, arr) => i === 0 || Math.abs(p.pct - arr[i-1].pct) > DEDUP_TOL);
              };

              const xTicks = getTicksFromChain(mainXChain);
              const yTicks = getTicksFromChain(mainYChain);

              return (
                <g opacity="0.7">
                  {/* Vertical grid lines from main H-chain tick marks */}
                  {xTicks.map((pt, i) => {
                    const [sx] = pctToSvg(pt.pct, 0);
                    return (
                      <line key={`gx${i}`} x1={sx} y1={0} x2={sx} y2={imgH}
                        stroke="#60a5fa" strokeWidth={gridStroke}
                        strokeDasharray={`${gridStroke * 8},${gridStroke * 4}`}
                        opacity="0.5" />
                    );
                  })}
                  {/* Vertical segment labels */}
                  {xTicks.map((pt, i) => {
                    if (i === 0) return null;
                    const prev = xTicks[i - 1];
                    const segM = Math.abs(pt.meters - prev.meters);
                    if (segM < 0.05) return null;
                    const [mx] = pctToSvg((pt.pct + prev.pct) / 2, 0);
                    return (
                      <text key={`xsl${i}`} x={mx} y={imgH * 0.025}
                        fill="#93c5fd" fontSize={segLabelSize} fontFamily="monospace"
                        textAnchor="middle" fontWeight="bold" opacity="0.9">
                        {segM.toFixed(2)}m
                      </text>
                    );
                  })}
                  {/* Horizontal grid lines from main V-chain tick marks */}
                  {yTicks.map((pt, i) => {
                    const [, sy] = pctToSvg(0, pt.pct);
                    return (
                      <line key={`gy${i}`} x1={0} y1={sy} x2={imgW} y2={sy}
                        stroke="#f87171" strokeWidth={gridStroke}
                        strokeDasharray={`${gridStroke * 8},${gridStroke * 4}`}
                        opacity="0.5" />
                    );
                  })}
                  {/* Horizontal segment labels */}
                  {yTicks.map((pt, i) => {
                    if (i === 0) return null;
                    const prev = yTicks[i - 1];
                    const segM = Math.abs(pt.meters - prev.meters);
                    if (segM < 0.05) return null;
                    const [, my] = pctToSvg(0, (pt.pct + prev.pct) / 2);
                    return (
                      <text key={`ysl${i}`} x={imgW * 0.98} y={my}
                        fill="#fca5a5" fontSize={segLabelSize} fontFamily="monospace"
                        textAnchor="end" fontWeight="bold" opacity="0.9">
                        {segM.toFixed(2)}m
                      </text>
                    );
                  })}
                </g>
              );
            })()}

            {/* Calibration points & line */}
            {/* === GPT-4o BEMAESSUNGSGITTER === */}
            {activeLayers.dimensionGridGpt && svgData?.dimension_grid_gpt && (() => {
              const dg = svgData.dimension_grid_gpt;
              const gridStroke = Math.max(imgW, imgH) * 0.0005;
              const labelSize = Math.max(imgW, imgH) * 0.0018;

              // Use pre-computed deduped ticks from backend (best chains only)
              const xTicks = dg.x_ticks || [];
              const yTicks = dg.y_ticks || [];
              const xSegs = dg.x_segments || [];
              const ySegs = dg.y_segments || [];

              return (
                <g opacity="0.75" data-testid="dimension-grid-gpt">
                  {xTicks.map((pct, i) => {
                    const [sx] = pctToSvg(pct, 0);
                    return (
                      <line key={`gpx${i}`} x1={sx} y1={0} x2={sx} y2={imgH}
                        stroke="#a78bfa" strokeWidth={gridStroke}
                        strokeDasharray={`${gridStroke * 6},${gridStroke * 3}`}
                        opacity="0.6" />
                    );
                  })}
                  {yTicks.map((pct, i) => {
                    const [, sy] = pctToSvg(0, pct);
                    return (
                      <line key={`gpy${i}`} x1={0} y1={sy} x2={imgW} y2={sy}
                        stroke="#a78bfa" strokeWidth={gridStroke}
                        strokeDasharray={`${gridStroke * 6},${gridStroke * 3}`}
                        opacity="0.6" />
                    );
                  })}
                  {/* Segment distance labels between vertical lines */}
                  {xSegs.map((seg, i) => {
                    const [sx1] = pctToSvg(seg.from_pct, 0);
                    const [sx2] = pctToSvg(seg.to_pct, 0);
                    const mx = (sx1 + sx2) / 2;
                    const labelY = imgH * 0.02;
                    return seg.meters >= 0.05 ? (
                      <text key={`gsx${i}`} x={mx} y={labelY} textAnchor="middle"
                        fill="#a78bfa" fontSize={labelSize} fontFamily="monospace" opacity="0.9">
                        {seg.meters.toFixed(seg.meters < 1 ? 3 : 2)}
                      </text>
                    ) : null;
                  })}
                  {/* Segment distance labels between horizontal lines */}
                  {ySegs.map((seg, i) => {
                    const [, sy1] = pctToSvg(0, seg.from_pct);
                    const [, sy2] = pctToSvg(0, seg.to_pct);
                    const my = (sy1 + sy2) / 2;
                    const labelX = imgW * 0.01;
                    return seg.meters >= 0.05 ? (
                      <text key={`gsy${i}`} x={labelX} y={my} textAnchor="start"
                        fill="#a78bfa" fontSize={labelSize} fontFamily="monospace" opacity="0.9"
                        dominantBaseline="middle">
                        {seg.meters.toFixed(seg.meters < 1 ? 3 : 2)}
                      </text>
                    ) : null;
                  })}
                </g>
              );
            })()}

            {calibrationPoints.p1 && (
              <g>
                <circle cx={calibrationPoints.p1.x} cy={calibrationPoints.p1.y} r="5"
                  fill="#f59e0b" stroke="white" strokeWidth="2" />
                <text x={calibrationPoints.p1.x + 8} y={calibrationPoints.p1.y + 4}
                  fill="#f59e0b" fontSize="10" fontFamily="monospace" fontWeight="bold">P1</text>
              </g>
            )}
            {calibrationPoints.p2 && (
              <g>
                <circle cx={calibrationPoints.p2.x} cy={calibrationPoints.p2.y} r="5"
                  fill="#f59e0b" stroke="white" strokeWidth="2" />
                <text x={calibrationPoints.p2.x + 8} y={calibrationPoints.p2.y + 4}
                  fill="#f59e0b" fontSize="10" fontFamily="monospace" fontWeight="bold">P2</text>
                <line x1={calibrationPoints.p1.x} y1={calibrationPoints.p1.y}
                  x2={calibrationPoints.p2.x} y2={calibrationPoints.p2.y}
                  stroke="#f59e0b" strokeWidth="2" strokeDasharray="6,3" />
              </g>
            )}


            {/* ─── Envelope overlay (on top of all interactive elements) ─── */}
            {activeLayers.envelope && svgData?.envelope && (() => {
              const envPoly = localSvgOverrides['__envelope__'] || svgData.envelope;
              const pts = envPoly.map(p => pctToSvg(p[0], p[1]).join(',')).join(' ');
              const envSelected = isElemSelected('__envelope__');
              const base = Math.max(imgW, imgH);
              const envStroke = base * 0.003;
              const envStrokeInner = base * 0.0015;
              const envDash = base * 0.008;
              const envGap = base * 0.004;
              const envHitWidth = envStroke * 4;
              return (
                <g data-testid="envelope-overlay-group">
                  {/* Outer glow */}
                  <polygon points={pts} fill="none"
                    stroke="#fbbf24" strokeWidth={envStroke * 1.8} strokeOpacity={0.15}
                    strokeLinejoin="round" pointerEvents="none" />
                  {/* Main stroke */}
                  <polygon points={pts} fill="none"
                    stroke="#fbbf24" strokeWidth={envStroke}
                    strokeDasharray={`${envDash},${envGap}`}
                    strokeOpacity={envSelected ? 1 : 0.85}
                    strokeLinejoin="round" pointerEvents="none" />
                  {/* Inner bright line */}
                  <polygon points={pts} fill={envSelected ? 'rgba(251,191,36,0.06)' : 'none'}
                    stroke="#fef3c7" strokeWidth={envStrokeInner}
                    strokeDasharray={`${envDash},${envGap}`}
                    strokeOpacity={envSelected ? 0.7 : 0.35}
                    strokeLinejoin="round" pointerEvents="none" />
                  {/* Wide invisible hit area for easy clicking on the stroke */}
                  <polygon points={pts} fill="none"
                    stroke="transparent" strokeWidth={envHitWidth}
                    strokeLinejoin="round"
                    style={{ cursor: (calibrationMode || drawingMode) ? 'crosshair' : 'pointer' }}
                    onClick={(e) => {
                      if (calibrationMode || drawingMode) return;
                      e.stopPropagation();
                      onSelectElement({
                        id: '__envelope__',
                        type: 'envelope',
                        name: 'Gebäudehülle',
                        polygon: envPoly,
                        color: '#fbbf24',
                      }, e);
                    }} />
                </g>
              );
            })()}

            {/* ─── Vertex editing overlay ─────────────────────── */}
            {editMode && (() => {
              const editId = editMode.id;
              let editPolygon = null;
              let isOpeningEdit = false;
              let isWallEdit = false;
              let openingType = null;
              // Find polygon from rooms, walls, openings, or envelope
              const editRoom = svgData?.rooms?.find(r => r.id === editId);
              if (editRoom) editPolygon = editRoom.polygon;
              if (!editPolygon) {
                const idx = svgData?.wall_lines?.findIndex((w, i) => (w.id || `wall_${i}`) === editId);
                if (idx >= 0) {
                  editPolygon = svgData.wall_lines[idx].polygon;
                  isWallEdit = true;
                }
              }
              if (!editPolygon && editId === '__envelope__' && svgData?.envelope) {
                editPolygon = svgData.envelope;
              }
              if (!editPolygon) {
                const editOpening = svgData?.openings?.find(o => o.id === editId);
                if (editOpening) {
                  editPolygon = editOpening.polygon;
                  // If no real polygon, create virtual polygon from control points
                  if (!editPolygon || editPolygon.length < 2) {
                    if (editOpening.type === 'window' && editOpening.from_pt && editOpening.to_pt) {
                      editPolygon = [editOpening.from_pt, editOpening.to_pt];
                    } else if (editOpening.type === 'door') {
                      const pts = [];
                      if (editOpening.hinge) pts.push(editOpening.hinge);
                      if (editOpening.closed_pt) pts.push(editOpening.closed_pt);
                      if (editOpening.open_pt) pts.push(editOpening.open_pt);
                      if (pts.length >= 2) editPolygon = pts;
                    } else if (editOpening.from_pt && editOpening.to_pt) {
                      editPolygon = [editOpening.from_pt, editOpening.to_pt];
                    } else if (editOpening.hinge && editOpening.closed_pt) {
                      editPolygon = [editOpening.hinge, editOpening.closed_pt];
                      if (editOpening.open_pt) editPolygon.push(editOpening.open_pt);
                    }
                  }
                  if (editPolygon && editPolygon.length >= 2) {
                    isOpeningEdit = true;
                    openingType = editOpening.type;
                  }
                }
              }
              // Check override (user may have already moved points)
              const override = localSvgOverrides[editId];
              if (override) editPolygon = override;
              if (!editPolygon || editPolygon.length < 2) return null;

              // ── Dynamic handle sizing based on viewBox ──
              // Compact handles: small visible dot, large invisible hit area
              const base = Math.max(imgW, imgH);
              const handleR = base * 0.0015;
              const handleRDrag = handleR * 1.3;
              const hitR = base * 0.006;
              const handleStroke = base * 0.0006;
              const lineStroke = base * 0.0012;
              const lineStrokeThin = base * 0.0008;
              const dashLen = base * 0.004;
              const dashGap = base * 0.002;
              const midR = handleR * 0.8;
              const midHitR = base * 0.005;
              const midStroke = base * 0.0005;
              const plusHalf = midR * 0.6;
              const labelSize = base * 0.003;
              const labelOffset = handleR * 2.5;

              // Labels for opening control points
              const pointLabels = isOpeningEdit
                ? openingType === 'door'
                  ? ['Scharnier', 'Geschl.', 'Offen']
                  : ['Von', 'Nach']
                : null;
              const editColor = isOpeningEdit
                ? (openingType === 'door' ? '#f59e0b' : '#22c55e')
                : '#f59e0b';

              return (
                <g data-testid="vertex-editor">
                  {/* Dashed outline / connecting lines */}
                  {isOpeningEdit ? (
                    <>
                      {editPolygon.length >= 2 && (() => {
                        const pts = editPolygon.map(p => pctToSvg(p[0], p[1]));
                        return (
                          <>
                            <line x1={pts[0][0]} y1={pts[0][1]} x2={pts[1][0]} y2={pts[1][1]}
                              stroke={editColor} strokeWidth={lineStroke} strokeDasharray={`${dashLen},${dashGap}`} opacity="0.8" />
                            {pts.length >= 3 && (
                              <line x1={pts[0][0]} y1={pts[0][1]} x2={pts[2][0]} y2={pts[2][1]}
                                stroke={editColor} strokeWidth={lineStrokeThin} strokeDasharray={`${dashLen*0.7},${dashGap*1.3}`} opacity="0.5" />
                            )}
                          </>
                        );
                      })()}
                    </>
                  ) : (
                    <path d={polyToPath(editPolygon)} fill="none"
                      stroke="#f59e0b" strokeWidth={lineStroke} strokeDasharray={`${dashLen},${dashGap}`} opacity="0.8" />
                  )}

                  {/* Edge drag handles — thick invisible lines on edges (rendered BEFORE midpoints) */}
                  {/* Skip edge drag for windows — they only need endpoint handles for width */}
                  {!(isOpeningEdit && openingType === 'window') && editPolygon.map((pt, i) => {
                    const next = editPolygon[(i + 1) % editPolygon.length];
                    const [sx1, sy1] = pctToSvg(pt[0], pt[1]);
                    const [sx2, sy2] = pctToSvg(next[0], next[1]);
                    const isHorizontal = Math.abs(pt[1] - next[1]) < 0.05;
                    const isVertical = Math.abs(pt[0] - next[0]) < 0.05;
                    if (!isHorizontal && !isVertical) return null;
                    const edgeCursor = isHorizontal ? 'ns-resize' : 'ew-resize';
                    return (
                      <line key={`edge-${i}`} x1={sx1} y1={sy1} x2={sx2} y2={sy2}
                        stroke="transparent" strokeWidth={Math.max(12, hitR * 1.5)}
                        style={{ cursor: edgeCursor }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          const pct = getSvgPctFromEvent(e);
                          onDragStart();
                          setDragVertex({ elementId: editId, mode: 'edge', edgeIndex: i, axis: isHorizontal ? 'y' : 'x', lastPct: pct });
                        }}
                      />
                    );
                  })}

                  {/* Edge midpoints (add vertex) – on top of edge drags for click priority */}
                  {/* Disabled for openings and 4-point walls (rectangle constraint) */}
                  {!isOpeningEdit && !(isWallEdit && editPolygon.length === 4) && editPolygon.map((pt, i) => {
                    const next = editPolygon[(i + 1) % editPolygon.length];
                    const [mx, my] = pctToSvg((pt[0] + next[0]) / 2, (pt[1] + next[1]) / 2);
                    return (
                      <g key={`mid-${i}`} data-testid={`add-vertex-${i}`} style={{ cursor: 'copy' }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          onAddVertex(editId, i);
                        }}>
                        <circle cx={mx} cy={my} r={midHitR} fill="transparent" />
                        <circle cx={mx} cy={my} r={midR} fill="#10b981" fillOpacity="0.7" stroke="white" strokeWidth={midStroke} />
                        <line x1={mx - plusHalf} y1={my} x2={mx + plusHalf} y2={my} stroke="white" strokeWidth={midStroke} />
                        <line x1={mx} y1={my - plusHalf} x2={mx} y2={my + plusHalf} stroke="white" strokeWidth={midStroke} />
                      </g>
                    );
                  })}

                  {/* Polygon/Line drag area — click to move all */}
                  {isOpeningEdit && openingType === 'window' && editPolygon.length === 2 ? (
                    (() => {
                      const [sx1, sy1] = pctToSvg(editPolygon[0][0], editPolygon[0][1]);
                      const [sx2, sy2] = pctToSvg(editPolygon[1][0], editPolygon[1][1]);
                      return (
                        <line x1={sx1} y1={sy1} x2={sx2} y2={sy2}
                          stroke="transparent" strokeWidth={Math.max(16, hitR * 2)}
                          style={{ cursor: 'move' }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            const pct = getSvgPctFromEvent(e);
                            onDragStart();
                            setDragVertex({ elementId: editId, mode: 'polygon', lastPct: pct });
                          }}
                        />
                      );
                    })()
                  ) : (
                    <polygon
                      points={editPolygon.map(p => pctToSvg(p[0], p[1]).join(',')).join(' ')}
                      fill="transparent"
                      style={{ cursor: 'move' }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        const pct = getSvgPctFromEvent(e);
                        onDragStart();
                        setDragVertex({ elementId: editId, mode: 'polygon', lastPct: pct });
                      }}
                    />
                  )}

                  {/* Vertex / control point handles */}
                  {editPolygon.map((pt, i) => {
                    const [sx, sy] = pctToSvg(pt[0], pt[1]);
                    const isDragging = dragVertex?.elementId === editId && dragVertex?.vertexIndex === i;
                    const label = pointLabels ? (pointLabels[i] || `P${i}`) : `${i}`;
                    return (
                      <g key={`vh-${i}`} data-testid={`vertex-handle-${i}`}>
                        {/* Large invisible hit area */}
                        <circle cx={sx} cy={sy} r={hitR} fill="transparent"
                          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            if (e.shiftKey && !isOpeningEdit && !(isWallEdit && editPolygon.length === 4)) {
                              onDeleteVertex(editId, i);
                              return;
                            }
                            onDragStart();
                            setDragVertex({ elementId: editId, vertexIndex: i });
                          }}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!isOpeningEdit && !(isWallEdit && editPolygon.length === 4)) onDeleteVertex(editId, i);
                          }}
                        />
                        {/* Visible handle — small dot */}
                        <circle cx={sx} cy={sy} r={isDragging ? handleRDrag : handleR}
                          fill={isDragging ? '#fbbf24' : editColor}
                          stroke="white" strokeWidth={handleStroke} pointerEvents="none" />
                        {/* Label */}
                        <text x={sx + labelOffset} y={sy - labelOffset * 0.8} fill="#fbbf24" fontSize={labelSize}
                          fontFamily="monospace" fontWeight="bold" pointerEvents="none">{label}</text>
                      </g>
                    );
                  })}
                </g>
              );
            })()}

            {/* ─── Drawing mode preview ───────────────────────── */}
            {drawingMode && drawingPoints.length > 0 && (() => {
              const pts = drawingPoints.map(p => pctToSvg(p[0], p[1]));
              const color = drawingMode === 'wall' ? '#3b82f6' : drawingMode === 'window' ? '#22c55e' : drawingMode === 'door' ? '#f59e0b' : '#8b5cf6';
              const dBase = Math.max(imgW, imgH);
              const dLineW = dBase * 0.001;
              const dDash = dBase * 0.004;
              const dGap = dBase * 0.002;
              const dR = dBase * 0.0015;
              const dStroke = dBase * 0.0006;
              return (
                <g data-testid="drawing-preview">
                  {/* Lines between placed points */}
                  {pts.map((pt, i) => {
                    if (i === 0) return null;
                    return (
                      <line key={`dl-${i}`} x1={pts[i-1][0]} y1={pts[i-1][1]} x2={pt[0]} y2={pt[1]}
                        stroke={color} strokeWidth={dLineW} strokeDasharray={`${dDash},${dGap}`} />
                    );
                  })}
                  {/* Close line preview */}
                  {pts.length >= 3 && (
                    <line x1={pts[pts.length-1][0]} y1={pts[pts.length-1][1]}
                      x2={pts[0][0]} y2={pts[0][1]}
                      stroke={color} strokeWidth={dLineW * 0.75} strokeDasharray={`${dGap},${dGap}`} opacity="0.5" />
                  )}
                  {/* Point markers */}
                  {pts.map((pt, i) => (
                    <circle key={`dp-${i}`} cx={pt[0]} cy={pt[1]} r={dR}
                      fill={color} stroke="white" strokeWidth={dStroke} />
                  ))}
                </g>
              );
            })()}
          </svg>
        </div>
      </div>

      {/* Magnifier lens — appears only during vertex drag */}
      {dragVertex && dragMousePct && result.original_image && (
        <div data-testid="vertex-magnifier" className="absolute top-2 right-2 z-50 rounded-lg overflow-hidden border-2 border-blue-500/60 shadow-lg shadow-black/50 pointer-events-none"
          style={{ width: 180, height: 180 }}>
          <svg viewBox={`${dragMousePct[0] / 100 * imgW - imgW * 0.02} ${dragMousePct[1] / 100 * imgH - imgH * 0.02} ${imgW * 0.04} ${imgH * 0.04}`}
            width={180} height={180} style={{ background: '#0a0a0a' }}>
            <image href={`data:image/png;base64,${result.original_image}`}
              x="0" y="0" width={imgW} height={imgH} />
            {/* Crosshair at cursor position */}
            {(() => {
              const cx = dragMousePct[0] / 100 * imgW;
              const cy = dragMousePct[1] / 100 * imgH;
              const s = imgW * 0.004;
              return (
                <g>
                  <line x1={cx - s} y1={cy} x2={cx + s} y2={cy} stroke="#3b82f6" strokeWidth={imgW * 0.0004} />
                  <line x1={cx} y1={cy - s} x2={cx} y2={cy + s} stroke="#3b82f6" strokeWidth={imgW * 0.0004} />
                  <circle cx={cx} cy={cy} r={imgW * 0.001} fill="none" stroke="#3b82f6" strokeWidth={imgW * 0.0003} />
                </g>
              );
            })()}
          </svg>
        </div>
      )}

      {/* Bottom status bar */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-zinc-900/90 border-t border-zinc-800 flex-shrink-0">
        <span className="font-mono text-[10px] text-zinc-500">{imgW} x {imgH}px</span>
        <span className="font-mono text-[10px] text-zinc-500">
          {result.processing_time}s | {svgData?.rooms?.length || 0} Räume | {svgData?.wall_lines?.length || 0} Wände
          {(() => {
            const calArea = svgData?.metadata?.total_area_m2_calibrated;
            const apiArea = svgData?.metadata?.total_area_m2;
            const area = calArea || apiArea;
            if (!area) return null;
            const source = 'kalibriert';
            return ` | Fläche: ${area} m²`;
          })()}
          {svgData?.calibration && <span className="text-emerald-400 ml-1">({svgData.calibration.source === 'manual' ? 'manuell kalibriert' : `ppm_x=${(svgData.calibration.ppm_x || svgData.calibration.scale_x_ppm || 0).toFixed(2)}`})</span>}
        </span>
      </div>
    </div>
  );
}

// ─── LAYER TOGGLE ────────────────────────────────────────────────
function LayerToggle({ label, color, active, onToggle, testId }) {
  return (
    <button data-testid={testId} onClick={onToggle}
      className={`flex items-center gap-2.5 px-3 py-2 border transition-all duration-150 w-full text-left
        ${active ? 'border-zinc-600 bg-zinc-800/80' : 'border-transparent bg-transparent hover:bg-zinc-800/50'}`}>
      <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: active ? color : '#3f3f46' }}></div>
      <span className="font-mono text-xs flex-1 text-zinc-300">{label}</span>
      {active ? <Eye className="w-3.5 h-3.5 text-zinc-400" /> : <EyeOff className="w-3.5 h-3.5 text-zinc-600" />}
    </button>
  );
}

// ─── ELEMENT EDIT MODAL ──────────────────────────────────────────
function ElementEditModal({ element, onSave, onClose, svgCalibration }) {
  const isOpening = element.type === 'door' || element.type === 'window';
  const isWall = element.type === 'wall';
  const isRoom = element.polygon && element.polygon.length >= 3 && !isWall && !isOpening;

  const [form, setForm] = useState(() => {
    if (isOpening) {
      return {
        type: element.type || 'window',
        name: element.name || element.label || '',
        width_m_matched: element.width_m_matched || element.width_m_calibrated || element.width_m || '',
        height_m_matched: element.height_m_matched || '',
        width_m_calibrated: element.width_m_calibrated || '',
        location: element.location || 'unknown',
        brh_m: element.brh_m || '',
        sth_m: element.sth_m || '',
      };
    }
    if (isWall) {
      return {
        name: element.name || element.label || '',
        wall_type: element.wall_type || 'unknown',
        length_m: element.length_m || element.length_m_calibrated || element.length_m_final || '',
        thickness_m: element.thickness_m_final || element.thickness_m || element.thickness_m_calibrated || '',
      };
    }
    return {
      name: element.name || element.label || '',
      type_de: element.type_de || element.room_type || '',
      area_m2_override: element.area_m2_override || '',
    };
  });

  const handleChange = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  // Auto-normalize: if value > 10, assume cm input → convert to meters
  const normalizeMeters = (val) => {
    const n = parseFloat(val);
    if (isNaN(n) || n === 0) return n;
    if (n > 10) return n / 100; // 235 → 2.35, 294 → 2.94
    return n;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const updates = {};
    if (isOpening) {
      const typeChanged = form.type !== element.type;
      updates.type = form.type;
      updates.name = form.name || (form.type === 'door' ? 'Tür' : 'Fenster');
      updates.label = form.name || (form.type === 'door' ? 'Tür' : 'Fenster');
      if (form.width_m_matched !== '') updates.width_m_matched = normalizeMeters(form.width_m_matched);
      if (form.height_m_matched !== '') updates.height_m_matched = normalizeMeters(form.height_m_matched);
      if (form.width_m_calibrated !== '') updates.width_m_calibrated = normalizeMeters(form.width_m_calibrated);
      updates.location = form.location;
      updates.width_source = 'manual';
      if (form.height_m_matched !== '') updates.height_source = 'manual';
      // BRH & STH (window properties)
      if (form.brh_m !== '') updates.brh_m = normalizeMeters(form.brh_m);
      else updates.brh_m = null;
      if (form.sth_m !== '') updates.sth_m = normalizeMeters(form.sth_m);
      else updates.sth_m = null;

      // Resize opening in SVG when width changes
      if (form.width_m_matched !== '' && element.from_pt && element.to_pt) {
        const newWidthM = normalizeMeters(form.width_m_matched);
        const oldWidthM = safeNum(element.width_m_final || element.width_m_matched || element.width_m);
        if (newWidthM > 0 && Math.abs(newWidthM - oldWidthM) > 0.01) {
          const cal = svgCalibration;
          if (cal) {
            const fp = element.from_pt, tp = element.to_pt;
            const dx = tp[0] - fp[0], dy = tp[1] - fp[1];
            const isHoriz = Math.abs(dx) > Math.abs(dy);
            const ppm = isHoriz ? (cal.ppm_x || cal.scale_x_ppm || 1) : (cal.ppm_y || cal.scale_y_ppm || 1);
            const newLenPx = newWidthM * ppm;
            const oldLenPx = Math.hypot(dx, dy);
            if (oldLenPx > 0.01) {
              const ratio = newLenPx / oldLenPx;
              // Extend from midpoint outward (symmetric resize)
              const mx = (fp[0] + tp[0]) / 2, my = (fp[1] + tp[1]) / 2;
              const halfDx = dx / 2 * ratio, halfDy = dy / 2 * ratio;
              updates.from_pt = [mx - halfDx, my - halfDy];
              updates.to_pt = [mx + halfDx, my + halfDy];
              updates.position = [mx, my];
              updates.width_m_final = newWidthM;
              updates.width_m = newWidthM;
            }
          }
        }
      }

      // Fix 1: Update color when type changes
      if (typeChanged) {
        updates.color = form.type === 'door' ? '#f59e0b' : '#22c55e';
      }

      // Fix 2: Convert coordinates when type changes (door↔window)
      if (typeChanged && form.type === 'window' && element.type === 'door') {
        // Door → Window: hinge/closed_pt → from_pt/to_pt
        if (element.hinge && element.closed_pt) {
          updates.from_pt = element.hinge;
          updates.to_pt = element.closed_pt;
        }
      } else if (typeChanged && form.type === 'door' && element.type === 'window') {
        // Window → Door: from_pt/to_pt → hinge/closed_pt/open_pt
        if (element.from_pt && element.to_pt) {
          updates.hinge = element.from_pt;
          updates.closed_pt = element.to_pt;
          // Calculate open_pt: perpendicular to the door line, same distance
          const dx = element.to_pt[0] - element.from_pt[0];
          const dy = element.to_pt[1] - element.from_pt[1];
          updates.open_pt = [element.from_pt[0] - dy, element.from_pt[1] + dx];
        }
      }
    } else if (isWall) {
      updates.name = form.name;
      updates.label = form.name;
      updates.wall_type = form.wall_type;
      updates.wall_type_source = 'manual';
      if (form.length_m !== '') updates.length_m = parseFloat(form.length_m);
      if (form.thickness_m !== '') {
        updates.thickness_m = parseFloat(form.thickness_m);
        updates.thickness_source = 'manual';
      }
    } else {
      updates.name = form.name;
      updates.label = form.name;
      updates.type_de = form.type_de;
      if (form.area_m2_override !== '') {
        const areaVal = parseFloat(form.area_m2_override);
        if (!isNaN(areaVal) && areaVal > 0) {
          updates.area_m2_override = areaVal;
          updates.area_m2_calibrated = areaVal;
          updates.area_m2 = areaVal;
        }
      }
    }
    onSave(element.id, updates);
  };

  const inputCls = "w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-xs text-zinc-200 font-mono focus:border-blue-500 focus:outline-none";
  const selectCls = "w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-xs text-zinc-200 font-mono focus:border-blue-500 focus:outline-none";

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60" onClick={onClose}>
      <form onSubmit={handleSubmit} onClick={e => e.stopPropagation()}
        data-testid="element-edit-modal"
        className="bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl w-[320px] max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
          <span className="font-mono text-sm text-white font-bold">Element bearbeiten</span>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-zinc-300">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-4 py-3 space-y-3">
          {isOpening && (
            <>
              <div>
                <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Typ</label>
                <select data-testid="edit-type" className={selectCls} value={form.type} onChange={e => handleChange('type', e.target.value)}>
                  <option value="window">Fenster</option>
                  <option value="door">Tür</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Name</label>
                <input data-testid="edit-name" className={inputCls} value={form.name} onChange={e => handleChange('name', e.target.value)} placeholder={form.type === 'door' ? 'z.B. Tür 1' : 'z.B. Fenster 1'} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Breite (m)</label>
                  <input data-testid="edit-width" type="number" step="0.01" min="0" className={inputCls}
                    value={form.width_m_matched} onChange={e => handleChange('width_m_matched', e.target.value)} />
                  {parseFloat(form.width_m_matched) > 10 && (
                    <span className="text-[9px] text-amber-500 mt-0.5 block">= {(parseFloat(form.width_m_matched) / 100).toFixed(2)} m (cm → m)</span>
                  )}
                </div>
                <div>
                  <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Höhe (m)</label>
                  <input data-testid="edit-height" type="number" step="0.01" min="0" className={inputCls}
                    value={form.height_m_matched} onChange={e => handleChange('height_m_matched', e.target.value)} />
                  {parseFloat(form.height_m_matched) > 10 && (
                    <span className="text-[9px] text-amber-500 mt-0.5 block">= {(parseFloat(form.height_m_matched) / 100).toFixed(2)} m (cm → m)</span>
                  )}
                </div>
              </div>
              {/* BRH & STH - only for windows */}
              {form.type === 'window' && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">BRH (m)</label>
                    <input data-testid="edit-brh" type="number" step="0.01" min="0" className={inputCls}
                      value={form.brh_m} onChange={e => handleChange('brh_m', e.target.value)} placeholder="Brüstungshöhe" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">STH (m)</label>
                    <input data-testid="edit-sth" type="number" step="0.01" min="0" className={inputCls}
                      value={form.sth_m} onChange={e => handleChange('sth_m', e.target.value)} placeholder="Sturzhöhe" />
                  </div>
                </div>
              )}
              <div>
                <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Mauerlichtes (m)</label>
                <input data-testid="edit-mauerlichtes" type="number" step="0.01" min="0" className={inputCls}
                  value={form.width_m_calibrated} onChange={e => handleChange('width_m_calibrated', e.target.value)} />
                {parseFloat(form.width_m_calibrated) > 10 && (
                  <span className="text-[9px] text-amber-500 mt-0.5 block">= {(parseFloat(form.width_m_calibrated) / 100).toFixed(2)} m (cm → m)</span>
                )}
              </div>
              <div>
                <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Lage</label>
                <select data-testid="edit-location" className={selectCls} value={form.location} onChange={e => handleChange('location', e.target.value)}>
                  <option value="exterior">Außen</option>
                  <option value="interior">Innen</option>
                  <option value="unknown">Unbekannt</option>
                </select>
              </div>
            </>
          )}
          {isWall && (
            <>
              <div>
                <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Name</label>
                <input className={inputCls} value={form.name} onChange={e => handleChange('name', e.target.value)} />
              </div>
              <div>
                <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Typ</label>
                <select className={selectCls} value={form.wall_type} onChange={e => handleChange('wall_type', e.target.value)}>
                  <option value="exterior">Außenwand</option>
                  <option value="interior">Innenwand</option>
                  <option value="unknown">Unbekannt</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Länge (m)</label>
                  <input type="number" step="any" min="0" className={inputCls}
                    value={form.length_m} onChange={e => handleChange('length_m', e.target.value)} />
                </div>
                <div>
                  <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Dicke (m)</label>
                  <input type="number" step="any" min="0" className={inputCls}
                    value={form.thickness_m} onChange={e => handleChange('thickness_m', e.target.value)} />
                </div>
              </div>
            </>
          )}
          {isRoom && (
            <>
              <div>
                <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Name</label>
                <input className={inputCls} value={form.name} onChange={e => handleChange('name', e.target.value)} />
              </div>
              <div>
                <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Raumtyp</label>
                <input className={inputCls} value={form.type_de} onChange={e => handleChange('type_de', e.target.value)} placeholder="z.B. Küche, Bad, Flur" />
              </div>
              <div>
                <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Fläche (m²)</label>
                <input data-testid="edit-area" type="number" step="0.01" min="0" className={inputCls}
                  value={form.area_m2_override} onChange={e => handleChange('area_m2_override', e.target.value)}
                  placeholder="z.B. 35.00" />
              </div>
            </>
          )}
        </div>
        <div className="flex gap-2 px-4 py-3 border-t border-zinc-800">
          <button type="button" onClick={onClose}
            className="flex-1 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-mono rounded transition-colors">
            Abbrechen
          </button>
          <button type="submit" data-testid="edit-save-btn"
            className="flex-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-mono rounded transition-colors font-bold">
            Speichern
          </button>
        </div>
      </form>
    </div>
  );
}


function RoofGeometryPanel({ analysisId, floorLevel }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [roofType, setRoofType] = useState('sattel');
  const [eave, setEave] = useState('');
  const [ridge, setRidge] = useState('');
  const [angle, setAngle] = useState('');
  const [innerWallHeight, setInnerWallHeight] = useState('');
  const [overhang, setOverhang] = useState('');
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);

  useEffect(() => {
    if (!open || !analysisId) return;
    axios.get(`${API_URL}/api/analyses/${analysisId}/roof-geometry`).then(r => {
      const rg = r.data?.roof_geometry || {};
      setData(rg);
      if (rg.roof_type) setRoofType(rg.roof_type);
      if (rg.eave_height_m) setEave(String(rg.eave_height_m));
      if (rg.ridge_height_m) setRidge(String(rg.ridge_height_m));
      if (rg.angle_deg) setAngle(String(rg.angle_deg));
      if (rg.inner_wall_height_m) setInnerWallHeight(String(rg.inner_wall_height_m));
      if (rg.overhang_m) setOverhang(String(rg.overhang_m));
      else if (rg.overhang_trauf_cm) setOverhang(String(rg.overhang_trauf_cm / 100));
    }).catch(() => {});
  }, [open, analysisId]);

  const handleSave = async (e) => {
    e.preventDefault();
    const eaveV = parseFloat(eave.replace(',', '.'));
    if (!eaveV) return;
    const oh = (parseFloat(overhang.replace(',', '.')) || 0) * 100;
    setSaving(true);
    try {
      const resp = await axios.post(`${API_URL}/api/analyses/${analysisId}/roof-geometry`, {
        roof_type: roofType,
        eave_height_m: eaveV,
        ridge_height_m: roofType === 'flach' ? eaveV : parseFloat(ridge.replace(',', '.')) || 0,
        angle_deg: roofType === 'flach' ? 0 : parseFloat(angle.replace(',', '.')) || 0,
        inner_wall_height_m: parseFloat(innerWallHeight.replace(',', '.')) || 0,
        overhang_trauf_cm: oh, overhang_ortgang_cm: oh,
      }, {withCredentials: true});
      setData(resp.data.roof_geometry);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const handleGPT = async () => {
    setAnalyzing(true);
    try {
      const resp = await axios.post(`${API_URL}/api/analyses/${analysisId}/analyze-roof-section`, {}, {withCredentials: true});
      if (resp.data?.status === 'ok') {
        const rs = resp.data.roof_section;
        const t = rs.roof_type || 'sattel';
        const ev = rs.eave_height_m;
        const rv = rs.ridge_height_m ?? ev;
        const av = rs.angle_deg || 0;
        const ohv = (rs.overhang_trauf_cm || 0) / 100;
        if (t) setRoofType(t);
        if (ev != null) setEave(String(ev));
        if (rv != null) setRidge(String(rv));
        if (av) setAngle(String(av));
        if (rs.drempel_height_m) setInnerWallHeight(String(rs.drempel_height_m));
        if (ohv > 0) setOverhang(String(ohv));
        if (ev != null) {
          const calcResp = await axios.post(`${API_URL}/api/analyses/${analysisId}/roof-geometry`, {
            roof_type: t, eave_height_m: ev, ridge_height_m: t === 'flach' ? ev : rv,
            angle_deg: t === 'flach' ? 0 : av, inner_wall_height_m: rs.drempel_height_m || 0,
            overhang_trauf_cm: rs.overhang_trauf_cm || 0, overhang_ortgang_cm: rs.overhang_trauf_cm || 0,
          }, {withCredentials: true});
          setData(calcResp.data.roof_geometry);
        }
      }
    } catch(err) { modal.alert('Schnittanalyse fehlgeschlagen: ' + (err.response?.data?.detail || err.message), { title: 'Fehler', type: 'warning' }); }
    setAnalyzing(false);
  };

  const isFlach = roofType === 'flach';

  if (!open) {
    return (
      <div className="px-3 py-2 border-b border-zinc-800">
        <button data-testid="open-roof-btn" onClick={() => setOpen(true)}
          className="w-full flex items-center justify-between px-2 py-1.5 bg-zinc-800/50 border border-zinc-700/50 rounded font-mono text-[10px] text-zinc-400 hover:text-zinc-200 hover:border-zinc-600 transition-colors">
          <span className="flex items-center gap-1.5">
            <span className="text-amber-500">&#9651;</span>
            <span className="text-amber-400">Dachberechnung</span>
          </span>
          {data?.roof_area_total_m2 ? (
            <span className="text-green-400 font-bold">{data.roof_area_total_m2} m&sup2;</span>
          ) : <span className="text-zinc-600">&#9662;</span>}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="roof-geometry-panel" className="px-3 py-2 border-b border-zinc-800">
      <div className="flex items-center justify-between mb-3">
        <span className="flex items-center gap-1.5">
          <span className="text-amber-500">&#9651;</span>
          <span className="font-mono text-[10px] text-amber-400 uppercase tracking-wider">Dachberechnung</span>
        </span>
        <button onClick={() => setOpen(false)} className="p-0.5 hover:bg-zinc-700 rounded">
          <X className="w-3 h-3 text-zinc-500" />
        </button>
      </div>

      {/* Dachtyp Cards */}
      <div className="grid grid-cols-3 gap-1.5 mb-3">
        {[
          { value: 'flach', label: 'Flachdach', icon: '\u25AC' },
          { value: 'sattel', label: 'Satteldach', icon: '\u25B3' },
          { value: 'walm', label: 'Walmdach', icon: '\u2B20' },
        ].map(t => (
          <button key={t.value} data-testid={`roof-type-${t.value}`}
            onClick={() => setRoofType(t.value)}
            className={`flex flex-col items-center gap-1 py-2 px-1 rounded border text-[10px] font-mono transition-all ${roofType === t.value ? 'bg-amber-900/30 border-amber-500/60 text-amber-300' : 'bg-zinc-800/50 border-zinc-700/40 text-zinc-500 hover:border-zinc-600 hover:text-zinc-400'}`}>
            <span className="text-lg leading-none">{t.icon}</span>
            <span>{t.label}</span>
          </button>
        ))}
      </div>

      <div className="text-[9px] font-mono text-zinc-600 mb-2 italic">
        Werte aus Schnitt-Analyse vorausgef&uuml;llt &ndash; manuell &auml;nderbar
      </div>

      <form onSubmit={handleSave} className="space-y-1.5">
        <div className={`grid ${isFlach ? 'grid-cols-1' : 'grid-cols-2'} gap-1.5`}>
          <div>
            <label className="font-mono text-[9px] text-zinc-500 block">Drempel / Traufh&ouml;he (m)</label>
            <input data-testid="roof-eave" type="text" inputMode="decimal" value={eave}
              onChange={e => setEave(e.target.value)} placeholder="1.20"
              className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1.5 text-[11px] font-mono text-white focus:border-amber-500 focus:outline-none" />
          </div>
          {!isFlach && (
            <div>
              <label className="font-mono text-[9px] text-zinc-500 block">Firsth&ouml;he (m)</label>
              <input data-testid="roof-ridge" type="text" inputMode="decimal" value={ridge}
                onChange={e => setRidge(e.target.value)} placeholder="4.50"
                className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1.5 text-[11px] font-mono text-white focus:border-amber-500 focus:outline-none" />
            </div>
          )}
        </div>
        {!isFlach && (
          <div className="grid grid-cols-2 gap-1.5">
            <div>
              <label className="font-mono text-[9px] text-zinc-500 block">Dachneigung (&deg;)</label>
              <input data-testid="roof-angle" type="text" inputMode="decimal" value={angle}
                onChange={e => setAngle(e.target.value)} placeholder="35"
                className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1.5 text-[11px] font-mono text-white focus:border-amber-500 focus:outline-none" />
            </div>
            <div>
              <label className="font-mono text-[9px] text-zinc-500 block">Innenwandh&ouml;he DG (m)</label>
              <input data-testid="roof-inner-wall" type="text" inputMode="decimal" value={innerWallHeight}
                onChange={e => setInnerWallHeight(e.target.value)} placeholder="2.35"
                className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1.5 text-[11px] font-mono text-white focus:border-amber-500 focus:outline-none" />
            </div>
          </div>
        )}
        <div>
          <label className="font-mono text-[9px] text-zinc-500 block">Dach&uuml;berstand (m)</label>
          <input data-testid="roof-overhang" type="text" inputMode="decimal" value={overhang}
            onChange={e => setOverhang(e.target.value)} placeholder="0.30"
            className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1.5 text-[11px] font-mono text-white focus:border-amber-500 focus:outline-none" />
        </div>
        <div className="flex gap-1.5">
          <button data-testid="roof-save-btn" type="submit" disabled={saving || analyzing || !eave}
            className="flex-1 px-2 py-2 bg-amber-600 hover:bg-amber-500 disabled:bg-zinc-700 disabled:text-zinc-500 text-white text-[11px] font-mono font-bold rounded transition-colors">
            {saving ? 'Berechne...' : 'Berechnen'}
          </button>
          <button data-testid="analyze-roof-section-btn" type="button" onClick={handleGPT} disabled={analyzing || saving}
            className="px-3 py-2 bg-zinc-700 hover:bg-zinc-600 text-zinc-300 text-[10px] font-mono rounded border border-zinc-600 transition-colors disabled:opacity-50">
            {analyzing ? '...' : 'Prüfen'}
          </button>
        </div>
      </form>

      {data?.roof_area_total_m2 != null && (
        <div className="mt-2 p-2 bg-zinc-800/50 rounded border border-zinc-700/30 space-y-1">
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 font-mono text-[10px]">
            <span className="text-zinc-500">Grundrissfl&auml;che</span>
            <span className="text-zinc-300 text-right">{data.grundriss_m2} m&sup2;</span>
            <span className="text-zinc-500">Geb&auml;ude L &times; B</span>
            <span className="text-zinc-300 text-right">{data.building_length_m} &times; {data.building_depth_m} m</span>
          </div>
          {data.teilflaechen?.length > 0 && (
            <div className="mt-1 pt-1 border-t border-zinc-700/30">
              {data.teilflaechen.map((tf, i) => (
                <div key={i} className="flex justify-between font-mono text-[10px]">
                  <span className="text-zinc-400">{tf.name}{tf.angle_deg ? ` (${tf.angle_deg}\u00B0)` : ''}</span>
                  <span className="text-green-400">{tf.area_m2} m&sup2;</span>
                </div>
              ))}
            </div>
          )}
          <div className="mt-1 pt-1 border-t border-zinc-700/30 flex justify-between font-mono text-[11px] font-bold">
            <span className="text-zinc-300">Dachfl&auml;che gesamt</span>
            <span className="text-green-400">{data.roof_area_total_m2} m&sup2;</span>
          </div>
        </div>
      )}
    </div>
  );
}



function PdfExportButton({ analysisId }) {
  const [loading, setLoading] = useState(false);
  if (!analysisId) return null;
  const handleExport = async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API_URL}/api/analyses/${analysisId}/export-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Elementlisten_${analysisId.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error('PDF export failed', e); }
    setLoading(false);
  };
  return (
    <button data-testid="pdf-export-btn" onClick={handleExport} disabled={loading}
      className="flex items-center gap-1.5 px-2 py-1 rounded hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors font-mono text-[10px]"
      title="Elementlisten als PDF exportieren">
      {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
      <span>PDF</span>
    </button>
  );
}


function ChecklistSidebarPanel({ result }) {
  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState({});
  const [step, setStep] = useState(0);

  const tasks = [
    { key: 'rooms', title: 'Raeume', items: ['Alle Raeume erkannt?', 'Raumgrenzen korrekt?'] },
    { key: 'walls', title: 'Waende', items: ['Alle Waende vorhanden?', 'Luecken/Ueberschneidungen?'] },
    { key: 'doors', title: 'Tueren', items: ['Alle Tueren erkannt?', 'Richtung und Anschlag?'] },
    { key: 'windows', title: 'Fenster', items: ['Alle Fenster erkannt?', 'Korrekt in Waenden?'] },
    { key: 'dims', title: 'Masse', items: ['Masse vorhanden?', 'Konsistent und plausibel?'] },
    { key: 'logic', title: 'Gesamtlogik', items: ['Geometrische Fehler?', 'Grundriss plausibel?'] },
  ];

  const doneCount = Object.values(checked).filter(Boolean).length;
  const currentTask = tasks[step];

  return (
    <div className="border-b border-zinc-800">
      <button onClick={() => setOpen(o => !o)}
        className="w-full px-3 py-2 bg-zinc-800/30 flex items-center justify-between hover:bg-zinc-800/50 transition-colors">
        <span className="flex items-center gap-1.5">
          <CheckCircle className="w-3 h-3 text-green-500" />
          <span className="font-mono text-[10px] text-zinc-500 uppercase tracking-wider">Grundriss pruefen</span>
        </span>
        <div className="flex items-center gap-2">
          {doneCount > 0 && <span className="font-mono text-[9px] text-green-400">{doneCount}/6</span>}
          {open ? <ChevronDown className="w-3 h-3 text-zinc-600" /> : <ChevronRight className="w-3 h-3 text-zinc-600" />}
        </div>
      </button>
      {open && (
        <div className="px-2 py-2">
          {/* Progress dots */}
          <div className="flex items-center gap-0.5 mb-1.5">
            {tasks.map((t, i) => (
              <button key={t.key} onClick={() => setStep(i)}
                className={`w-full h-1 rounded-full transition-colors ${
                  checked[t.key] ? 'bg-green-500' : i === step ? 'bg-blue-500' : 'bg-zinc-700'
                }`} title={t.title} />
            ))}
          </div>
          {/* Current step */}
          <button
            onClick={() => setChecked(prev => ({ ...prev, [currentTask.key]: !prev[currentTask.key] }))}
            className={`w-full flex items-start gap-1.5 px-2 py-1.5 rounded border text-left transition-colors ${
              checked[currentTask.key] ? 'bg-green-500/5 border-green-500/20' : 'bg-zinc-900/50 border-zinc-800'
            }`}>
            <div className={`w-3.5 h-3.5 mt-0.5 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${
              checked[currentTask.key] ? 'bg-green-500 border-green-500' : 'border-zinc-600'
            }`}>
              {checked[currentTask.key] && <CheckCircle className="w-2.5 h-2.5 text-white" />}
            </div>
            <div className="flex-1 min-w-0">
              <span className={`font-mono text-[10px] font-bold ${checked[currentTask.key] ? 'text-green-400' : 'text-zinc-300'}`}>
                {step + 1}. {currentTask.title}
              </span>
              {currentTask.items.map((item, j) => (
                <div key={j} className="font-mono text-[9px] text-zinc-500 leading-tight">{item}</div>
              ))}
            </div>
          </button>
          {/* Nav */}
          <div className="flex items-center justify-between mt-1">
            <button onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0}
              className="px-1.5 py-0.5 font-mono text-[9px] text-zinc-400 hover:text-white disabled:text-zinc-700 transition-colors flex items-center gap-0.5">
              <ChevronRight className="w-2.5 h-2.5 rotate-180" /> Zurueck
            </button>
            <button onClick={() => setStep(s => Math.min(5, s + 1))} disabled={step === 5}
              className="px-1.5 py-0.5 font-mono text-[9px] text-zinc-400 hover:text-white disabled:text-zinc-700 transition-colors flex items-center gap-0.5">
              Weiter <ChevronRight className="w-2.5 h-2.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ApplySectionButton({ analysisId, onApplied }) {
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  const handleApply = async () => {
    setApplying(true);
    setResult(null);
    try {
      const resp = await axios.post(`${API_URL}/api/analyses/${analysisId}/apply-section-data`);
      setResult(resp.data);
      if (resp.data.status === 'ok' && onApplied) onApplied();
    } catch (err) {
      setResult({ status: 'error', message: err.response?.data?.detail || err.message });
    } finally {
      setApplying(false);
    }
  };

  return (
    <div data-testid="apply-section-panel" className="px-3 py-2 border-b border-zinc-800">
      <button data-testid="apply-section-btn" onClick={handleApply} disabled={applying}
        className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded font-mono text-[10px] text-amber-400 hover:bg-amber-500/20 disabled:opacity-40 transition-colors">
        {applying ? <Loader2 className="w-3 h-3 animate-spin" /> : <Scissors className="w-3 h-3" />}
        {applying ? 'Wird übernommen...' : 'Schnitt-Daten übernehmen'}
      </button>
      {result && result.status === 'ok' && (
        <div className="mt-1.5 font-mono text-[9px] text-green-400 space-y-0.5">
          <div>Raumhöhe (IW): {result.room_height_m} m</div>
          {result.drempel_m && <div>Drempel (AW): {result.drempel_m} m</div>}
          <div>{result.walls_updated} Wände aktualisiert{result.aw_drempel_count ? ` (${result.aw_drempel_count} AW, ${result.iw_room_height_count} IW)` : ''}</div>
        </div>
      )}
      {result && result.status === 'error' && (
        <div className="mt-1.5 font-mono text-[9px] text-red-400">{result.message}</div>
      )}
    </div>
  );
}

function ValidationStatusBadge({ analysisId, onCompleted }) {
  const [status, setStatus] = useState(null);
  const [results, setResults] = useState(null);
  const prevStatusRef = useRef(null);

  useEffect(() => {
    if (!analysisId) return;
    const check = async () => {
      try {
        const resp = await axios.get(`${API_URL}/api/analyses/${analysisId}/validation-status`);
        const newStatus = resp.data.status;
        // Trigger re-fetch when transitioning to 'completed'
        if (newStatus === 'completed' && prevStatusRef.current && prevStatusRef.current !== 'completed') {
          if (onCompleted) onCompleted();
        }
        prevStatusRef.current = newStatus;
        setStatus(newStatus);
        setResults(resp.data.results);
      } catch { /* ignore */ }
    };
    check();
    const interval = setInterval(check, 3000);
    return () => clearInterval(interval);
  }, [analysisId, onCompleted]);

  const [validationOpen, setValidationOpen] = useState(false);

  if (!status || status === 'none') return null;

  const handleRerun = async (scope = 'all') => {
    setStatus('running');
    try {
      await axios.post(`${API_URL}/api/analyses/${analysisId}/validate-openings?scope=${scope}`);
    } catch { /* ignore */ }
  };

  return (
    <div data-testid="validation-status" className="border-b border-zinc-800">
      <button onClick={() => setValidationOpen(p => !p)}
        className="w-full px-3 py-2 flex items-center justify-between hover:bg-zinc-800/30 transition-colors">
        <span className="font-mono text-[10px] text-green-400">
          {status === 'running' ? 'KI-Validierung...' : `KI-Validierung: ${results?.corrected || 0}/${results?.total || 0}`}
        </span>
        <ChevronDown className={`w-3 h-3 text-zinc-500 transition-transform ${validationOpen ? 'rotate-180' : ''}`} />
      </button>
      {validationOpen && (
        <div className="px-3 pb-2 space-y-1">
          {status === 'running' && (
            <div className="flex items-center gap-1.5 font-mono text-[10px] text-blue-400">
              <Loader2 className="w-3 h-3 animate-spin" />
              Läuft{results?.scope && results.scope !== 'all' ? ` (nur ${results.scope === 'doors' ? 'Türen' : 'Fenster'})` : ''}...
            </div>
          )}
          {status === 'completed' && results && (
            <div className="space-y-1">
              <div className="font-mono text-[10px] text-green-400">
                {results.corrected}/{results.total} korrigiert
                {results.type_changes > 0 && `, ${results.type_changes} Typ`}
                {results.hinge_flips > 0 && `, ${results.hinge_flips} Angel`}
              </div>
              <div className="flex items-center gap-1 text-[9px] flex-wrap">
                <button onClick={() => handleRerun('all')} data-testid="revalidate-all"
                  className="px-1.5 py-0.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300">
                  <RotateCcw className="w-2.5 h-2.5 inline -mt-0.5 mr-0.5" />Alle
                </button>
                <button onClick={() => handleRerun('doors')} data-testid="revalidate-doors"
                  className="px-1.5 py-0.5 rounded bg-amber-900/40 hover:bg-amber-900/60 text-amber-300">
                  Nur Türen
                </button>
                <button onClick={() => handleRerun('windows')} data-testid="revalidate-windows"
                  className="px-1.5 py-0.5 rounded bg-emerald-900/40 hover:bg-emerald-900/60 text-emerald-300">
                  Nur Fenster
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MultiThicknessInput({ walls, onSave, onSaveHeight }) {
  const [value, setValue] = useState('');
  const [heightValue, setHeightValue] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmitCombined = async (e) => {
    e.preventDefault();
    const cm = value ? parseFloat(value.replace(',', '.')) : null;
    const m = heightValue ? parseFloat(heightValue.replace(',', '.')) : null;
    if (!cm && !m) return;
    if (cm && (cm <= 0 || cm > 80)) return;
    if (m && (m <= 0 || m > 20)) return;
    setSaving(true);
    try {
      const ids = walls.map(w => w.id);
      if (cm) await onSave(ids, cm / 100);
      if (m) await onSaveHeight(ids, m);
      setValue('');
      setHeightValue('');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="multi-thickness-input" className="mt-3 pt-3 border-t border-zinc-700/50 space-y-2">
      <span className="text-zinc-400 font-mono text-[10px] uppercase tracking-wider">Wandmaße zuweisen</span>
      <form onSubmit={handleSubmitCombined} className="space-y-1.5">
        <div className="flex gap-1.5">
          <span className="text-zinc-500 text-[10px] font-mono w-6 flex items-center">D:</span>
          <input data-testid="multi-thickness-value" type="text" inputMode="decimal" value={value}
            onChange={e => setValue(e.target.value)} placeholder="cm"
            className="flex-1 bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-xs font-mono text-white placeholder-zinc-600 focus:border-blue-500 focus:outline-none" />
        </div>
        <div className="flex gap-1.5">
          <span className="text-zinc-500 text-[10px] font-mono w-6 flex items-center">H:</span>
          <input data-testid="multi-height-value" type="text" inputMode="decimal" value={heightValue}
            onChange={e => setHeightValue(e.target.value)} placeholder="m"
            className="flex-1 bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-xs font-mono text-white placeholder-zinc-600 focus:border-amber-500 focus:outline-none" />
        </div>
        <button data-testid="multi-wall-dims-apply" type="submit" disabled={saving || (!value && !heightValue)}
          className="w-full px-2.5 py-1 bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-700 disabled:text-zinc-500 text-white text-xs font-mono rounded transition-colors">
          {saving ? '...' : `${walls.length}× setzen`}
        </button>
      </form>
    </div>
  );
}


function MultiOpeningInput({ openings, onSave }) {
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [saving, setSaving] = useState(false);

  const windows = openings.filter(o => o.type === 'window');
  const doors = openings.filter(o => o.type === 'door');
  const label = windows.length > 0 && doors.length > 0
    ? `${windows.length} Fenster + ${doors.length} Türen`
    : windows.length > 0 ? `${windows.length} Fenster` : `${doors.length} Türen`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const wM = width ? parseFloat(width.replace(',', '.')) / 100 : null;
    const hM = height ? parseFloat(height.replace(',', '.')) / 100 : null;
    if (!wM && !hM) return;
    setSaving(true);
    try {
      await onSave(openings.map(o => o.id), wM, hM);
      setWidth('');
      setHeight('');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="multi-opening-input" className="mt-3 pt-3 border-t border-zinc-700/50">
      <span className="text-zinc-400 font-mono text-[10px] uppercase tracking-wider">Maße zuweisen ({label})</span>
      <form onSubmit={handleSubmit} className="mt-1.5 space-y-1.5">
        <div className="flex gap-1.5 items-center">
          <span className="text-zinc-500 text-[10px] font-mono w-8">B:</span>
          <input data-testid="multi-opening-width" type="text" inputMode="decimal" value={width}
            onChange={e => setWidth(e.target.value)} placeholder="cm"
            className="flex-1 bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-xs font-mono text-white placeholder-zinc-600 focus:border-green-500 focus:outline-none" />
        </div>
        <div className="flex gap-1.5 items-center">
          <span className="text-zinc-500 text-[10px] font-mono w-8">H:</span>
          <input data-testid="multi-opening-height" type="text" inputMode="decimal" value={height}
            onChange={e => setHeight(e.target.value)} placeholder="cm"
            className="flex-1 bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-xs font-mono text-white placeholder-zinc-600 focus:border-green-500 focus:outline-none" />
        </div>
        <button data-testid="multi-opening-apply" type="submit" disabled={saving || (!width && !height)}
          className="w-full px-2.5 py-1 bg-green-600 hover:bg-green-500 disabled:bg-zinc-700 disabled:text-zinc-500 text-white text-xs font-mono rounded transition-colors">
          {saving ? '...' : `${openings.length}× setzen`}
        </button>
      </form>
    </div>
  );
}


// ─── ELEMENT DETAIL PANEL ────────────────────────────────────────
function ElementDetail({ elements, onEditElement, onDeleteElement, onSaveMultiThickness, onSaveMultiHeight, onSaveMultiOpeningDims, onFlipDoorGeometry, result, localSvgOverrides }) {
  if (!elements || elements.length === 0) {
    return (
      <div className="px-3 py-6 text-center">
        <MousePointer className="w-5 h-5 text-zinc-600 mx-auto mb-2" />
        <p className="font-mono text-xs text-zinc-500">Raum oder Wand anklicken</p>
        <p className="font-mono text-[10px] text-zinc-600 mt-1">Ctrl+Klick für Mehrfachauswahl</p>
      </div>
    );
  }

  // Get ppm from calibration
  const cal = result?.svg_data?.calibration || {};
  const ppmX = cal.ppm_x || cal.scale_x_ppm || 0;
  const ppmY = cal.ppm_y || cal.scale_y_ppm || 0;

  if (elements.length === 1) {
    const element = elements[0];
    const isRoom = element.polygon && element.polygon.length >= 3 && element.type !== 'wall' && element.type !== 'envelope';
    const isWall = element.type === 'wall';
    const isOpening = element.type === 'door' || element.type === 'window';
    const isEnvelope = element.type === 'envelope';

    // Simple: backend already computed all values via ppm
    // If polygon was overridden (vertex dragged), dynamically recalculate area
    let areaM2 = element.area_m2_override || element.area_m2 || element.area_m2_calibrated;
    if (isRoom && localSvgOverrides && localSvgOverrides[element.id]) {
      const overridePoly = localSvgOverrides[element.id];
      const imgSize = result?.image_size || {};
      const dynArea = computePolygonAreaM2(overridePoly, imgSize.width, imgSize.height, ppmX, ppmY);
      if (dynArea != null) areaM2 = dynArea;
    }
    let perimeterM = element.perimeter_m || element.perimeter_m_calibrated;
    let lengthM = element.length_m || element.length_m_calibrated;
    let thicknessM = safeNum(element.thickness_m_final || element.thickness_m || element.thickness_m_calibrated);
    let widthM = element.width_m_matched || element.width_m || element.width_m_calibrated;
    let measureSource = (ppmX > 0 && ppmY > 0) ? 'kalibriert' : 'api';

    return (
      <div data-testid="element-detail" className="px-3 py-3 border-b border-zinc-800 bg-blue-500/5">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-3.5 h-3.5 rounded-sm" style={{ backgroundColor: (element.type === 'window' || element.type === 'door') ? resolveOpeningColor(element) : element.color }}></div>
          <span className="font-mono text-sm text-white font-bold flex-1">{
            element.gpt_label
              ? `${element.type === 'door' ? 'Tür' : 'Fenster'} — ${element.gpt_label}`
              : (element.type === 'door' || element.type === 'window')
                ? `${element.type === 'door' ? 'Tür' : 'Fenster'} ${(element.id || '').replace(/^(window_|door_)/, '')}`
                : (element.label || element.name)
          }</span>
          {onEditElement && (
            <button data-testid="edit-element-btn" onClick={() => onEditElement(element)}
              className="p-1 rounded hover:bg-zinc-700/50 text-zinc-500 hover:text-zinc-300 transition-colors"
              title="Element bearbeiten">
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
          {onDeleteElement && (
            <button data-testid="delete-element-btn" onClick={() => onDeleteElement(element)}
              className="p-1 rounded hover:bg-red-900/50 text-zinc-500 hover:text-red-400 transition-colors"
              title="Element löschen">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {isRoom && (
          <div className="space-y-1.5 font-mono text-xs">
            <div className="flex justify-between"><span className="text-zinc-500">Typ</span><span className="text-zinc-300">{element.type_de || element.room_type || element.type || translateRoom(element.name)}</span></div>
            {areaM2 && (
              <div className="flex justify-between">
                <span className="text-zinc-500">Fläche</span>
                <span className="text-green-400 font-bold text-sm">{areaM2} m²</span>
              </div>
            )}
            {perimeterM && (
              <div className="flex justify-between">
                <span className="text-zinc-500">Umfang</span>
                <span className="text-blue-400 font-bold">{perimeterM} m</span>
              </div>
            )}
            {element.polygon && (() => {
              const poly = element.polygon;
              const n = (poly.length > 3 && Math.abs(poly[0][0] - poly[poly.length-1][0]) < 0.01 && Math.abs(poly[0][1] - poly[poly.length-1][1]) < 0.01) ? poly.length - 1 : poly.length;
              return <div className="flex justify-between"><span className="text-zinc-500">Polygon-Punkte</span><span className="text-zinc-400">{n}</span></div>;
            })()}
          </div>
        )}
        {isWall && (<>
          <div className="space-y-1.5 font-mono text-xs">
            {lengthM && (
              <div className="flex justify-between">
                <span className="text-zinc-500">Länge</span>
                <span className="text-red-400 font-bold text-sm">{lengthM} m{element.drag_override && <span className="text-cyan-400 ml-1">(Drag)</span>}</span>
              </div>
            )}
            {element.length_m_text && (
              <div className="flex justify-between text-[10px]">
                <span className="text-zinc-600">PDF-Text</span>
                <span className={
                  element.length_confidence === 'high' ? 'text-emerald-400' :
                  element.length_confidence === 'medium' ? 'text-amber-400' :
                  element.length_confidence === 'low' ? 'text-red-400' : 'text-zinc-500'
                }>
                  {element.length_m_text} m
                  {element.length_text_delta_m != null && ` (Δ ${(element.length_text_delta_m * 1000).toFixed(0)} mm)`}
                </span>
              </div>
            )}
            {thicknessM && (
              <div className="flex justify-between">
                <span className="text-zinc-500">Dicke</span>
                <span className="text-zinc-300">{(thicknessM * 100).toFixed(1)} cm{element.drag_override ? <span className="text-cyan-400 ml-1">(Drag)</span> : element.thickness_source === 'din_snap' ? ' (DIN)' : element.thickness_source === 'pixel_cluster' ? ' (Pixel)' : element.thickness_source === 'propagated' ? ' (Prop.)' : element.thickness_source === 'pdf_dimension' ? ' (PDF)' : element.thickness_source === 'manual' ? ' (Manuell)' : ''}</span>
              </div>
            )}
            {element.height_m && (
              <div className="flex justify-between">
                <span className="text-zinc-500">Höhe</span>
                <span className="text-amber-400">{element.height_m} m</span>
              </div>
            )}
            <div className="flex justify-between"><span className="text-zinc-500">Richtung</span><span className="text-zinc-300">{element.orientation === 'horizontal' ? 'Horizontal' : element.orientation === 'vertical' ? 'Vertikal' : 'Diagonal'}</span></div>
            {element.wall_type && (
              <div className="flex justify-between"><span className="text-zinc-500">Typ</span><span className="text-zinc-300">{element.wall_type === 'exterior' ? 'Außenwand' : element.wall_type === 'interior' ? 'Innenwand' : 'Unbekannt'}</span></div>
            )}
            {element.decomposed && element.parent_id && (
              <div className="flex justify-between"><span className="text-zinc-500">Segment von</span><span className="text-zinc-400 text-[10px]">{element.parent_id}</span></div>
            )}
            {element.is_complex && element.segments && element.segments.length > 1 && (
              <div className="mt-2 pt-2 border-t border-zinc-800">
                <span className="text-zinc-500 text-[10px]">Wandsegmente ({element.segments.length})</span>
                <div className="mt-1 space-y-0.5">
                  {element.segments.map((seg, i) => (
                    <div key={i} className="flex justify-between text-[10px]">
                      <span className="text-amber-500/70">{seg.orientation === 'horizontal' ? 'H' : 'V'}{i+1}</span>
                      <span className="text-amber-400">{seg.length_m}m × {(seg.thickness_m * 100).toFixed(1)}cm</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          {/* Wandflucht */}
          {element.polygon && element.wall_type && (() => {
            const flucht = detectWandflucht(element, result?.svg_data?.wall_lines || [], result?.svg_data?.openings || []);
            if (!flucht) return null;
            return (
              <div className="mt-2 pt-2 border-t border-zinc-700/40 space-y-1">
                <span className="font-mono text-[9px] text-zinc-500 uppercase tracking-wider">
                  Wandflucht ({flucht.wallCount} Seg.{flucht.openingCount > 0 ? ` + ${flucht.openingCount} Oeffn.` : ''})
                </span>
                <div className="flex justify-between font-mono text-xs">
                  <span className="text-zinc-400">Laenge</span>
                  <span className="text-white font-bold">{flucht.totalRun.toFixed(2)} m</span>
                </div>
                {flucht.nettoArea > 0 && (
                  <div className="flex justify-between font-mono text-xs">
                    <span className="text-zinc-400">Netto-Flaeche</span>
                    <span className="text-green-400 font-bold">{flucht.nettoArea.toFixed(2)} m2</span>
                  </div>
                )}
                {flucht.nettoVol > 0 && (
                  <div className="flex justify-between font-mono text-xs">
                    <span className="text-zinc-400">Netto-Volumen</span>
                    <span className="text-cyan-400 font-bold">{flucht.nettoVol.toFixed(3)} m3</span>
                  </div>
                )}
                {flucht.nettoArea === 0 && (
                  <div className="font-mono text-[9px] text-amber-500/80">Wandhoehe setzen fuer Flaeche</div>
                )}
              </div>
            );
          })()}
        </>)}
        {isOpening && (
          <div className="space-y-1.5 font-mono text-xs">
            <div className="flex justify-between"><span className="text-zinc-500">Typ</span><span className="text-zinc-300">{element.type === 'door' ? 'Tür' : 'Fenster'}{element.original_type ? ` (war: ${element.original_type === 'door' ? 'Tür' : 'Fenster'})` : ''}</span></div>
            {element.gpt_label && (
              <div className="flex justify-between"><span className="text-zinc-500">Plan-Nr.</span><span className="text-amber-400">{element.gpt_label}</span></div>
            )}
            {element.din_anschlag && (
              <div className="flex justify-between items-center">
                <span className="text-zinc-500">Anschlag</span>
                <button data-testid="toggle-hinge-btn" onClick={() => onFlipDoorGeometry?.(element.id, 'hinge')}
                  className="text-zinc-300 hover:text-blue-400 font-mono text-xs transition-colors cursor-pointer flex items-center gap-1">
                  {element.din_anschlag}
                  <RotateCcw className="w-2.5 h-2.5 text-zinc-600" />
                </button>
              </div>
            )}
            {element.type === 'door' && element.open_pt && (
              <div className="flex justify-between items-center">
                <span className="text-zinc-500">Öffnet in</span>
                <button data-testid="toggle-direction-btn" onClick={() => onFlipDoorGeometry?.(element.id, 'direction')}
                  className="text-zinc-300 hover:text-blue-400 font-mono text-xs transition-colors cursor-pointer flex items-center gap-1">
                  {element.swing_room || 'Außen'}{element.opening_direction ? ` (${element.opening_direction})` : ''}
                  <RotateCcw className="w-2.5 h-2.5 text-zinc-600" />
                </button>
              </div>
            )}
            {widthM && (
              <div className="flex justify-between">
                <span className="text-zinc-500">Breite</span>
                <span className="text-zinc-300">
                  {widthM} m
                  {element.width_source === 'pdf_chain' && <span className="text-green-500 ml-1">(PDF-Kette)</span>}
                  {element.width_source === 'pdf_dimension' && <span className="text-blue-500 ml-1">(PDF)</span>}
                  {element.width_source === 'propagated' && <span className="text-teal-400 ml-1">(Prop.)</span>}
                  {element.width_source === 'manual' && <span className="text-amber-400 ml-1">(Manuell)</span>}
                  {element.width_source === 'drag' && <span className="text-cyan-400 ml-1">(Drag)</span>}
                  {element.width_source === 'gpt_validated' && <span className="text-purple-400 ml-1">(Validiert)</span>}
                  {element.width_source === 'pdf_arc' && <span className="text-orange-400 ml-1">(PDF-Arc)</span>}
                  {element.width_source === 'ava_table' && <span className="text-blue-300 ml-1">(AVA)</span>}
                  {element.width_source === 'user_confirmed' && <span className="text-amber-300 ml-1">(Bestätigt)</span>}
                </span>
              </div>
            )}
            {element.width_m_calibrated && widthM !== element.width_m_calibrated && (
              <div className="flex justify-between"><span className="text-zinc-500">Mauerlichtes</span><span className="text-zinc-500">{element.width_m_calibrated} m</span></div>
            )}
            {element.height_m_matched && (
              <div className="flex justify-between">
                <span className="text-zinc-500">Höhe</span>
                <span className="text-zinc-300">{safeStr(element.height_m_matched)} m{element.height_source === 'pdf_chain' ? ' (PDF-Kette)' : element.height_source === 'gpt_validated' ? ' (Validiert)' : ''}</span>
              </div>
            )}
            {element.type === 'window' && element.brh_m != null && (
              <div className="flex justify-between"><span className="text-zinc-500">BRH</span><span className="text-zinc-300">{safeNum(element.brh_m).toFixed(2)} m</span></div>
            )}
            {element.type === 'window' && element.sth_m != null && (
              <div className="flex justify-between"><span className="text-zinc-500">STH</span><span className="text-zinc-300">{safeNum(element.sth_m).toFixed(2)} m</span></div>
            )}
            {element.location && (
              <div className="flex justify-between"><span className="text-zinc-500">Lage</span><span className="text-zinc-300">{element.location === 'exterior' ? 'Außen' : element.location === 'interior' ? 'Innen' : 'Unbekannt'}</span></div>
            )}
            {element.connects && element.connects.length > 0 && (
              <div className="flex justify-between"><span className="text-zinc-500">Verbindet</span><span className="text-zinc-300 text-right">{element.connects.join(' ↔ ')}</span></div>
            )}
          </div>
        )}
        {isEnvelope && (
          <div className="space-y-1.5 font-mono text-xs">
            <div className="flex justify-between"><span className="text-zinc-500">Typ</span><span className="text-amber-300">Gebäudehülle</span></div>
            {element.polygon && (() => {
              const pts = element.polygon;
              const n = pts.length;
              let area = null;
              let perimeter = null;
              if (ppmX > 0 && ppmY > 0) {
                const mPts = pts.map(p => [p[0] / ppmX, p[1] / ppmY]);
                area = +shoelaceArea(mPts).toFixed(1);
                perimeter = +polygonPerimeter(mPts).toFixed(2);
              }
              return (
                <>
                  <div className="flex justify-between"><span className="text-zinc-500">Eckpunkte</span><span className="text-zinc-300">{n}</span></div>
                  {area != null && (
                    <div className="flex justify-between"><span className="text-zinc-500">Grundfläche</span><span className="text-amber-400 font-bold text-sm">{area} m²</span></div>
                  )}
                  {perimeter != null && (
                    <div className="flex justify-between"><span className="text-zinc-500">Umfang</span><span className="text-amber-400">{perimeter} m</span></div>
                  )}
                </>
              );
            })()}
          </div>
        )}
      </div>
    );
  }

  // Multi selection - aggregated view
  const rooms = elements.filter(el => el.polygon && el.polygon.length >= 3 && el.type !== 'wall' && el.type !== 'envelope');
  const walls = elements.filter(el => el.type === 'wall');
  const openings = elements.filter(el => el.type === 'door' || el.type === 'window');

  let totalArea = 0;
  let totalWallLength = 0;
  let totalOpeningWidth = 0;
  let totalWallArea = 0;
  let totalWallVolume = 0;
  let totalOpeningArea = 0;

  for (const op of openings) {
    const w = safeNum(op.width_m_final || op.width_m_calibrated || op.width_m);
    const h = safeNum(op.height_m_matched || op.height_m);
    if (w) totalOpeningWidth += w;
    if (w > 0 && h > 0) totalOpeningArea += w * h;
  }

  const imgSize = result?.image_size || {};
  for (const room of rooms) {
    let a = room.area_m2_override || room.area_m2 || room.area_m2_calibrated;
    if (localSvgOverrides && localSvgOverrides[room.id]) {
      const dynArea = computePolygonAreaM2(localSvgOverrides[room.id], imgSize.width, imgSize.height, ppmX, ppmY);
      if (dynArea != null) a = dynArea;
    }
    if (a) totalArea += parseFloat(a);
  }
  // Average wall thickness for opening volume deduction
  let sumThickness = 0, countThickness = 0;
  for (const wall of walls) {
    const l = safeNum(wall.length_m_final || wall.length_m || wall.length_m_calibrated);
    const h = safeNum(wall.height_m);
    const d = safeNum(wall.thickness_m_final || wall.thickness_m);
    totalWallLength += l;
    if (h > 0) totalWallArea += l * h;
    if (h > 0 && d > 0) { totalWallVolume += l * h * d; sumThickness += d; countThickness++; }
  }
  const avgThickness = countThickness > 0 ? sumThickness / countThickness : 0;
  // Total wall run = wall segments + opening widths (continuous wall including openings)
  const totalWallRun = totalWallLength + totalOpeningWidth;
  // Brutto = full run × avg height
  const avgWallHeight = walls.length > 0 ? walls.reduce((s, w) => s + safeNum(w.height_m), 0) / walls.length : 0;
  const bruttoFromRun = avgWallHeight > 0 ? totalWallRun * avgWallHeight : 0;
  const bruttoArea = openings.length > 0 ? Math.max(totalWallArea, bruttoFromRun) : totalWallArea;
  const nettoWallArea = Math.max(0, bruttoArea - totalOpeningArea);
  const nettoVolume = avgThickness > 0 ? Math.max(0, nettoWallArea * avgThickness) : 0;

  const hasWallsOrOpenings = walls.length > 0 || openings.length > 0;

  return (
    <div data-testid="element-detail-multi" className="px-3 py-3 border-b border-zinc-800 bg-blue-500/5">
      <div className="flex items-center gap-2 mb-3">
        <span className="font-mono text-sm text-white font-bold">{elements.length} Elemente</span>
        <span className="font-mono text-[10px] text-zinc-500">ausgewählt</span>
      </div>
      <div className="space-y-1.5 font-mono text-xs">
        {rooms.length > 0 && (
          <>
            <div className="flex justify-between">
              <span className="text-zinc-500">Räume</span>
              <span className="text-zinc-300">{rooms.length}</span>
            </div>
            {totalArea > 0 && (
              <div className="flex justify-between">
                <span className="text-zinc-500">Fläche gesamt</span>
                <span className="text-green-400 font-bold text-sm">{totalArea.toFixed(1)} m²</span>
              </div>
            )}
          </>
        )}
        {walls.length > 0 && (
          <>
            <div className="flex justify-between">
              <span className="text-zinc-500">Wände</span>
              <span className="text-zinc-300">{walls.length}</span>
            </div>
            {totalWallLength > 0 && (
              <div className="flex justify-between">
                <span className="text-zinc-500">Wandlänge</span>
                <span className="text-blue-400 font-bold">{totalWallLength.toFixed(2)} m</span>
              </div>
            )}
          </>
        )}
        {openings.length > 0 && (
          <>
            <div className="flex justify-between">
              <span className="text-zinc-500">Öffnungen</span>
              <span className="text-zinc-300">{openings.length} ({openings.filter(o => o.type === 'door').length} Türen, {openings.filter(o => o.type === 'window').length} Fenster)</span>
            </div>
            {totalOpeningWidth > 0 && (
              <div className="flex justify-between">
                <span className="text-zinc-500">Öffnungsbreite</span>
                <span className="text-zinc-400">{totalOpeningWidth.toFixed(2)} m</span>
              </div>
            )}
          </>
        )}
        {/* Total summary */}
        {hasWallsOrOpenings && totalWallLength > 0 && (
          <div className="mt-2 pt-2 border-t border-zinc-700/50 space-y-1">
            {openings.length > 0 ? (
              <div className="flex justify-between items-baseline">
                <span className="text-zinc-400 font-medium">Wandlauf</span>
                <span className="text-white font-bold text-base">{totalWallRun.toFixed(2)} m</span>
              </div>
            ) : (
              <div className="flex justify-between items-baseline">
                <span className="text-zinc-400 font-medium">Wandlänge</span>
                <span className="text-white font-bold text-base">{totalWallLength.toFixed(2)} m</span>
              </div>
            )}
            {bruttoArea > 0 ? (
              <>
                <div className="flex justify-between items-baseline">
                  <span className="text-zinc-500 text-[10px]">Brutto (L x H)</span>
                  <span className="text-zinc-400">{bruttoArea.toFixed(2)} m²</span>
                </div>
                {totalOpeningArea > 0 && (
                  <div className="flex justify-between items-baseline">
                    <span className="text-zinc-500 text-[10px]">- Öffnungen ({openings.length})</span>
                    <span className="text-red-400/80">-{totalOpeningArea.toFixed(2)} m²</span>
                  </div>
                )}
                <div className="flex justify-between items-baseline">
                  <span className="text-zinc-400 font-medium">Netto-Wandfläche</span>
                  <span className="text-green-400 font-bold text-base">{nettoWallArea.toFixed(2)} m²</span>
                </div>
              </>
            ) : (
              <div className="text-amber-500/80 text-[10px] font-mono mt-1">Wandhöhe (H) setzen für Fläche/Volumen</div>
            )}
            {nettoVolume > 0 ? (
              <div className="flex justify-between items-baseline">
                <span className="text-zinc-400 font-medium">Netto-Volumen</span>
                <span className="text-cyan-400 font-bold text-base">{nettoVolume.toFixed(3)} m³</span>
              </div>
            ) : bruttoArea > 0 && avgThickness === 0 ? (
              <div className="text-amber-500/80 text-[10px] font-mono">Wanddicke (D) setzen für Volumen</div>
            ) : null}
          </div>
        )}
        {/* Thickness assignment for selected walls */}
        {walls.length > 0 && onSaveMultiThickness && (
          <MultiThicknessInput walls={walls} onSave={onSaveMultiThickness} onSaveHeight={onSaveMultiHeight} />
        )}
        {/* Width/Height assignment for selected openings */}
        {openings.length > 0 && onSaveMultiOpeningDims && (
          <MultiOpeningInput openings={openings} onSave={onSaveMultiOpeningDims} />
        )}
      </div>
      <div className="mt-3 pt-2 border-t border-zinc-800/50 max-h-32 overflow-y-auto">
        <div className="flex flex-wrap gap-1">
          {elements.map(el => {
            const isOp = el.type === 'door' || el.type === 'window';
            return (
              <span key={el.id} className={`px-1.5 py-0.5 border font-mono text-[10px] truncate max-w-[120px] ${
                isOp ? 'bg-red-500/10 border-red-500/30 text-red-400' : 'bg-zinc-800 border-zinc-700 text-zinc-400'}`}>
                {isOp ? '−' : '+'} {el.label || el.name || el.id}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}


// ─── SUMMARY DROPDOWN ────────────────────────────────────────────
function SummaryDropdown({ result, filteredSvgData, scale, scaleInfo, totalAreaM2, totalWallLengthM }) {
  const [open, setOpen] = useState(false);

  const walls = (filteredSvgData?.wall_lines || []).filter(w => !w.excluded_from_lv);
  const openings = (filteredSvgData?.openings || []).filter(o => !o.excluded_from_lv);

  // Group walls by type
  const awWalls = walls.filter(w => w.wall_type === 'exterior');
  const iwWalls = walls.filter(w => w.wall_type === 'interior');
  const unclassifiedWalls = walls.filter(w => !w.wall_type);

  // Helper: sum wall metrics
  const calcWallStats = (wallGroup) => {
    let totalLength = 0, bruttoArea = 0, volume = 0;
    for (const w of wallGroup) {
      const l = safeNum(w.length_m_final || w.length_m);
      const h = safeNum(w.height_m);
      const d = safeNum(w.thickness_m_final || w.thickness_m);
      totalLength += l;
      if (h > 0) bruttoArea += l * h;
      if (h > 0 && d > 0) volume += l * h * d;
    }
    return { totalLength, bruttoArea, volume };
  };

  // Helper: sum opening areas by location (skip openings without height)
  const calcOpeningArea = (loc) => {
    let area = 0, count = 0, skipped = 0;
    for (const o of openings) {
      const oLoc = safeStr(o.location);
      if (oLoc === loc) {
        const w = safeNum(o.width_m_final || o.width_m);
        const h = safeNum(o.height_m_matched || o.height_m);
        if (w > 0 && h > 0) {
          area += w * h;
          count++;
        } else if (w > 0) {
          skipped++;
        }
      }
    }
    return { area, count, skipped };
  };

  const awStats = calcWallStats(awWalls);
  const iwStats = calcWallStats(iwWalls);
  const awOpenings = calcOpeningArea('exterior');
  const iwOpenings = calcOpeningArea('interior');
  const awNettoArea = awStats.bruttoArea - awOpenings.area;
  const iwNettoArea = iwStats.bruttoArea - iwOpenings.area;

  const doorCount = openings.filter(o => o.type === 'door').length;
  const windowCount = openings.filter(o => o.type === 'window').length;

  const WallTypeBlock = ({ label, color, stats, openingStats, nettoArea, wallCount }) => (
    <div className="mt-1.5 pt-1.5 border-t border-zinc-800/60">
      <div className="flex items-center gap-1.5 mb-1">
        <div className={`w-2 h-2 rounded-full ${color}`}></div>
        <span className="font-mono text-[10px] text-zinc-400 uppercase tracking-wider">{label} ({wallCount})</span>
      </div>
      <div className="space-y-0.5 pl-3.5">
        <div className="flex justify-between font-mono text-xs">
          <span className="text-zinc-500">Länge</span>
          <span className="text-zinc-200">{stats.totalLength.toFixed(2)} m</span>
        </div>
        {stats.bruttoArea > 0 && (
          <>
            <div className="flex justify-between font-mono text-xs">
              <span className="text-zinc-500">Brutto-Fläche (VOB)</span>
              <span className="text-zinc-300">{stats.bruttoArea.toFixed(2)} m²</span>
            </div>
            {openingStats.area > 0 && (
              <div className="flex justify-between font-mono text-xs">
                <span className="text-zinc-600">− Öffnungen ({openingStats.count})</span>
                <span className="text-red-400/80">−{openingStats.area.toFixed(2)} m²</span>
              </div>
            )}
            <div className="flex justify-between font-mono text-xs font-bold">
              <span className="text-zinc-400">Netto-Fläche (Material)</span>
              <span className="text-green-400">{Math.max(0, nettoArea).toFixed(2)} m²</span>
            </div>
            {openingStats.skipped > 0 && (
              <div className="flex justify-between font-mono text-[10px]">
                <span className="text-amber-500/80">⚠ {openingStats.skipped} Öffnung(en) ohne Höhe</span>
                <span className="text-amber-500/60">nicht abgezogen</span>
              </div>
            )}
          </>
        )}
        {stats.volume > 0 && (
          <div className="flex justify-between font-mono text-xs">
            <span className="text-zinc-500">Volumen</span>
            <span className="text-zinc-300">{stats.volume.toFixed(3)} m³</span>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div data-testid="summary-dropdown" className="border-t border-zinc-800">
      <button
        data-testid="summary-toggle"
        onClick={() => setOpen(o => !o)}
        className="w-full px-3 py-2.5 flex items-center justify-between hover:bg-zinc-800/30 transition-colors"
      >
        <span className="font-mono text-[10px] text-zinc-500 uppercase tracking-wider">Zusammenfassung</span>
        {open ? <ChevronDown className="w-3.5 h-3.5 text-zinc-500" /> : <ChevronRight className="w-3.5 h-3.5 text-zinc-500" />}
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-1">
          {/* Basic counts */}
          {[
            ['Räume', (filteredSvgData?.rooms || []).filter(r => !r.excluded_from_lv).length],
            ['Wandsegmente', walls.length],
            ['Türen', doorCount],
            ['Fenster', windowCount],
            ['Bemaßungen', result.svg_data?.dimensions?.length || 0],
          ].map(([label, count]) => (
            <div key={label} className="flex justify-between font-mono text-xs">
              <span className="text-zinc-500">{label}</span>
              <span className="text-zinc-200">{count}</span>
            </div>
          ))}

          {/* AW block */}
          {awWalls.length > 0 && (
            <WallTypeBlock
              label="Außenwände (AW)" color="bg-red-500" stats={awStats}
              openingStats={awOpenings} nettoArea={awNettoArea} wallCount={awWalls.length}
            />
          )}

          {/* IW block */}
          {iwWalls.length > 0 && (
            <WallTypeBlock
              label="Innenwände (IW)" color="bg-cyan-500" stats={iwStats}
              openingStats={iwOpenings} nettoArea={iwNettoArea} wallCount={iwWalls.length}
            />
          )}

          {/* Unclassified walls */}
          {unclassifiedWalls.length > 0 && (() => {
            const uStats = calcWallStats(unclassifiedWalls);
            return (
              <div className="mt-1.5 pt-1.5 border-t border-zinc-800/60">
                <div className="flex justify-between font-mono text-xs">
                  <span className="text-zinc-600">Nicht klassifiziert</span>
                  <span className="text-zinc-400">{unclassifiedWalls.length} Wände, {uStats.totalLength.toFixed(2)} m</span>
                </div>
              </div>
            );
          })()}

          {/* Calibration & total area */}
          {(scale || scaleInfo || result.svg_data?.has_scale || result.svg_data?.calibration) && (
            <div className="mt-1.5 pt-1.5 border-t border-zinc-800">
              {(() => {
                const calArea = result.svg_data?.metadata?.total_area_m2_calibrated;
                const displayArea = scale ? totalAreaM2 : (calArea || totalAreaM2 || result.svg_data?.metadata?.total_area_m2);
                return displayArea ? (
                  <div className="flex justify-between font-mono text-xs">
                    <span className="text-zinc-500">Gesamtfläche</span>
                    <span className="text-green-400 font-bold">{typeof displayArea === 'number' ? displayArea.toFixed(1) : displayArea} m²</span>
                  </div>
                ) : null;
              })()}
              {(() => {
                const calWall = result.svg_data?.metadata?.total_wall_length_m_calibrated;
                const displayWall = scale ? totalWallLengthM : (calWall || totalWallLengthM || result.svg_data?.metadata?.total_wall_length_m);
                return displayWall ? (
                  <div className="flex justify-between font-mono text-xs">
                    <span className="text-zinc-500">Wandlänge ges.</span>
                    <span className="text-blue-400 font-bold">{typeof displayWall === 'number' ? displayWall.toFixed(1) : displayWall} m</span>
                  </div>
                ) : null;
              })()}
              <div className="flex justify-between font-mono text-xs">
                <span className="text-zinc-500">Quelle</span>
                <span className={result.svg_data?.calibration ? 'text-emerald-400' : 'text-zinc-400'}>
                  {result.svg_data?.calibration ? `Kalibriert (${result.svg_data.calibration.source || 'auto'})` : 'Vorberechnet'}
                </span>
              </div>
              {result.svg_data?.calibration && (
                <div className="mt-1 pt-1 border-t border-zinc-800 space-y-1" data-testid="calibration-info">
                  <div className="flex justify-between font-mono text-xs">
                    <span className="text-zinc-500">Kalibrierung</span>
                    <span className="text-green-400">{result.svg_data.calibration.source || 'auto'}</span>
                  </div>
                  <div className="flex justify-between font-mono text-[10px]">
                    <span className="text-zinc-600">ppm_x / ppm_y</span>
                    <span className="text-zinc-500">
                      {(result.svg_data.calibration.ppm_x || result.svg_data.calibration.scale_x_ppm || 0).toFixed(4)} × {(result.svg_data.calibration.ppm_y || result.svg_data.calibration.scale_y_ppm || 0).toFixed(4)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="flex justify-between font-mono text-xs mt-1 pt-1 border-t border-zinc-800">
            <span className="text-zinc-500">Engine</span>
            <span className="text-blue-400">{result.analysis_method || 'laizyanalyze'}</span>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── SCALE CALIBRATION PANEL ─────────────────────────────────────
function ScaleCalibration({ calibrationMode, calibrationPoints, scale, pixelDist,
                            onStartCalibration, onCancelCalibration, onSetDistance, onResetScale, scaleInfo, ppmX, ppmY }) {
  const [distInput, setDistInput] = useState('');
  const [collapsed, setCollapsed] = useState(true);
  // Auto-expand when in calibration mode
  const isCalibrating = calibrationMode === 'point1' || calibrationMode === 'point2' || calibrationMode === 'input';

  const handleSubmit = (e) => {
    e.preventDefault();
    const val = parseFloat(distInput.replace(',', '.'));
    if (val > 0) onSetDistance(val);
  };

  return (
    <div className="border-b border-zinc-800">
      <button onClick={() => !isCalibrating && setCollapsed(c => !c)}
        className="w-full px-3 py-2 bg-zinc-800/30 flex items-center justify-between hover:bg-zinc-800/50 transition-colors">
        <span className="flex items-center gap-1.5">
          <Ruler className="w-3 h-3 text-amber-400" />
          <span className="font-mono text-[10px] text-zinc-500 uppercase tracking-wider">Massstab</span>
        </span>
        <div className="flex items-center gap-2">
          {ppmX > 0 && <span className="font-mono text-[9px] text-emerald-500">{ppmX.toFixed(1)}</span>}
          {collapsed && !isCalibrating ? <ChevronRight className="w-3 h-3 text-zinc-600" /> : <ChevronDown className="w-3 h-3 text-zinc-600" />}
        </div>
      </button>
      {(!collapsed || isCalibrating) && (
      <div className="px-3 py-2.5">
        {!calibrationMode && !scale && (
          <div className="space-y-2">
            {(ppmX > 0 || scaleInfo) && (
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                <span className="font-mono text-xs text-emerald-400">
                  {ppmX > 0 ? `Kalibriert (${ppmX.toFixed(2)} %/m × ${ppmY.toFixed(2)} %/m)` : 'API-Skala erkannt'}
                </span>
              </div>
            )}
            <button data-testid="start-calibration-btn" onClick={onStartCalibration}
              className="w-full px-3 py-2 bg-amber-500/10 border border-amber-500/30 text-amber-400 font-mono text-xs hover:bg-amber-500/20 transition-colors flex items-center justify-center gap-2">
              <Crosshair className="w-3.5 h-3.5" /> {ppmX > 0 ? 'Manuell neu kalibrieren' : 'Maßstab kalibrieren'}
            </button>
            {ppmX > 0 && (
              <p className="font-mono text-[10px] text-zinc-500">Manuelle Kalibrierung überschreibt automatische Werte.</p>
            )}
          </div>
        )}

        {calibrationMode === 'point1' && (
          <div className="space-y-2">
            <p className="font-mono text-xs text-amber-400">Klicken Sie auf <strong>Punkt 1</strong> im Plan</p>
            <p className="font-mono text-[10px] text-zinc-500">Wählen Sie den Startpunkt einer bekannten Strecke</p>
            <button onClick={onCancelCalibration} className="font-mono text-[10px] text-zinc-500 hover:text-white">Abbrechen</button>
          </div>
        )}

        {calibrationMode === 'point2' && (
          <div className="space-y-2">
            <p className="font-mono text-xs text-amber-400">Klicken Sie auf <strong>Punkt 2</strong> im Plan</p>
            <p className="font-mono text-[10px] text-zinc-500">Wählen Sie den Endpunkt der bekannten Strecke</p>
            <button onClick={onCancelCalibration} className="font-mono text-[10px] text-zinc-500 hover:text-white">Abbrechen</button>
          </div>
        )}

        {calibrationMode === 'input' && (
          <form onSubmit={handleSubmit} className="space-y-2">
            <p className="font-mono text-[10px] text-zinc-400">Wie lang ist die markierte Strecke in Metern?</p>
            <div className="flex gap-1.5">
              <input data-testid="distance-input" type="text" value={distInput}
                onChange={(e) => setDistInput(e.target.value)}
                placeholder="z.B. 5.07"
                autoFocus
                className="flex-1 bg-zinc-900 border border-zinc-700 px-2 py-1.5 font-mono text-xs text-white placeholder:text-zinc-600 focus:border-amber-500 focus:outline-none" />
              <span className="font-mono text-xs text-zinc-500 self-center">m</span>
            </div>
            <div className="flex gap-2">
              <button data-testid="confirm-scale-btn" type="submit"
                className="flex-1 px-2 py-1.5 bg-amber-500 text-black font-mono text-xs font-bold hover:bg-amber-400 transition-colors">
                Bestätigen
              </button>
              <button type="button" onClick={onCancelCalibration}
                className="px-2 py-1.5 border border-zinc-700 font-mono text-xs text-zinc-400 hover:text-white">
                Abbruch
              </button>
            </div>
          </form>
        )}

        {scale && !calibrationMode && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500"></div>
              <span className="font-mono text-xs text-green-400">Kalibriert</span>
            </div>
            <div className="font-mono text-[10px] text-zinc-400 space-y-0.5">
              <div>1 Pixel = {(1/scale).toFixed(4)} m</div>
              <div>{Math.round(scale)} px/m</div>
            </div>
            <div className="flex gap-2">
              <button onClick={onStartCalibration}
                className="font-mono text-[10px] text-zinc-500 hover:text-amber-400 transition-colors">
                Neu kalibrieren
              </button>
              <button data-testid="reset-scale-btn" onClick={onResetScale}
                className="font-mono text-[10px] text-zinc-500 hover:text-red-400 transition-colors">
                Zurücksetzen
              </button>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
}

// ─── HISTORY SIDEBAR ─────────────────────────────────────────────
function HistorySidebar({ analyses, onSelect, onClose, onDelete }) {
  return (
    <div data-testid="history-sidebar" className="fixed inset-y-0 right-0 w-80 bg-zinc-900 border-l border-zinc-700 z-50 flex flex-col slide-up">
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-700">
        <span className="font-mono text-sm font-medium">Analyse-Verlauf</span>
        <button onClick={onClose} data-testid="close-history" className="text-zinc-500 hover:text-white transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {analyses.length === 0 && <p className="p-4 font-mono text-xs text-zinc-500">Noch keine Analysen.</p>}
        {analyses.map((a) => (
          <div key={a.analysis_id} className="flex items-center border-b border-zinc-800/50 hover:bg-zinc-800 transition-colors group">
            <button onClick={() => onSelect(a.analysis_id)}
              data-testid={`history-item-${a.analysis_id}`}
              className="flex-1 text-left px-4 py-3">
              <div className="flex items-center gap-2 mb-1">
                <FileImage className="w-3.5 h-3.5 text-blue-400" />
                <span className="font-mono text-xs text-zinc-300 truncate">{a.filename}</span>
              </div>
              <div className="flex items-center gap-3 text-[10px] text-zinc-500 font-mono">
                <span>{new Date(a.created_at).toLocaleDateString('de-DE')}</span>
                <span>{a.processing_time}s</span>
              </div>
            </button>
            <button
              data-testid={`delete-analysis-${a.analysis_id}`}
              onClick={(e) => { e.stopPropagation(); onDelete(a.analysis_id, a.filename); }}
              className="px-3 py-3 text-zinc-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
              title="Analyse löschen">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── MAIN APP ────────────────────────────────────────────────────
export default function App({ onNavigate }) {
  const modal = useModal();
  // Navigation: 'projects' | 'project-detail' | 'analysis'
  const [view, setView] = useState('projects');
  const [currentProjectId, setCurrentProjectId] = useState(null);

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingStart, setLoadingStart] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [analyses, setAnalyses] = useState([]);
  const [selectedElements, setSelectedElements] = useState([]);
  const [lvEditorOpen, setLvEditorOpen] = useState(false);
  const [lvEditorElementIds, setLvEditorElementIds] = useState([]);
  const [activeLayers, setActiveLayers] = useState({
    walls: false, rooms: false, windows: false, doors: false, fixtures: false,
    roomPolygons: true, wallPolygons: true, wallLines: false, openings: true, dimensions: false,
    grid: false, wallTypes: false, envelope: false, dimensionGrid: false, dimensionGridGpt: false,
  });

  // Scale calibration state
  const [calibrationMode, setCalibrationMode] = useState(null);
  const [calibrationPoints, setCalibrationPoints] = useState({ p1: null, p2: null });
  const [pixelDist, setPixelDist] = useState(0);
  const [scale, setScale] = useState(null);

  // Polygon editing state
  const [editMode, setEditMode] = useState(null); // { id, type } of element being edited
  const [localSvgOverrides, setLocalSvgOverrides] = useState({}); // elementId → polygon
  const [showElementLists, setShowElementLists] = useState(false);
  const [customElements, setCustomElements] = useState([]); // manually drawn elements
  const [drawingMode, setDrawingMode] = useState(null); // null | 'wall' | 'room' | 'window' | 'door'
  const [drawingPoints, setDrawingPoints] = useState([]);
  const [hasUnsavedEdits, setHasUnsavedEdits] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({ rooms: true, walls: true, openings: true, wallGroups: true, extWalls: true, intWalls: true, layerToggles: true });
  const [undoStack, setUndoStack] = useState([]); // [{overrides, custom}, ...]
  const [editingElement, setEditingElement] = useState(null); // element being edited in modal
  const [gptGridLoading, setGptGridLoading] = useState(false);
  const [toolboxOpen, setToolboxOpen] = useState(false); // Werkzeugkasten standardmäßig zu
  const [darkMode, setDarkMode] = useState(localStorage.getItem('editorTheme') === 'light' ? false : true);
  const [layerPanelOpen, setLayerPanelOpen] = useState(true);
  const [validationPanelOpen, setValidationPanelOpen] = useState(false);

  const toggleLayer = (layer) => setActiveLayers(prev => ({ ...prev, [layer]: !prev[layer] }));

  const fetchAnalyses = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/analyses`);
      setAnalyses(res.data.analyses || []);
    } catch (e) { console.error('Failed to fetch analyses:', e); }
  }, []);

  const loadAnalysis = useCallback(async (id) => {
    try {
      setLoading(true); setError(null);
      const res = await axios.get(`${API_URL}/api/analyses/${id}`);
      setResult(res.data); setShowHistory(false); setSelectedElements([]);
      setScale(null); setCalibrationMode(null); setCalibrationPoints({ p1: null, p2: null });
      setEditMode(null); setLocalSvgOverrides({}); setCustomElements([]); setDrawingMode(null); setDrawingPoints([]); setHasUnsavedEdits(false); setUndoStack([]);
    } catch (e) { setError('Analyse konnte nicht geladen werden'); }
    finally { setLoading(false); }
  }, []);

  const deleteAnalysis = useCallback(async (id, filename) => {
    if (!await modal.confirm(`Analyse "${filename}" wirklich löschen?`, { title: 'Analyse löschen', confirmText: 'Löschen', type: 'warning' })) return;
    try {
      await axios.delete(`${API_URL}/api/analyses/${id}`);
      setAnalyses(prev => prev.filter(a => a.analysis_id !== id));
      if ((result?.id || result?.analysis_id) === id) { setResult(null); setSelectedElements([]); }
    } catch (e) { console.error('Delete failed:', e); }
  }, [result]);

  const handleFile = useCallback(async (file, projectId, floorLevel) => {
    setLoading(true); setError(null); setResult(null); setSelectedElements([]); setLoadingStart(Date.now());
    setScale(null); setCalibrationMode(null); setCalibrationPoints({ p1: null, p2: null });
    setEditMode(null); setLocalSvgOverrides({}); setCustomElements([]); setDrawingMode(null); setDrawingPoints([]); setHasUnsavedEdits(false);
    const formData = new FormData();
    formData.append('file', file);
    if (projectId) formData.append('project_id', projectId);
    if (floorLevel !== undefined && floorLevel !== null) formData.append('floor_level', String(floorLevel));
    try {
      const res = await axios.post(`${API_URL}/api/analyze`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });
      const data = res.data;
      // Async analysis: poll until completed
      if (data.status === 'processing' && data.analysis_id) {
        const aid = data.analysis_id;
        let attempts = 0;
        while (attempts < 120) { // max 10 min polling (5s intervals)
          await new Promise(r => setTimeout(r, 5000));
          try {
            const poll = await axios.get(`${API_URL}/api/analyses/${aid}`);
            if (poll.data.status === 'completed') {
              setResult(poll.data);
              fetchAnalyses();
              return;
            }
            if (poll.data.status === 'failed') {
              setError(poll.data.error || 'Analyse fehlgeschlagen.');
              return;
            }
          } catch { /* retry */ }
          attempts++;
        }
        setError('Analyse-Timeout. Bitte erneut versuchen.');
      } else {
        setResult(data);
        fetchAnalyses();
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Analyse fehlgeschlagen. Bitte erneut versuchen.');
    } finally { setLoading(false); setLoadingStart(null); }
  }, [fetchAnalyses]);

  // Upload handler for project detail view - uploads and navigates to analysis
  const handleUploadToFloor = useCallback(async (file, projectId, floorLevel) => {
    setView('analysis');
    await handleFile(file, projectId, floorLevel);
  }, [handleFile]);

  // Navigate to project list
  const goToProjects = useCallback(() => {
    setView('projects');
    setCurrentProjectId(null);
    setResult(null);
    setError(null);
    setSelectedElements([]);
  }, []);

  // Navigate to project detail
  const goToProjectDetail = useCallback((projectId) => {
    setCurrentProjectId(projectId);
    setView('project-detail');
  }, []);

  // Open analysis from project detail
  const openAnalysisFromProject = useCallback(async (analysisId) => {
    setView('analysis');
    try {
      setLoading(true); setError(null);
      const res = await axios.get(`${API_URL}/api/analyses/${analysisId}`);
      setResult(res.data); setSelectedElements([]);
      setScale(null); setCalibrationMode(null); setCalibrationPoints({ p1: null, p2: null });
      setEditMode(null); setLocalSvgOverrides({}); setCustomElements([]); setDrawingMode(null); setDrawingPoints([]); setHasUnsavedEdits(false); setUndoStack([]);
    } catch (e) { setError('Analyse konnte nicht geladen werden'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAnalyses(); }, [fetchAnalyses]);

  // ─── Calibration handlers ────────────────────────────────────
  const startCalibration = useCallback(() => {
    setCalibrationMode('point1');
    setCalibrationPoints({ p1: null, p2: null });
    setSelectedElements([]);
  }, []);

  const cancelCalibration = useCallback(() => {
    setCalibrationMode(null);
    setCalibrationPoints({ p1: null, p2: null });
  }, []);

  const handleCalibrationClick = useCallback((point) => {
    if (calibrationMode === 'point1') {
      setCalibrationPoints({ p1: point, p2: null });
      setCalibrationMode('point2');
    } else if (calibrationMode === 'point2') {
      const p1 = calibrationPoints.p1;
      const dist = Math.sqrt(Math.pow(point.x - p1.x, 2) + Math.pow(point.y - p1.y, 2));
      setCalibrationPoints(prev => ({ ...prev, p2: point }));
      setPixelDist(dist);
      setCalibrationMode('input');
    }
  }, [calibrationMode, calibrationPoints.p1]);

  const handleSetDistance = useCallback((meters) => {
    if (pixelDist > 0 && meters > 0) {
      const pxPerMeter = pixelDist / meters;
      setScale(pxPerMeter);
      setCalibrationMode(null);
    }
  }, [pixelDist]);

  const resetScale = useCallback(() => {
    setScale(null);
    setCalibrationPoints({ p1: null, p2: null });
    setCalibrationMode(null);
  }, []);

  // ─── Multi-selection handler ─────────────────────────────────
  const handleElementSelect = useCallback((element, event) => {
    if (!element) {
      setSelectedElements([]);
      return;
    }
    const isMulti = event?.ctrlKey || event?.metaKey;
    if (isMulti) {
      setSelectedElements(prev => {
        const exists = prev.some(el => el.id === element.id);
        if (exists) return prev.filter(el => el.id !== element.id);
        return [...prev, element];
      });
    } else {
      setSelectedElements(prev => {
        if (prev.length === 1 && prev[0].id === element.id) return [];
        return [element];
      });
    }
  }, []);

  // ─── Process SVG data with labels ────────────────────────────
  const svgData = result?.svg_data;
  const scaleInfo = svgData?.scale_info || null;

  // Extract ppm values from calibration for coordinate display
  const ppmX = svgData?.calibration?.ppm_x || svgData?.calibration?.scale_x_ppm || 0;
  const ppmY = svgData?.calibration?.ppm_y || svgData?.calibration?.scale_y_ppm || 0;

  const filteredSvgData = useMemo(() => {
    if (!svgData) return null;

    const rooms = (svgData.rooms || [])
      .filter(r => {
        if (r.custom) return true;
        if (r.laizy_id) return true;
        if (r.area_m2) return true;
        return (r.area_pct || 0) >= 1.0;
      })
      .map((room, i) => {
        const override = localSvgOverrides[room.id];
        let polygon = override || room.polygon;
        let centroid = room.centroid;
        // When polygon is overridden, clear static area so dynamic recalculation kicks in
        let areaOverrides = {};
        if (override && polygon.length >= 3) {
          const cx = polygon.reduce((s, p) => s + p[0], 0) / polygon.length;
          const cy = polygon.reduce((s, p) => s + p[1], 0) / polygon.length;
          centroid = [cx, cy];
          areaOverrides = { area_m2_calibrated: null, area_m2: null, area_text: null };
        }
        return {
          ...room,
          ...areaOverrides,
          polygon,
          centroid,
          letter: room.letter || (LETTERS[i] || `${i + 1}`),
          label: room.label || `Raum ${LETTERS[i] || i + 1} - ${room.type_de || translateRoom(room.name)}`,
        };
      });

    // Append custom room elements
    const customRooms = customElements
      .filter(c => c.type !== 'wall' && c.type !== 'window' && c.type !== 'door')
      .map((c, i) => {
        const override = localSvgOverrides[c.id];
        let polygon = override || c.polygon;
        let centroid = c.centroid;
        if (override && polygon.length >= 3) {
          const cx = polygon.reduce((s, p) => s + p[0], 0) / polygon.length;
          const cy = polygon.reduce((s, p) => s + p[1], 0) / polygon.length;
          centroid = [cx, cy];
        }
        return { ...c, polygon, centroid, letter: `+${i + 1}`, label: c.name };
      });

    // Apply overrides to walls
    const walls = (svgData.wall_lines || []).map((wall, i) => {
      const wallId = wall.id || `wall_${i}`;
      const override = localSvgOverrides[wallId];
      return override ? { ...wall, polygon: override } : wall;
    });

    // Append custom wall elements
    const customWalls = customElements
      .filter(c => c.type === 'wall')
      .map(c => {
        const override = localSvgOverrides[c.id];
        return override ? { ...c, polygon: override } : c;
      });

    // Apply overrides to openings (supports both polygon and virtual polygon overrides)
    const openings = (svgData.openings || []).map(opening => {
      const override = localSvgOverrides[opening.id];
      if (!override) return opening;
      // Check if this is a virtual polygon override (2-3 pts for control points)
      if (override.length <= 3 && (opening.from_pt || opening.hinge)) {
        const updated = { ...opening };
        if (opening.type === 'window' || (opening.from_pt && !opening.hinge)) {
          if (override[0]) updated.from_pt = override[0];
          if (override[1]) updated.to_pt = override[1];
        }
        if (opening.type === 'door' || opening.hinge) {
          if (override[0]) updated.hinge = override[0];
          if (override[1]) updated.closed_pt = override[1];
          if (override[2]) updated.open_pt = override[2];
        }
        // Recalculate position/midpoint
        let pt1 = null, pt2 = null;
        if (updated.from_pt && updated.to_pt) {
          updated.position = [(updated.from_pt[0] + updated.to_pt[0]) / 2, (updated.from_pt[1] + updated.to_pt[1]) / 2];
          pt1 = updated.from_pt; pt2 = updated.to_pt;
        } else if (updated.hinge && updated.closed_pt) {
          updated.position = [(updated.hinge[0] + updated.closed_pt[0]) / 2, (updated.hinge[1] + updated.closed_pt[1]) / 2];
          pt1 = updated.hinge; pt2 = updated.closed_pt;
        }
        // Recalculate width dynamically from new control points
        if (pt1 && pt2 && (ppmX > 0 || ppmY > 0)) {
          const dx = (pt2[0] - pt1[0]) / (ppmX || 1);
          const dy = (pt2[1] - pt1[1]) / (ppmY || 1);
          const newWidthM = Math.sqrt(dx * dx + dy * dy);
          if (newWidthM > 0.01) {
            const w = parseFloat(newWidthM.toFixed(3));
            updated.width_m = w;
            updated.width_m_matched = w;
            updated.width_m_final = w;
            updated.width_source = 'drag';
          }
        }
        return updated;
      }
      return { ...opening, polygon: override };
    });

    // Append custom opening elements
    const customOpenings = customElements
      .filter(c => c.type === 'window' || c.type === 'door')
      .map(c => {
        const override = localSvgOverrides[c.id];
        return override ? { ...c, polygon: override } : c;
      });

    return {
      ...svgData,
      rooms: [...rooms, ...customRooms],
      wall_lines: [...walls, ...customWalls],
      openings: [...openings, ...customOpenings],
    };
  }, [svgData, localSvgOverrides, customElements]);

  const imgW = result?.image_size?.width || 800;
  const imgH = result?.image_size?.height || 600;

  // ─── Compute totals from backend-computed values ─────────────
  const totalAreaM2 = useMemo(() => {
    if (!filteredSvgData?.rooms) return null;
    let total = 0;
    for (const room of filteredSvgData.rooms) {
      const a = room.area_m2_override || room.area_m2 || room.area_m2_calibrated;
      if (a) total += parseFloat(a);
    }
    return total > 0 ? +total.toFixed(1) : (svgData?.metadata?.total_area_m2 || null);
  }, [filteredSvgData, svgData]);

  const totalWallLengthM = useMemo(() => {
    if (!filteredSvgData?.wall_lines) return null;
    let total = 0;
    for (const wall of filteredSvgData.wall_lines) {
      const l = wall.length_m || wall.length_m_calibrated;
      if (l) total += parseFloat(l);
    }
    return total > 0 ? +total.toFixed(1) : (svgData?.metadata?.total_wall_length_m || null);
  }, [filteredSvgData, svgData]);

  // ─── Quick-selection helpers ─────────────────────────────────
  const selectAllRooms = useCallback(() => {
    if (!filteredSvgData?.rooms) return;
    setSelectedElements(filteredSvgData.rooms);
  }, [filteredSvgData]);

  const selectAllWalls = useCallback(() => {
    if (!filteredSvgData?.wall_lines) return;
    setSelectedElements(filteredSvgData.wall_lines.map((line, i) => ({
      ...line,
      id: line.id || `wall_${i}`,
      type: 'wall',
      name: line.name || `Wand ${i + 1}`,
      label: line.name || `Wand ${i + 1}`,
      color: '#dc2626',
    })));
  }, [filteredSvgData]);

  const clearSelection = useCallback(() => {
    setSelectedElements([]);
  }, []);

  // ─── Polygon editing handlers ────────────────────────────────
  const getOriginalPolygon = useCallback((elementId) => {
    if (!svgData) return null;
    const room = svgData.rooms?.find(r => r.id === elementId);
    if (room) return room.polygon;
    const wallIdx = svgData.wall_lines?.findIndex((w, i) => (w.id || `wall_${i}`) === elementId);
    if (wallIdx >= 0) return svgData.wall_lines[wallIdx].polygon;
    const opening = svgData.openings?.find(o => o.id === elementId);
    if (opening) {
      // Return real polygon if it exists
      if (opening.polygon && opening.polygon.length >= 3) return opening.polygon;
      // Create virtual polygon from control points
      if (opening.type === 'window' && opening.from_pt && opening.to_pt) {
        return [opening.from_pt, opening.to_pt];
      }
      if (opening.type === 'door') {
        const pts = [];
        if (opening.hinge) pts.push(opening.hinge);
        if (opening.closed_pt) pts.push(opening.closed_pt);
        if (opening.open_pt) pts.push(opening.open_pt);
        if (pts.length >= 2) return pts;
      }
      // Fallback: try from_pt/to_pt for any type
      if (opening.from_pt && opening.to_pt) return [opening.from_pt, opening.to_pt];
      if (opening.hinge && opening.closed_pt) {
        const pts = [opening.hinge, opening.closed_pt];
        if (opening.open_pt) pts.push(opening.open_pt);
        return pts;
      }
    }
    const custom = customElements.find(c => c.id === elementId);
    if (custom) return custom.polygon;
    // Envelope
    if (elementId === '__envelope__' && svgData.envelope) return svgData.envelope;
    return null;
  }, [svgData, customElements]);

  // Push current state onto undo stack (call BEFORE making changes)
  const pushUndo = useCallback(() => {
    setUndoStack(prev => [...prev, {
      overrides: JSON.parse(JSON.stringify(localSvgOverrides)),
      custom: JSON.parse(JSON.stringify(customElements)),
    }]);
  }, [localSvgOverrides, customElements]);

  // Called once when vertex drag begins (not on every mousemove)
  const handleDragStart = useCallback(() => {
    pushUndo();
  }, [pushUndo]);

  const handleVertexMove = useCallback((elementId, vertexIndex, newPct) => {
    setLocalSvgOverrides(prev => {
      const original = prev[elementId] || getOriginalPolygon(elementId);
      if (!original) return prev;
      const polygon = original.map(p => [...p]);

      // 90° rectangle constraint for walls with exactly 4 vertices
      const isWall4 = editMode?.type === 'wall' && polygon.length === 4;
      if (isWall4) {
        const n = 4;
        const prevIdx = (vertexIndex + 3) % n;
        const nextIdx = (vertexIndex + 1) % n;
        // Determine edge directions from current polygon state
        const prevEdgeX = polygon[vertexIndex][0] - polygon[prevIdx][0];
        const prevEdgeY = polygon[vertexIndex][1] - polygon[prevIdx][1];
        const nextEdgeX = polygon[nextIdx][0] - polygon[vertexIndex][0];
        const nextEdgeY = polygon[nextIdx][1] - polygon[vertexIndex][1];
        // Move the dragged vertex
        polygon[vertexIndex] = [...newPct];
        // Previous neighbor: maintain shared axis
        if (Math.abs(prevEdgeX) > Math.abs(prevEdgeY)) {
          // Edge prev→current is horizontal → share Y
          polygon[prevIdx] = [polygon[prevIdx][0], newPct[1]];
        } else {
          // Edge prev→current is vertical → share X
          polygon[prevIdx] = [newPct[0], polygon[prevIdx][1]];
        }
        // Next neighbor: maintain shared axis
        if (Math.abs(nextEdgeX) > Math.abs(nextEdgeY)) {
          // Edge current→next is horizontal → share Y
          polygon[nextIdx] = [polygon[nextIdx][0], newPct[1]];
        } else {
          // Edge current→next is vertical → share X
          polygon[nextIdx] = [newPct[0], polygon[nextIdx][1]];
        }
      } else {
        polygon[vertexIndex] = newPct;
      }

      return { ...prev, [elementId]: polygon };
    });
    setHasUnsavedEdits(true);
  }, [getOriginalPolygon, editMode]);

  const handleEdgeMove = useCallback((elementId, edgeIndex, newPct, lastPct) => {
    if (!newPct || !lastPct) return;
    const dx = newPct[0] - lastPct[0];
    const dy = newPct[1] - lastPct[1];
    setLocalSvgOverrides(prev => {
      const original = prev[elementId] || getOriginalPolygon(elementId);
      if (!original) return prev;
      const polygon = original.map(p => [...p]);
      const i1 = edgeIndex;
      const i2 = (edgeIndex + 1) % polygon.length;
      const isH = Math.abs(polygon[i1][1] - polygon[i2][1]) < 0.05;
      if (isH) {
        polygon[i1][1] += dy;
        polygon[i2][1] += dy;
      } else {
        polygon[i1][0] += dx;
        polygon[i2][0] += dx;
      }
      return { ...prev, [elementId]: polygon };
    });
    setHasUnsavedEdits(true);
  }, [getOriginalPolygon]);

  const handlePolygonMove = useCallback((elementId, newPct, lastPct) => {
    if (!newPct || !lastPct) return;
    const dx = newPct[0] - lastPct[0];
    const dy = newPct[1] - lastPct[1];
    setLocalSvgOverrides(prev => {
      const original = prev[elementId] || getOriginalPolygon(elementId);
      if (!original) return prev;
      const polygon = original.map(p => [p[0] + dx, p[1] + dy]);
      return { ...prev, [elementId]: polygon };
    });
    setHasUnsavedEdits(true);
  }, [getOriginalPolygon]);



  const handleAddVertex = useCallback((elementId, afterIndex) => {
    pushUndo();
    setLocalSvgOverrides(prev => {
      const original = prev[elementId] || getOriginalPolygon(elementId);
      if (!original) return prev;
      const polygon = [...original];
      const next = polygon[(afterIndex + 1) % polygon.length];
      const mid = [(polygon[afterIndex][0] + next[0]) / 2, (polygon[afterIndex][1] + next[1]) / 2];
      polygon.splice(afterIndex + 1, 0, mid);
      return { ...prev, [elementId]: polygon };
    });
    setHasUnsavedEdits(true);
  }, [getOriginalPolygon, pushUndo]);

  const handleDeleteVertex = useCallback((elementId, vertexIndex) => {
    pushUndo();
    setLocalSvgOverrides(prev => {
      const original = prev[elementId] || getOriginalPolygon(elementId);
      if (!original || original.length <= 3) return prev;
      const polygon = [...original];
      polygon.splice(vertexIndex, 1);
      return { ...prev, [elementId]: polygon };
    });
    setHasUnsavedEdits(true);
  }, [getOriginalPolygon, pushUndo]);

  const startEditMode = useCallback((element) => {
    if (!element) return;
    setEditMode({ id: element.id, type: element.type || 'room' });
    setSelectedElements([element]);
    setDrawingMode(null);
    setDrawingPoints([]);
  }, []);

  const stopEditMode = useCallback(() => {
    setEditMode(null);
  }, []);

  const handleEditElement = useCallback((element) => {
    setEditingElement(element);
  }, []);

  const handleDeleteElement = useCallback(async (element) => {
    if (!result?.svg_data) return;
    const elementId = element.id;

    // Deep copy the relevant arrays
    const svgData = {
      ...result.svg_data,
      openings: [...(result.svg_data.openings || [])],
      wall_lines: [...(result.svg_data.wall_lines || [])],
      rooms: [...(result.svg_data.rooms || [])],
    };

    // Remove from the correct list
    let found = false;
    for (const key of ['openings', 'wall_lines', 'rooms']) {
      const idx = svgData[key].findIndex(el => el.id === elementId);
      if (idx !== -1) {
        svgData[key] = svgData[key].filter(el => el.id !== elementId);
        found = true;
        break;
      }
    }
    if (!found) return;

    // Also clear any local overrides for this element
    setLocalSvgOverrides(prev => {
      const next = { ...prev };
      delete next[elementId];
      return next;
    });

    // Save to backend
    const analysisId = result.id || result.analysis_id;
    if (analysisId) {
      try {
        await axios.patch(`${API_URL}/api/analyses/${analysisId}/svg-data`, { svg_data: svgData });
      } catch (e) {
        console.error('Delete element failed:', e);
      }
    }

    setResult(prev => ({ ...prev, svg_data: svgData }));
    setSelectedElements([]);
  }, [result]);

  const handleSaveElementEdit = useCallback(async (elementId, updates) => {
    if (!result?.svg_data) return;
    const svgData = { ...result.svg_data };

    // Detect wall thickness change for propagation
    const isWallThicknessChange = updates.thickness_source === 'manual' && updates.thickness_m;
    // Detect opening width change for propagation
    const isOpeningWidthChange = updates.width_source === 'manual' && updates.width_m_matched;
    let oldThickness = null;

    // Find and update the element in the correct list
    const lists = [
      { key: 'openings', items: svgData.openings || [] },
      { key: 'wall_lines', items: svgData.wall_lines || [] },
      { key: 'rooms', items: svgData.rooms || [] },
    ];

    let foundInSvg = false;
    for (const list of lists) {
      const idx = list.items.findIndex(el => el.id === elementId);
      if (idx !== -1) {
        if (isWallThicknessChange && list.key === 'wall_lines') {
          oldThickness = list.items[idx].thickness_m;
        }
        list.items[idx] = { ...list.items[idx], ...updates };
        svgData[list.key] = [...list.items];
        foundInSvg = true;
        break;
      }
    }

    // Also check customElements (for newly drawn elements not yet saved to backend)
    if (!foundInSvg) {
      const customIdx = customElements.findIndex(el => el.id === elementId);
      if (customIdx !== -1) {
        setCustomElements(prev => prev.map(el =>
          el.id === elementId ? { ...el, ...updates } : el
        ));
        setSelectedElements(prev => prev.map(el => el.id === elementId ? { ...el, ...updates } : el));
        setEditingElement(null);
        setHasUnsavedEdits(true);
        return;
      }
    }

    // Save to backend
    const analysisId = result.id || result.analysis_id;
    if (analysisId) {
      try {
        await axios.patch(`${API_URL}/api/analyses/${analysisId}/svg-data`, { svg_data: svgData });

        // Propagate wall thickness to all walls with same pixel width
        if (isWallThicknessChange && updates.thickness_m !== oldThickness) {
          const propResp = await fetch(`${API_URL}/api/analyses/${analysisId}/propagate-thickness`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ wall_id: elementId, thickness_m: updates.thickness_m }),
          });
          if (propResp.ok) {
            const propData = await propResp.json();
            if (propData.propagated > 1) {
              // Reload to get all propagated values
              const reloadResp = await fetch(`${API_URL}/api/analyses/${analysisId}`);
              if (reloadResp.ok) {
                setResult(await reloadResp.json());
                setSelectedElements(prev => prev.map(el => el.id === elementId ? { ...el, ...updates } : el));
                setEditingElement(null);
                return;
              }
            }
          }
        }

        // Propagate opening width to all same-type openings within ±3cm
        if (isOpeningWidthChange) {
          const propResp = await fetch(`${API_URL}/api/analyses/${analysisId}/propagate-opening-width`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ opening_id: elementId, width_m: updates.width_m_matched }),
          });
          if (propResp.ok) {
            const propData = await propResp.json();
            if (propData.propagated > 1) {
              const reloadResp = await fetch(`${API_URL}/api/analyses/${analysisId}`);
              if (reloadResp.ok) {
                setResult(await reloadResp.json());
                setSelectedElements(prev => prev.map(el => el.id === elementId ? { ...el, ...updates } : el));
                setEditingElement(null);
                return;
              }
            }
          }
        }
      } catch (e) {
        console.error('Save edit failed:', e);
      }
    }

    // Update local state
    setResult(prev => ({ ...prev, svg_data: svgData }));

    // Update selected elements to reflect changes
    setSelectedElements(prev => prev.map(el => el.id === elementId ? { ...el, ...updates } : el));

    setEditingElement(null);
  }, [result, customElements]);

  const handleSaveMultiThickness = useCallback(async (wallIds, thickness_m) => {
    if (!result?.svg_data || !wallIds.length) return;
    const svgData = { ...result.svg_data };
    const walls = [...(svgData.wall_lines || [])];

    for (const wall of walls) {
      if (wallIds.includes(wall.id)) {
        wall.thickness_m = thickness_m;
        wall.thickness_m_final = thickness_m;
        wall.thickness_source = 'manual';
      }
    }
    svgData.wall_lines = walls;

    const analysisId = result.id || result.analysis_id;
    if (analysisId) {
      await axios.patch(`${API_URL}/api/analyses/${analysisId}/svg-data`, { svg_data: svgData });
    }
    setResult(prev => ({ ...prev, svg_data: svgData }));
    setSelectedElements(prev => prev.map(el =>
      wallIds.includes(el.id) ? { ...el, thickness_m, thickness_m_final: thickness_m, thickness_source: 'manual' } : el
    ));
  }, [result]);

  const handleSaveMultiHeight = useCallback(async (wallIds, height_m) => {
    if (!result?.svg_data || !wallIds.length) return;
    const svgData = { ...result.svg_data };
    const walls = [...(svgData.wall_lines || [])];

    for (const wall of walls) {
      if (wallIds.includes(wall.id)) {
        wall.height_m = height_m;
      }
    }
    svgData.wall_lines = walls;

    const analysisId = result.id || result.analysis_id;
    if (analysisId) {
      await axios.patch(`${API_URL}/api/analyses/${analysisId}/svg-data`, { svg_data: svgData });
    }
    setResult(prev => ({ ...prev, svg_data: svgData }));
    setSelectedElements(prev => prev.map(el =>
      wallIds.includes(el.id) ? { ...el, height_m } : el
    ));
  }, [result]);




  const handleFlipDoorGeometry = useCallback(async (doorId, flipType) => {
    // 'refetch' = backend already persisted flip; just reload analysis
    if (flipType === 'refetch') {
      const aid = result?.analysis_id || result?.id;
      if (!aid) return;
      try {
        const r = await axios.get(`${API_URL}/api/analyses/${aid}`);
        setResult(r.data);
      } catch (e) { console.error(e); }
      return;
    }
    if (!result?.svg_data) return;

    // Check if this door is a manual/custom element (not yet persisted)
    const customDoor = customElements.find(c => c.id === doorId && c.type === 'door');
    if (customDoor) {
      // Mutate a copy of the manual door
      const door = { ...customDoor };
      if (!door.hinge) door.hinge = door.from_pt || [0, 0];
      if (!door.closed_pt) door.closed_pt = door.to_pt || [0, 0];
      if (!door.open_pt) {
        const dx = door.closed_pt[0] - door.hinge[0];
        const dy = door.closed_pt[1] - door.hinge[1];
        door.open_pt = [door.hinge[0] - dy, door.hinge[1] + dx];
      }
      const hinge = door.hinge, closed = door.closed_pt, openPt = door.open_pt;
      if (flipType === 'hinge') {
        const swingDir = [openPt[0] - hinge[0], openPt[1] - hinge[1]];
        door.hinge = [...closed];
        door.closed_pt = [...hinge];
        door.open_pt = [closed[0] + swingDir[0], closed[1] + swingDir[1]];
      } else if (flipType === 'direction') {
        const swingDir = [openPt[0] - hinge[0], openPt[1] - hinge[1]];
        door.open_pt = [hinge[0] - swingDir[0], hinge[1] - swingDir[1]];
      }
      const swing = [door.open_pt[0] - door.hinge[0], door.open_pt[1] - door.hinge[1]];
      const toClosed = [door.closed_pt[0] - door.hinge[0], door.closed_pt[1] - door.hinge[1]];
      const facingCross = swing[0] * toClosed[1] - swing[1] * toClosed[0];
      const hingeSide = facingCross < 0 ? 'links' : 'rechts';
      door.din_anschlag = `DIN ${hingeSide}`;
      door.hinge_side = hingeSide;
      setCustomElements(prev => prev.map(el => el.id === doorId ? door : el));
      setSelectedElements(prev => prev.map(el => el.id === doorId ? { ...el, ...door } : el));
      setHasUnsavedEdits(true);
      return;
    }

    const svgData = { ...result.svg_data };
    const openings = [...(svgData.openings || [])];
    const rooms = svgData.rooms || [];

    const door = openings.find(o => o.id === doorId);
    if (!door) return;

    // Ensure door geometry exists (ex-windows may only have from_pt/to_pt)
    if (!door.hinge) door.hinge = door.from_pt || [0, 0];
    if (!door.closed_pt) door.closed_pt = door.to_pt || [0, 0];
    if (!door.open_pt) {
      // Compute open_pt perpendicular to the wall
      const dx = door.closed_pt[0] - door.hinge[0];
      const dy = door.closed_pt[1] - door.hinge[1];
      door.open_pt = [door.hinge[0] - dy, door.hinge[1] + dx];
    }

    const hinge = door.hinge;
    const closed = door.closed_pt;
    const openPt = door.open_pt;

    if (flipType === 'hinge') {
      // Swap hinge and closed, recalculate open
      const swingDir = [openPt[0] - hinge[0], openPt[1] - hinge[1]];
      door.hinge = [...closed];
      door.closed_pt = [...hinge];
      door.open_pt = [closed[0] + swingDir[0], closed[1] + swingDir[1]];
    } else if (flipType === 'direction') {
      // Mirror open_pt about the wall line
      const swingDir = [openPt[0] - hinge[0], openPt[1] - hinge[1]];
      door.open_pt = [hinge[0] - swingDir[0], hinge[1] - swingDir[1]];
    }

    // Recompute swing analysis
    const swing = [door.open_pt[0] - door.hinge[0], door.open_pt[1] - door.hinge[1]];
    const toClosed = [door.closed_pt[0] - door.hinge[0], door.closed_pt[1] - door.hinge[1]];
    const facingCross = swing[0] * toClosed[1] - swing[1] * toClosed[0];
    const hingeSide = facingCross < 0 ? 'links' : 'rechts';
    door.din_anschlag = `DIN ${hingeSide}`;
    door.hinge_side = hingeSide;

    // Find swing room
    const op = door.open_pt;
    let swingRoom = null;
    for (const r of rooms) {
      const poly = r.polygon || [];
      if (poly.length < 3) continue;
      let inside = false;
      for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const [xi, yi] = poly[i]; const [xj, yj] = poly[j];
        if (((yi > op[1]) !== (yj > op[1])) && (op[0] < (xj - xi) * (op[1] - yi) / (yj - yi) + xi))
          inside = !inside;
      }
      if (inside) { swingRoom = r.name || r.letter || '?'; break; }
    }
    door.swing_room = swingRoom;
    const connects = door.connects || [];
    door.opening_direction = swingRoom && connects.length ? (swingRoom === connects[0] ? 'innen' : 'außen') : null;

    svgData.openings = openings;

    const analysisId = result.id || result.analysis_id;
    if (analysisId) {
      await axios.patch(`${API_URL}/api/analyses/${analysisId}/svg-data`, { svg_data: svgData });
    }
    setResult(prev => ({ ...prev, svg_data: svgData }));
    setSelectedElements(prev => prev.map(el =>
      el.id === doorId ? { ...el, ...door } : el
    ));
  }, [result, customElements]);


  const handleSaveMultiOpeningDims = useCallback(async (openingIds, width_m, height_m) => {
    if (!result?.svg_data || !openingIds.length) return;
    const svgData = { ...result.svg_data };
    const openings = [...(svgData.openings || [])];

    for (const op of openings) {
      if (openingIds.includes(op.id)) {
        if (width_m) {
          op.width_m_matched = width_m;
          op.width_m = width_m;
          op.width_source = 'manual';
        }
        if (height_m) {
          op.height_m_matched = height_m;
          op.height_source = 'manual';
        }
      }
    }
    svgData.openings = openings;

    const analysisId = result.id || result.analysis_id;
    if (analysisId) {
      await axios.patch(`${API_URL}/api/analyses/${analysisId}/svg-data`, { svg_data: svgData });
    }
    setResult(prev => ({ ...prev, svg_data: svgData }));
    setSelectedElements(prev => prev.map(el => {
      if (!openingIds.includes(el.id)) return el;
      const updates = {};
      if (width_m) { updates.width_m_matched = width_m; updates.width_m = width_m; updates.width_source = 'manual'; }
      if (height_m) { updates.height_m_matched = height_m; updates.height_source = 'manual'; }
      return { ...el, ...updates };
    }));
  }, [result]);


  const finishDrawingRef = useRef(null);

  const finishDrawing = useCallback(() => {
    // Opening drawing: 2 points for window/door
    if (drawingMode === 'window' || drawingMode === 'door') {
      if (drawingPoints.length < 2) {
        setDrawingMode(null);
        setDrawingPoints([]);
        return;
      }
      pushUndo();
      const id = `custom_${Date.now()}`;
      const p1 = drawingPoints[0];
      const p2 = drawingPoints[1];
      let newElement;
      if (drawingMode === 'window') {
        newElement = {
          id, type: 'window',
          name: 'Fenster (manuell)',
          label: 'Fenster (manuell)',
          from_pt: p1, to_pt: p2,
          position: [(p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2],
          color: '#22c55e',
          custom: true,
        };
      } else {
        const dx = p2[0] - p1[0];
        const dy = p2[1] - p1[1];
        const openPt = [p1[0] - dy, p1[1] + dx];
        // Default DIN-Anschlag berechnen (gleiche Logik wie handleFlipDoorGeometry)
        const swing = [openPt[0] - p1[0], openPt[1] - p1[1]];
        const toClosed = [p2[0] - p1[0], p2[1] - p1[1]];
        const facingCross = swing[0] * toClosed[1] - swing[1] * toClosed[0];
        const hingeSide = facingCross < 0 ? 'links' : 'rechts';
        newElement = {
          id, type: 'door',
          name: 'Tür (manuell)',
          label: 'Tür (manuell)',
          hinge: p1, closed_pt: p2,
          open_pt: openPt,
          position: [(p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2],
          color: '#f59e0b',
          custom: true,
          din_anschlag: `DIN ${hingeSide}`,
          hinge_side: hingeSide,
          swing_room: 'Außen',
          opening_direction: null,
        };
      }
      setCustomElements(prev => [...prev, newElement]);
      setDrawingMode(null);
      setDrawingPoints([]);
      setHasUnsavedEdits(true);
      // Open edit modal so user can enter dimensions
      setTimeout(() => setEditingElement(newElement), 100);
      return;
    }

    // Polygon drawing: 3+ points for wall/room
    if (drawingPoints.length < 3) {
      setDrawingMode(null);
      setDrawingPoints([]);
      return;
    }
    pushUndo();
    const id = `custom_${Date.now()}`;
    const cx = drawingPoints.reduce((s, p) => s + p[0], 0) / drawingPoints.length;
    const cy = drawingPoints.reduce((s, p) => s + p[1], 0) / drawingPoints.length;
    const newElement = {
      id,
      name: drawingMode === 'wall' ? `Wand (manuell)` : `Raum (manuell)`,
      type: drawingMode === 'wall' ? 'wall' : 'room',
      type_de: drawingMode === 'wall' ? 'Wand' : 'Raum',
      polygon: drawingPoints,
      centroid: [cx, cy],
      color: drawingMode === 'wall' ? '#3b82f6' : '#8b5cf6',
      custom: true,
    };
    setCustomElements(prev => [...prev, newElement]);
    setDrawingMode(null);
    setDrawingPoints([]);
    setHasUnsavedEdits(true);
  }, [drawingPoints, drawingMode, pushUndo]);

  // Keep ref updated for use in handleDrawingClick
  finishDrawingRef.current = finishDrawing;

  const handleDrawingClick = useCallback((pctCoords) => {
    setDrawingPoints(prev => {
      const next = [...prev, pctCoords];
      // Auto-finish openings after 2 points
      if ((drawingMode === 'window' || drawingMode === 'door') && next.length >= 2) {
        setTimeout(() => finishDrawingRef.current?.(), 0);
      }
      return next;
    });
  }, [drawingMode]);

  const deleteCustomElement = useCallback((elementId) => {
    setCustomElements(prev => prev.filter(el => el.id !== elementId));
    setLocalSvgOverrides(prev => {
      const next = { ...prev };
      delete next[elementId];
      return next;
    });
    setSelectedElements(prev => prev.filter(el => el.id !== elementId));
    setHasUnsavedEdits(true);
  }, []);

  const saveEdits = useCallback(async () => {
    const analysisId = result?.id || result?.analysis_id;
    if (!analysisId) return;
    const baseData = { ...result.svg_data };
    // Apply polygon overrides to rooms
    const imgSize = result?.image_size || {};
    const cal = result?.svg_data?.calibration || {};
    if (baseData.rooms) {
      baseData.rooms = baseData.rooms.map(room => {
        const override = localSvgOverrides[room.id];
        if (!override) return room;
        const cx = override.reduce((s, p) => s + p[0], 0) / override.length;
        const cy = override.reduce((s, p) => s + p[1], 0) / override.length;
        // Dynamically recalculate area from new polygon
        const newArea = computePolygonAreaM2(override, imgSize.width, imgSize.height, cal.ppm_x, cal.ppm_y);
        return { ...room, polygon: override, centroid: [cx, cy],
          ...(newArea != null ? { area_m2_override: newArea, area_m2: newArea } : {}) };
      });
    }
    // Apply overrides to walls (recalculate length/thickness from new polygon)
    if (baseData.wall_lines) {
      const ppmX = cal.ppm_x || cal.scale_x_ppm || 0;
      const ppmY = cal.ppm_y || cal.scale_y_ppm || 0;
      baseData.wall_lines = baseData.wall_lines.map((wall, i) => {
        const wallId = wall.id || `wall_${i}`;
        const override = localSvgOverrides[wallId];
        if (!override) return wall;
        const updated = { ...wall, polygon: override };
        // Recalculate length/thickness for 4-point rectangles
        if (override.length === 4 && (ppmX > 0 || ppmY > 0)) {
          const edgeLengths = [];
          for (let ei = 0; ei < 4; ei++) {
            const ej = (ei + 1) % 4;
            const dx = (override[ej][0] - override[ei][0]) / (ppmX || 1);
            const dy = (override[ej][1] - override[ei][1]) / (ppmY || 1);
            edgeLengths.push(Math.sqrt(dx * dx + dy * dy));
          }
          const pair1 = (edgeLengths[0] + edgeLengths[2]) / 2;
          const pair2 = (edgeLengths[1] + edgeLengths[3]) / 2;
          updated.length_m = parseFloat(Math.max(pair1, pair2).toFixed(3));
          updated.thickness_m = parseFloat(Math.min(pair1, pair2).toFixed(4));
          updated.thickness_m_final = updated.thickness_m;
        }
        return updated;
      });
    }
    // Apply overrides to openings (convert virtual polygon to control points + recalc width)
    if (baseData.openings) {
      baseData.openings = baseData.openings.map(opening => {
        const override = localSvgOverrides[opening.id];
        if (!override) return opening;
        // Virtual polygon override (2-3 points for control points)
        if (override.length <= 3 && (opening.from_pt || opening.hinge)) {
          const updated = { ...opening };
          if (opening.type === 'window' || (opening.from_pt && !opening.hinge)) {
            if (override[0]) updated.from_pt = override[0];
            if (override[1]) updated.to_pt = override[1];
          }
          if (opening.type === 'door' || opening.hinge) {
            if (override[0]) updated.hinge = override[0];
            if (override[1]) updated.closed_pt = override[1];
            if (override[2]) updated.open_pt = override[2];
          }
          if (updated.from_pt && updated.to_pt) {
            updated.position = [(updated.from_pt[0] + updated.to_pt[0]) / 2, (updated.from_pt[1] + updated.to_pt[1]) / 2];
            // Recalculate width from new pixel positions
            const ppmX = cal.ppm_x || cal.scale_x_ppm || 0;
            const ppmY = cal.ppm_y || cal.scale_y_ppm || 0;
            if (ppmX > 0 || ppmY > 0) {
              const dx = (updated.to_pt[0] - updated.from_pt[0]) / (ppmX || 1);
              const dy = (updated.to_pt[1] - updated.from_pt[1]) / (ppmY || 1);
              const newWidthM = Math.sqrt(dx * dx + dy * dy);
              if (newWidthM > 0.01) {
                updated.width_m = parseFloat(newWidthM.toFixed(3));
                updated.width_m_final = updated.width_m;
                updated.width_m_matched = updated.width_m;
                updated.width_source = 'drag';
              }
            }
          } else if (updated.hinge && updated.closed_pt) {
            updated.position = [(updated.hinge[0] + updated.closed_pt[0]) / 2, (updated.hinge[1] + updated.closed_pt[1]) / 2];
            // Recalculate width from hinge to closed_pt for doors
            if (ppmX > 0 || ppmY > 0) {
              const dx = (updated.closed_pt[0] - updated.hinge[0]) / (ppmX || 1);
              const dy = (updated.closed_pt[1] - updated.hinge[1]) / (ppmY || 1);
              const newWidthM = Math.sqrt(dx * dx + dy * dy);
              if (newWidthM > 0.01) {
                updated.width_m = parseFloat(newWidthM.toFixed(3));
                updated.width_m_final = updated.width_m;
                updated.width_m_matched = updated.width_m;
                updated.width_source = 'drag';
              }
            }
          }
          return updated;
        }
        return { ...opening, polygon: override };
      });
    }
    // Append custom elements
    for (const custom of customElements) {
      if (custom.type === 'wall') {
        baseData.wall_lines = [...(baseData.wall_lines || []), custom];
      } else if (custom.type === 'window' || custom.type === 'door') {
        baseData.openings = [...(baseData.openings || []), custom];
      } else {
        baseData.rooms = [...(baseData.rooms || []), custom];
      }
    }
    // Apply envelope override
    if (localSvgOverrides['__envelope__']) {
      baseData.envelope = localSvgOverrides['__envelope__'];
    }
    try {
      await axios.patch(`${API_URL}/api/analyses/${analysisId}/svg-data`, { svg_data: baseData });
      setResult(prev => ({ ...prev, svg_data: baseData }));
      setLocalSvgOverrides({});
      setCustomElements([]);
      setHasUnsavedEdits(false);
      setEditMode(null);
      setUndoStack([]);
    } catch (e) {
      console.error('Save failed:', e);
    }
  }, [result, localSvgOverrides, customElements]);

  const undoAllEdits = useCallback(() => {
    setLocalSvgOverrides({});
    setCustomElements([]);
    setHasUnsavedEdits(false);
    setEditMode(null);
    setUndoStack([]);
  }, []);

  const undo = useCallback(() => {
    if (undoStack.length === 0) return;
    const lastState = undoStack[undoStack.length - 1];
    setLocalSvgOverrides(lastState.overrides);
    setCustomElements(lastState.custom);
    setUndoStack(prev => prev.slice(0, -1));
    if (undoStack.length <= 1) setHasUnsavedEdits(false);
  }, [undoStack]);

  // Ctrl+Z keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo]);

  const toggleSection = useCallback((section) => {
    setCollapsedSections(prev => ({ ...prev, [section]: !prev[section] }));
  }, []);

  // Elements with polygon overrides applied (for live dimension updates)
  const displayElements = useMemo(() => {
    const cal = result?.svg_data?.calibration || {};
    const ppmX = cal.ppm_x || cal.scale_x_ppm || 0;
    const ppmY = cal.ppm_y || cal.scale_y_ppm || 0;

    return selectedElements.map(el => {
      const override = localSvgOverrides[el.id];
      if (!override) return el;
      // Clear ALL cached area/length values so dynamic recalculation kicks in
      if (el.centroid || (el.polygon && el.polygon.length >= 3 && el.type !== 'wall')) {
        return { ...el, polygon: override,
          area_m2_calibrated: null, area_m2: null, area_text: null, area_relative: null,
          perimeter_m_calibrated: null, perimeter_m: null };
      }
      // For walls with 4 vertices: recalculate length and thickness from overridden polygon
      if (el.type === 'wall' && override.length === 4 && (ppmX > 0 || ppmY > 0)) {
        const updated = { ...el, polygon: override };
        // Compute edge lengths in meters
        const edgeLengths = [];
        for (let i = 0; i < 4; i++) {
          const j = (i + 1) % 4;
          const dx = (override[j][0] - override[i][0]) / (ppmX || 1);
          const dy = (override[j][1] - override[i][1]) / (ppmY || 1);
          edgeLengths.push(Math.sqrt(dx * dx + dy * dy));
        }
        // Pair opposite edges: (0,2) and (1,3)
        const pair1 = (edgeLengths[0] + edgeLengths[2]) / 2;
        const pair2 = (edgeLengths[1] + edgeLengths[3]) / 2;
        const dynLength = Math.max(pair1, pair2);
        const dynThickness = Math.min(pair1, pair2);
        updated.length_m = parseFloat(dynLength.toFixed(3));
        updated.length_m_calibrated = updated.length_m;
        updated.thickness_m = parseFloat(dynThickness.toFixed(4));
        updated.thickness_m_final = updated.thickness_m;
        updated.drag_override = true;
        return updated;
      }
      // For openings: recalculate width from overridden control points
      if ((el.type === 'window' || el.type === 'door') && override.length <= 3) {
        const updated = { ...el };
        if (el.type === 'window' || (el.from_pt && !el.hinge)) {
          if (override[0]) updated.from_pt = override[0];
          if (override[1]) updated.to_pt = override[1];
        }
        if (el.type === 'door' || el.hinge) {
          if (override[0]) updated.hinge = override[0];
          if (override[1]) updated.closed_pt = override[1];
          if (override[2]) updated.open_pt = override[2];
        }
        // Recalculate width dynamically from new control points
        let pt1 = null, pt2 = null;
        if (updated.from_pt && updated.to_pt) {
          pt1 = updated.from_pt; pt2 = updated.to_pt;
        } else if (updated.hinge && updated.closed_pt) {
          pt1 = updated.hinge; pt2 = updated.closed_pt;
        }
        if (pt1 && pt2 && (ppmX > 0 || ppmY > 0)) {
          const dx = (pt2[0] - pt1[0]) / (ppmX || 1);
          const dy = (pt2[1] - pt1[1]) / (ppmY || 1);
          const newWidthM = Math.sqrt(dx * dx + dy * dy);
          if (newWidthM > 0.01) {
            const w = parseFloat(newWidthM.toFixed(3));
            updated.width_m = w;
            updated.width_m_matched = w;
            updated.width_m_final = w;
            updated.width_source = 'drag';
          }
        }
        return updated;
      }
      return { ...el, polygon: override };
    });
  }, [selectedElements, localSvgOverrides, result]);


  // ─── PROJECT LIST / DETAIL VIEWS ────────────────────────────
  if (view === 'projects' && !loading) {
    return <ProjectListView onSelectProject={goToProjectDetail} onOpenAnalysis={openAnalysisFromProject} onNavigate={onNavigate} />;
  }

  if (view === 'project-detail' && !loading && currentProjectId) {
    return (
      <ProjectDetailView
        projectId={currentProjectId}
        onBack={goToProjects}
        onOpenAnalysis={openAnalysisFromProject}
        onUploadToFloor={handleUploadToFloor}
      />
    );
  }

  // ─── UPLOAD VIEW (standalone, without project) ───────────────
  if (!result && !loading && view === 'analysis') {
    // If we ended up here without a result, go back to projects
    return <ProjectListView onSelectProject={goToProjectDetail} />;
  }

  // ─── LOADING VIEW ────────────────────────────────────────────
  if (loading && !result) {
    return (
      <div className="min-h-screen bg-zinc-950 flex flex-col items-center justify-center px-6">
        <div className="mb-6">
          <h2 className="font-mono text-xl font-medium text-center mb-2">Grundriss wird analysiert</h2>
          <p className="font-sans text-xs text-zinc-500 text-center">LAIzyAnalyze verarbeitet den Plan – dies kann 30-120 Sekunden dauern</p>
        </div>
        <TerminalLoading startTime={loadingStart} />
        {error && (
          <div data-testid="error-message" className="mt-4 flex items-center gap-2 px-4 py-3 border border-red-500/30 bg-red-500/5 max-w-2xl">
            <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <span className="font-mono text-xs text-red-400">{error}</span>
          </div>
        )}
      </div>
    );
  }

  // ─── RESULTS VIEW ─────────────────────────────────────────────
  const tm = darkMode; // theme shorthand
  return (
    <div className={`h-screen flex flex-col overflow-hidden ${tm ? 'bg-zinc-950 text-zinc-100' : 'bg-white text-zinc-900'}`}>
      {/* Top bar */}
      <header className={`border-b px-4 py-2 flex items-center justify-between flex-shrink-0 ${tm ? 'border-zinc-800 bg-zinc-950' : 'border-zinc-200 bg-zinc-50'}`}>
        <div className="flex items-center gap-3">
          <button data-testid="back-to-project-btn" onClick={() => {
            if (currentProjectId) {
              setResult(null); setError(null); setSelectedElements([]);
              setView('project-detail');
            } else {
              goToProjects();
            }
          }}
            className={`p-1.5 rounded-md transition-colors ${tm ? 'hover:bg-zinc-800' : 'hover:bg-zinc-200'}`} title="Zurück">
            <ArrowLeft className={`w-3.5 h-3.5 ${tm ? 'text-zinc-400' : 'text-zinc-600'}`} />
          </button>
          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
          <span className={`font-mono text-sm font-medium tracking-tight ${tm ? '' : 'text-zinc-800'}`}>LAIzyBlueprint</span>
          <ChevronRight className={`w-3 h-3 ${tm ? 'text-zinc-600' : 'text-zinc-400'}`} />
          <span className={`font-mono text-xs truncate max-w-[200px] ${tm ? 'text-zinc-500' : 'text-zinc-500'}`}>{result?.filename}</span>
        </div>
        <div className="flex items-center gap-2">
          {result?.processing_time && (
            <span className={`font-mono text-[10px] flex items-center gap-1 ${tm ? 'text-zinc-500' : 'text-zinc-500'}`}>
              <Clock className="w-3 h-3" /> {result.processing_time}s
            </span>
          )}
          <button data-testid="new-analysis-btn" onClick={() => {
              if (currentProjectId) {
                setResult(null); setError(null); setSelectedElements([]);
                setView('project-detail');
              } else {
                goToProjects();
              }
            }}
            className="px-3 py-1.5 bg-blue-600 text-white font-mono text-xs hover:bg-blue-500 transition-colors">
            Zurück zum Projekt
          </button>
          <button data-testid="history-btn-results" onClick={() => { setShowHistory(true); fetchAnalyses(); }}
            className={`p-1.5 border transition-colors ${tm ? 'border-zinc-700 hover:border-zinc-500 text-zinc-400 hover:text-white' : 'border-zinc-300 hover:border-zinc-400 text-zinc-500 hover:text-zinc-700'}`}>
            <History className="w-3.5 h-3.5" />
          </button>
        </div>
      </header>

      {/* Main content */}
      <div className={`flex flex-1 overflow-hidden ${tm ? '' : 'bg-zinc-100'}`}>
        {/* Left sidebar - Layers (collapsible) */}
        <aside className={`${layerPanelOpen ? 'w-56' : 'w-10'} border-r flex-shrink-0 flex flex-col transition-all duration-200 ${tm ? 'border-zinc-800 bg-zinc-900/50' : 'border-zinc-200 bg-white'}`}>
          <button onClick={() => setLayerPanelOpen(p => !p)}
            className={`px-3 py-2 border-b flex items-center gap-1.5 transition-colors ${tm ? 'border-zinc-800 hover:bg-zinc-800' : 'border-zinc-200 hover:bg-zinc-100'}`}
            data-testid="toggle-layer-panel">
            <Layers className={`w-3 h-3 ${tm ? 'text-zinc-500' : 'text-zinc-500'}`} />
            {layerPanelOpen && <span className={`font-mono text-[10px] uppercase tracking-wider flex-1 ${tm ? 'text-zinc-500' : 'text-zinc-600'}`}>Layer & Werkzeuge</span>}
            <ChevronLeft className={`w-3 h-3 transition-transform ${!layerPanelOpen ? 'rotate-180' : ''} ${tm ? 'text-zinc-500' : 'text-zinc-400'}`} />
          </button>
          {layerPanelOpen && (
          <div className="flex-1 overflow-y-auto">
            {/* Dark/Light mode toggle */}
            <div className={`px-3 py-2 border-b flex items-center justify-between ${tm ? 'border-zinc-800' : 'border-zinc-200'}`}>
              <span className={`font-mono text-[9px] uppercase ${tm ? 'text-zinc-500' : 'text-zinc-500'}`}>Ansicht</span>
              <button onClick={() => { setDarkMode(p => { localStorage.setItem('editorTheme', !p ? 'dark' : 'light'); return !p; }); }}
                data-testid="toggle-dark-mode"
                className={`px-2 py-1 rounded text-[10px] font-mono transition-colors ${tm ? 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700' : 'bg-zinc-200 text-zinc-700 hover:bg-zinc-300'}`}>
                {tm ? 'Hell' : 'Dunkel'}
              </button>
            </div>

            {/* Checklist - above scale */}
            {!editMode && !drawingMode && result && (
              <ChecklistSidebarPanel result={result} />
            )}
            {/* Scale calibration */}
            <ScaleCalibration
              calibrationMode={calibrationMode}
              calibrationPoints={calibrationPoints}
              scale={scale}
              pixelDist={pixelDist}
              onStartCalibration={startCalibration}
              onCancelCalibration={cancelCalibration}
              onSetDistance={handleSetDistance}
              onResetScale={resetScale}
              scaleInfo={scaleInfo}
              ppmX={ppmX}
              ppmY={ppmY}
            />

            {/* Layer toggles - in a collapsible dropdown */}
            <div className={`border-t ${tm ? 'border-zinc-800' : 'border-zinc-200'}`}>
              <button onClick={() => setCollapsedSections(p => ({...p, layerToggles: !p.layerToggles}))}
                className={`w-full px-3 py-1.5 flex items-center justify-between ${tm ? 'hover:bg-zinc-800/50' : 'hover:bg-zinc-100'}`}>
                <span className={`font-mono text-[9px] uppercase ${tm ? 'text-zinc-500' : 'text-zinc-500'}`}>Layer ein/aus</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${collapsedSections.layerToggles ? '' : 'rotate-180'} ${tm ? 'text-zinc-500' : 'text-zinc-400'}`} />
              </button>
              {!collapsedSections.layerToggles && (
                <div>
                  <LayerToggle label="Raum-Polygone" color="#3b82f6" active={activeLayers.roomPolygons} onToggle={() => toggleLayer('roomPolygons')} testId="toggle-room-polygons" />
                  <LayerToggle label="Wand-Polygone" color="#334155" active={activeLayers.wallPolygons} onToggle={() => toggleLayer('wallPolygons')} testId="toggle-wall-polygons" />
                  <LayerToggle label="Wand-Linien" color="#475569" active={activeLayers.wallLines} onToggle={() => toggleLayer('wallLines')} testId="toggle-wall-lines" />
                  <LayerToggle label="Türen/Fenster" color="#22c55e" active={activeLayers.openings} onToggle={() => toggleLayer('openings')} testId="toggle-openings" />
                  <LayerToggle label="Bemaßungen" color="#94a3b8" active={activeLayers.dimensions} onToggle={() => toggleLayer('dimensions')} testId="toggle-dimensions" />
                  {svgData?.dimension_grid && (
                    <LayerToggle label="Bemaßungsgitter" color="#60a5fa" active={activeLayers.dimensionGrid} onToggle={() => toggleLayer('dimensionGrid')} testId="toggle-dimension-grid" />
                  )}
                  {svgData?.dimension_grid_gpt && (
                    <LayerToggle label="PDF-Vektor-Gitter" color="#a78bfa" active={activeLayers.dimensionGridGpt} onToggle={() => toggleLayer('dimensionGridGpt')} testId="toggle-dimension-grid-gpt" />
                  )}
                  {svgData?.wall_lines?.some(w => w.wall_type) && (
                    <LayerToggle label="Wandtypen" color="#ef4444" active={activeLayers.wallTypes} onToggle={() => toggleLayer('wallTypes')} testId="toggle-wall-types" />
                  )}
                  {svgData?.envelope && (
                    <LayerToggle label="Envelope" color="#fbbf24" active={activeLayers.envelope} onToggle={() => toggleLayer('envelope')} testId="toggle-envelope" />
                  )}
                  {result?.layers && (
                    <>
                      <div className={`px-3 py-1 mt-1 border-t ${tm ? 'border-zinc-800' : 'border-zinc-200'}`}><span className={`font-mono text-[9px] uppercase ${tm ? 'text-zinc-600' : 'text-zinc-400'}`}>Pixel-Masken</span></div>
                      <LayerToggle label="Wände (Pixel)" color="#a1a1aa" active={activeLayers.walls} onToggle={() => toggleLayer('walls')} testId="toggle-walls" />
                      <LayerToggle label="Räume (Pixel)" color="#10b981" active={activeLayers.rooms} onToggle={() => toggleLayer('rooms')} testId="toggle-rooms" />
                      <LayerToggle label="Fenster (Pixel)" color="#3b82f6" active={activeLayers.windows} onToggle={() => toggleLayer('windows')} testId="toggle-windows" />
                      <LayerToggle label="Türen (Pixel)" color="#f59e0b" active={activeLayers.doors} onToggle={() => toggleLayer('doors')} testId="toggle-doors" />
                      <LayerToggle label="Einrichtung" color="#8b5cf6" active={activeLayers.fixtures} onToggle={() => toggleLayer('fixtures')} testId="toggle-fixtures" />
                    </>
                  )}
                </div>
              )}
            </div>

            {/* KI-Validierung (collapsible) */}
            {svgData?.dimension_grid && (
              <div className={`border-t ${tm ? 'border-zinc-800' : 'border-zinc-200'}`}>
                <button onClick={() => setValidationPanelOpen(p => !p)}
                  className={`w-full px-3 py-1.5 flex items-center justify-between text-left ${tm ? 'hover:bg-zinc-800' : 'hover:bg-zinc-100'}`}>
                  <span className={`font-mono text-[9px] uppercase ${tm ? 'text-zinc-500' : 'text-zinc-500'}`}>KI-Validierung</span>
                  <ChevronDown className={`w-3 h-3 transition-transform ${validationPanelOpen ? 'rotate-180' : ''} ${tm ? 'text-zinc-500' : 'text-zinc-400'}`} />
                </button>
                {validationPanelOpen && (
                  <div className="px-3 py-1 space-y-1">
                    <button data-testid="extract-grid-gpt-btn"
                      onClick={async () => {
                        const aid = result?.id || result?.analysis_id;
                        if (!aid) return;
                        setGptGridLoading(true);
                        try {
                          const resp = await axios.post(`${API_URL}/api/analyses/${aid}/extract-grid-gpt`);
                          if (resp.data?.dimension_grid_gpt) {
                            setResult(prev => ({...prev, svg_data: {...prev.svg_data, dimension_grid_gpt: resp.data.dimension_grid_gpt}}));
                            setActiveLayers(prev => ({...prev, dimensionGridGpt: true, dimensionGrid: false}));
                          }
                        } catch(e) { console.error('Grid extraction failed:', e); }
                        setGptGridLoading(false);
                      }}
                      disabled={gptGridLoading}
                      className="w-full text-[10px] font-mono px-2 py-1 rounded bg-violet-900/40 text-violet-300 hover:bg-violet-800/50 border border-violet-700/30 transition-colors disabled:opacity-50">
                      {gptGridLoading ? 'Wird analysiert...' : svgData?.dimension_grid_gpt ? 'Gitter neu berechnen' : 'Präzises Gitter (PDF-Vektor)'}
                    </button>
                    {svgData?.dimension_grid_gpt && (
                      <button
                        data-testid="validate-grid-btn"
                        onClick={async () => {
                          setGptGridLoading(true);
                          try {
                            const resp = await axios.post(`${API_URL}/api/analyses/${result.analysis_id}/validate-grid`, {}, {withCredentials: true});
                            if (resp.data?.status === 'ok') {
                              const s = resp.data.stats;
                              modal.alert(`KI-Validierung abgeschlossen:\n${s.x_corrected} vertikale + ${s.y_corrected} horizontale korrigiert\n${s.x_removed || 0} vertikale + ${s.y_removed || 0} horizontale entfernt\n${s.x_missing_added} vertikale + ${s.y_missing_added} horizontale hinzugefügt\nGesamt: ${s.total_vertical} V + ${s.total_horizontal} H`, { title: 'KI-Validierung', type: 'success' });
                              const fresh = await axios.get(`${API_URL}/api/analyses/${result.analysis_id}`, {withCredentials: true});
                              if (fresh.data) setResult(fresh.data);
                            }
                          } catch(e) { console.error('Grid validation failed:', e); modal.alert('Validierung fehlgeschlagen: ' + (e.response?.data?.detail || e.message), { title: 'Fehler', type: 'warning' }); }
                          setGptGridLoading(false);
                        }}
                        disabled={gptGridLoading}
                        className="w-full text-[10px] font-mono px-2 py-1 rounded bg-amber-900/40 text-amber-300 hover:bg-amber-800/50 border border-amber-700/30 transition-colors disabled:opacity-50">
                        {gptGridLoading ? 'Validiert...' : `KI-Validierung (${svgData.dimension_grid_gpt.gpt_validated ? 'erledigt' : 'offen'})`}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          )}

          {/* Summary Dropdown */}
          {layerPanelOpen && filteredSvgData && (
            <SummaryDropdown result={result} filteredSvgData={filteredSvgData} scale={scale} scaleInfo={scaleInfo} totalAreaM2={totalAreaM2} totalWallLengthM={totalWallLengthM} />
          )}
        </aside>

        {/* Center viewport */}
        <div className="flex-1 relative flex flex-col">
          {showElementLists ? (
            <ElementLists
              analysisId={result?.analysis_id}
              onHighlight={(ids) => {
                setShowElementLists(false);
                if (ids?.length) {
                  const allElements = [
                    ...(filteredSvgData?.wall_lines || []),
                    ...(filteredSvgData?.openings || []),
                  ];
                  const matches = allElements.filter(el => ids.includes(el.id));
                  setSelectedElements(matches);
                }
              }}
              onClose={() => setShowElementLists(false)}
            />
          ) : (
          <SVGViewport
            result={{...result, svg_data: filteredSvgData}}
            activeLayers={activeLayers}
            selectedElements={selectedElements}
            onSelectElement={handleElementSelect}
            calibrationMode={calibrationMode}
            calibrationPoints={calibrationPoints}
            onCalibrationClick={handleCalibrationClick}
            scale={scale}
            scaleInfo={scaleInfo}
            ppmX={ppmX}
            ppmY={ppmY}
            darkMode={darkMode}
            editMode={editMode}
            onVertexMove={handleVertexMove}
            onEdgeMove={handleEdgeMove}
            onPolygonMove={handlePolygonMove}
            onAddVertex={handleAddVertex}
            onDeleteVertex={handleDeleteVertex}
            onDragStart={handleDragStart}
            localSvgOverrides={localSvgOverrides}
            drawingMode={drawingMode}
            drawingPoints={drawingPoints}
            onDrawingClick={handleDrawingClick}
            showElementLists={showElementLists}
            onToggleElementLists={() => setShowElementLists(v => !v)}
          />
          )}
        </div>

        {/* Right panel - Properties */}
        <aside className={`w-64 border-l flex-shrink-0 flex flex-col overflow-y-auto ${tm ? 'border-zinc-800 bg-zinc-900/50' : 'border-zinc-200 bg-white'}`}>
          <div className={`px-3 py-2 border-b ${tm ? 'border-zinc-800' : 'border-zinc-200'}`}>
            <span className={`font-mono text-[10px] uppercase tracking-wider flex items-center gap-1.5 ${tm ? 'text-zinc-500' : 'text-zinc-500'}`}>
              <CheckCircle className="w-3 h-3" /> Eigenschaften
            </span>
          </div>

          {/* Quick select buttons */}
          {filteredSvgData && (
            <div className={`px-3 py-2 border-b flex flex-wrap gap-1.5 ${tm ? 'border-zinc-800' : 'border-zinc-200'}`} data-testid="quick-select-bar">
              <button data-testid="select-all-rooms-btn" onClick={selectAllRooms}
                className={`px-2 py-1 border font-mono text-[10px] transition-colors ${tm ? 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500' : 'bg-zinc-50 border-zinc-200 text-zinc-600 hover:text-zinc-900 hover:border-zinc-400 hover:bg-zinc-100'}`}>
                Alle Räume
              </button>
              <button data-testid="select-all-walls-btn" onClick={selectAllWalls}
                className={`px-2 py-1 border font-mono text-[10px] transition-colors ${tm ? 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500' : 'bg-zinc-50 border-zinc-200 text-zinc-600 hover:text-zinc-900 hover:border-zinc-400 hover:bg-zinc-100'}`}>
                Alle Wände
              </button>
              <button data-testid="open-lv-editor-btn"
                onClick={() => { setLvEditorElementIds([]); setLvEditorOpen(true); }}
                className="px-2 py-1 bg-emerald-500/10 border border-emerald-500/30 font-mono text-[10px] text-emerald-400 hover:bg-emerald-500/20 transition-colors">
                LV-Editor öffnen
              </button>
              {selectedElements.length > 0 && (
                <>
                <button data-testid="clear-selection-btn" onClick={clearSelection}
                  className="px-2 py-1 bg-red-500/10 border border-red-500/30 font-mono text-[10px] text-red-400 hover:bg-red-500/20 transition-colors">
                  Auswahl aufheben ({selectedElements.length})
                </button>
                <button data-testid="export-selection-lv-btn"
                  onClick={() => {
                    const ids = selectedElements.map(el => el.id).join(',');
                    window.open(`${API_URL}/api/analyses/${result?.analysis_id}/export-langtext-lv?element_ids=${ids}`, '_blank');
                  }}
                  className="px-2 py-1 bg-blue-500/10 border border-blue-500/30 font-mono text-[10px] text-blue-400 hover:bg-blue-500/20 transition-colors">
                  Auswahl → Langtext-LV (direkt)
                </button>
                <button data-testid="open-lv-editor-selection-btn"
                  onClick={() => {
                    setLvEditorElementIds(selectedElements.map(el => el.id));
                    setLvEditorOpen(true);
                  }}
                  className="px-2 py-1 bg-emerald-500/10 border border-emerald-500/30 font-mono text-[10px] text-emerald-400 hover:bg-emerald-500/20 transition-colors">
                  Auswahl → LV-Editor
                </button>
                </>
              )}
            </div>
          )}

          {/* Edit toolbar (collapsible Werkzeugkasten) */}
          {filteredSvgData && (() => {
            const autoOpen = !!(editMode || drawingMode || hasUnsavedEdits || (selectedElements && selectedElements.length > 0));
            const isOpen = toolboxOpen || autoOpen;
            return (
              <div className="border-b border-zinc-800" data-testid="edit-toolbar-container">
                {/* Toggle-Header */}
                <button
                  onClick={() => setToolboxOpen(o => !o)}
                  data-testid="toolbox-toggle-btn"
                  disabled={autoOpen}
                  className={`w-full flex items-center justify-between px-3 py-2 transition-colors ${
                    isOpen ? 'bg-zinc-800/40' : 'bg-zinc-900/30 hover:bg-zinc-800/30'
                  } ${autoOpen ? 'cursor-default' : 'cursor-pointer'}`}
                  title={autoOpen ? 'Werkzeuge sind aktiv (Auswahl/Bearbeitung)' : 'Werkzeugkasten ein-/ausklappen'}
                >
                  <span className="flex items-center gap-1.5 font-mono text-[10px] text-zinc-400 uppercase tracking-wider">
                    <PenTool className="w-3 h-3" />
                    Werkzeuge
                    {autoOpen && (
                      <span className="ml-1 px-1.5 py-0.5 bg-amber-500/15 border border-amber-500/30 text-amber-300 text-[8px] rounded">
                        {editMode ? 'Bearbeiten' : drawingMode ? 'Zeichnen' : hasUnsavedEdits ? 'Ungespeichert' : 'Auswahl'}
                      </span>
                    )}
                  </span>
                  <ChevronDown className={`w-3.5 h-3.5 text-zinc-500 transition-transform ${isOpen ? 'rotate-0' : '-rotate-90'}`} />
                </button>

                {/* Werkzeug-Inhalt */}
                {isOpen && (
                  <div className="px-3 py-2" data-testid="edit-toolbar">
                    <div className="flex flex-wrap gap-1.5">
                      {/* Edit selected element */}
                      {selectedElements.length === 1 && !editMode && (selectedElements[0].polygon || selectedElements[0].from_pt || selectedElements[0].hinge) && (
                        <button data-testid="start-edit-btn" onClick={() => startEditMode(selectedElements[0])}
                          className="px-2 py-1 bg-amber-500/10 border border-amber-500/30 font-mono text-[10px] text-amber-400 hover:bg-amber-500/20 transition-colors flex items-center gap-1">
                          <Pencil className="w-3 h-3" /> Bearbeiten
                        </button>
                      )}
                      {editMode && (
                        <button data-testid="stop-edit-btn" onClick={stopEditMode}
                          className="px-2 py-1 bg-amber-500/10 border border-amber-500/30 font-mono text-[10px] text-amber-400 hover:bg-amber-500/20 transition-colors flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> Fertig
                        </button>
                      )}
                      {/* Drawing buttons */}
                      {!editMode && !drawingMode && (
                        <>
                          <button data-testid="draw-room-btn" onClick={() => { setDrawingMode('room'); setDrawingPoints([]); setEditMode(null); }}
                            className="px-2 py-1 bg-purple-500/10 border border-purple-500/30 font-mono text-[10px] text-purple-400 hover:bg-purple-500/20 transition-colors flex items-center gap-1">
                            <PenTool className="w-3 h-3" /> Raum zeichnen
                          </button>
                          <button data-testid="draw-wall-btn" onClick={() => { setDrawingMode('wall'); setDrawingPoints([]); setEditMode(null); }}
                            className="px-2 py-1 bg-blue-500/10 border border-blue-500/30 font-mono text-[10px] text-blue-400 hover:bg-blue-500/20 transition-colors flex items-center gap-1">
                            <PenTool className="w-3 h-3" /> Wand zeichnen
                          </button>
                          <button data-testid="draw-window-btn" onClick={() => { setDrawingMode('window'); setDrawingPoints([]); setEditMode(null); }}
                            className="px-2 py-1 bg-green-500/10 border border-green-500/30 font-mono text-[10px] text-green-400 hover:bg-green-500/20 transition-colors flex items-center gap-1">
                            <PenTool className="w-3 h-3" /> Fenster
                          </button>
                          <button data-testid="draw-door-btn" onClick={() => { setDrawingMode('door'); setDrawingPoints([]); setEditMode(null); }}
                            className="px-2 py-1 bg-amber-500/10 border border-amber-500/30 font-mono text-[10px] text-amber-400 hover:bg-amber-500/20 transition-colors flex items-center gap-1">
                            <PenTool className="w-3 h-3" /> Tür
                          </button>
                        </>
                      )}
                      {drawingMode && (
                        <>
                          {(drawingMode === 'wall' || drawingMode === 'room') && (
                            <button data-testid="finish-drawing-btn" onClick={finishDrawing}
                              disabled={drawingPoints.length < 3}
                              className="px-2 py-1 bg-green-500/10 border border-green-500/30 font-mono text-[10px] text-green-400 hover:bg-green-500/20 transition-colors flex items-center gap-1 disabled:opacity-30">
                              <CheckCircle className="w-3 h-3" /> Abschließen ({drawingPoints.length})
                            </button>
                          )}
                          {(drawingMode === 'window' || drawingMode === 'door') && (
                            <span className="font-mono text-[10px] text-zinc-400">
                              2 Punkte klicken ({drawingPoints.length}/2)
                            </span>
                          )}
                          <button data-testid="cancel-drawing-btn" onClick={() => { setDrawingMode(null); setDrawingPoints([]); }}
                            className="px-2 py-1 bg-red-500/10 border border-red-500/30 font-mono text-[10px] text-red-400 hover:bg-red-500/20 transition-colors flex items-center gap-1">
                            <X className="w-3 h-3" /> Abbrechen
                          </button>
                        </>
                      )}
                    </div>

                    {/* Save / undo bar */}
                    {hasUnsavedEdits && (
                      <div className="flex gap-1.5 mt-2 pt-2 border-t border-zinc-800/50">
                        <button data-testid="save-edits-btn" onClick={saveEdits}
                          className="flex-1 px-2 py-1.5 bg-green-500/20 border border-green-500/40 font-mono text-[10px] text-green-400 hover:bg-green-500/30 transition-colors flex items-center justify-center gap-1">
                          <Save className="w-3 h-3" /> Speichern
                        </button>
                        {undoStack.length > 0 && (
                          <button data-testid="undo-step-btn" onClick={undo}
                            className="px-2 py-1.5 bg-amber-500/10 border border-amber-500/30 font-mono text-[10px] text-amber-400 hover:bg-amber-500/20 transition-colors flex items-center gap-1"
                            title="Rückgängig (Ctrl+Z)">
                            <RotateCcw className="w-3 h-3" /> {undoStack.length}
                          </button>
                        )}
                        <button data-testid="undo-edits-btn" onClick={undoAllEdits}
                          className="px-2 py-1.5 bg-zinc-800 border border-zinc-700 font-mono text-[10px] text-zinc-400 hover:text-white transition-colors flex items-center gap-1">
                          <X className="w-3 h-3" /> Alles
                        </button>
                      </div>
                    )}

                    {/* Edit hints */}
                    {editMode && (
                      <p className="font-mono text-[9px] text-zinc-600 mt-2">
                        Ziehen = verschieben | Rechtsklick = Punkt löschen | Grün = hinzufügen | Ctrl+Z = rückgängig
                      </p>
                    )}
                    {drawingMode && (
                      <p className="font-mono text-[9px] text-zinc-600 mt-2">
                        Klicken = Punkt setzen | Ab 3 Punkten abschließbar
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Checklist - moved to SVGViewport toolbar */}

          {/* Roof Geometry (nur Dachgeschoss) */}
          {result && !editMode && !drawingMode && (result?.floor_level || 0) >= 2 && (
            <RoofGeometryPanel analysisId={result?.analysis_id || result?.id} floorLevel={result?.floor_level || 0} />
          )}

          {/* Apply section data button */}
          {result?.project_id && !editMode && !drawingMode && (
            <ApplySectionButton analysisId={result.analysis_id || result.id} onApplied={() => {
              const aid = result.analysis_id || result.id;
              if (aid) axios.get(`${API_URL}/api/analyses/${aid}`).then(r => setResult(r.data));
            }} />
          )}

          {/* GPT Validation Status */}
          <ValidationStatusBadge analysisId={result?.analysis_id || result?.id} onCompleted={() => {
            const aid = result?.analysis_id || result?.id;
            if (aid) axios.get(`${API_URL}/api/analyses/${aid}`).then(r => setResult(r.data));
          }} />


          {/* Selected element detail */}
          <ElementDetail elements={displayElements} onEditElement={handleEditElement} onDeleteElement={handleDeleteElement} onSaveMultiThickness={handleSaveMultiThickness} onSaveMultiHeight={handleSaveMultiHeight} onSaveMultiOpeningDims={handleSaveMultiOpeningDims} onFlipDoorGeometry={handleFlipDoorGeometry} result={result} localSvgOverrides={localSvgOverrides} />

          {/* Rooms list */}
          {filteredSvgData?.rooms?.length > 0 && (
            <div className="border-b border-zinc-800">
              <button onClick={() => toggleSection('rooms')} data-testid="toggle-rooms-section"
                className="w-full px-3 py-2 bg-zinc-800/30 flex items-center justify-between hover:bg-zinc-800/50 transition-colors">
                <span className="font-mono text-[10px] text-zinc-500 uppercase tracking-wider">
                  Räume ({filteredSvgData.rooms.length})
                </span>
                {collapsedSections.rooms
                  ? <ChevronRight className="w-3.5 h-3.5 text-zinc-500" />
                  : <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />}
              </button>
              {!collapsedSections.rooms && filteredSvgData.rooms.map((room) => {
                const isSelected = selectedElements.some(el => el.id === room.id);
                const areaDisplay = fmtAreaValue(room);
                const isCalibrated = scale && room.polygon?.length >= 3;
                const hasApiScale = !scale && scaleInfo && room.polygon?.length >= 3;
                return (
                  <button key={room.id} data-testid={`room-list-${room.id}`}
                    onClick={(e) => handleElementSelect(room, e)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 border-b border-zinc-800/30 transition-colors text-left
                      ${isSelected ? 'bg-blue-500/10 border-l-2 border-l-blue-500' : 'hover:bg-zinc-800/50'}`}>
                    <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: room.color }}></div>
                    <div className="flex-1 min-w-0">
                      <span className="font-mono text-xs text-white block font-medium">Raum {room.letter}</span>
                      <span className="font-mono text-[10px] text-zinc-500">{translateRoom(room.name)}</span>
                    </div>
                    <span className={`font-mono text-xs font-bold ${isCalibrated || hasApiScale || room.area_m2 ? 'text-green-400' : 'text-blue-400'}`}>
                      {areaDisplay}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Wall lines list */}
          {filteredSvgData?.wall_lines?.length > 0 && (
            <div className="border-b border-zinc-800">
              <button onClick={() => toggleSection('walls')} data-testid="toggle-walls-section"
                className="w-full px-3 py-2 bg-zinc-800/30 flex items-center justify-between hover:bg-zinc-800/50 transition-colors">
                <span className="font-mono text-[10px] text-zinc-500 uppercase tracking-wider">
                  Wände ({filteredSvgData.wall_lines.length})
                </span>
                {collapsedSections.walls
                  ? <ChevronRight className="w-3.5 h-3.5 text-zinc-500" />
                  : <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />}
              </button>
              {!collapsedSections.walls && filteredSvgData.wall_lines.map((line, i) => {
                const wallId = line.id || `wall_${i}`;
                const isSelected = selectedElements.some(el => el.id === wallId);
                const wallName = line.name || `Wand ${i + 1}`;
                const lengthDisplay = fmtLengthValue(line);
                const isCalibrated = scale && line.polygon?.length >= 4;
                const hasApiScale = !scale && scaleInfo && line.polygon?.length >= 4;
                return (
                  <button key={wallId} data-testid={`wall-list-${i}`}
                    onClick={(e) => handleElementSelect({
                      ...line, id: wallId, type: 'wall',
                      name: wallName, label: wallName, color: '#dc2626',
                    }, e)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 border-b border-zinc-800/30 transition-colors text-left
                      ${isSelected ? 'bg-red-500/10 border-l-2 border-l-red-500' : 'hover:bg-zinc-800/50'}`}>
                    <div className="w-2.5 h-0.5 bg-red-500 flex-shrink-0"></div>
                    <div className="flex-1 min-w-0">
                      <span className="font-mono text-xs text-zinc-300">{wallName}</span>
                    </div>
                    <span className={`font-mono text-[10px] font-bold ${isCalibrated || hasApiScale || line.length_m ? 'text-green-400' : 'text-red-400'}`}>
                      {lengthDisplay}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Openings list */}
          {filteredSvgData?.openings?.length > 0 && (
            <div className="border-b border-zinc-800">
              <button onClick={() => toggleSection('openings')} data-testid="toggle-openings-section"
                className="w-full px-3 py-2 bg-zinc-800/30 flex items-center justify-between hover:bg-zinc-800/50 transition-colors">
                <span className="font-mono text-[10px] text-zinc-500 uppercase tracking-wider">
                  Öffnungen ({filteredSvgData.openings.length})
                </span>
                {collapsedSections.openings
                  ? <ChevronRight className="w-3.5 h-3.5 text-zinc-500" />
                  : <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />}
              </button>
              {!collapsedSections.openings && filteredSvgData.openings.map((opening) => (
                <button key={opening.id}
                  onClick={(e) => handleElementSelect(opening, e)}
                  className={`w-full flex items-center gap-3 px-3 py-2 border-b border-zinc-800/30 transition-colors text-left
                    ${selectedElements.some(el => el.id === opening.id) ? 'bg-green-500/10' : 'hover:bg-zinc-800/50'}`}>
                  <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: resolveOpeningColor(opening) }}></div>
                  <div className="flex-1 min-w-0">
                    <span className="font-mono text-xs text-zinc-300">{opening.name || (opening.type === 'door' ? 'Tür' : 'Fenster')}</span>
                  </div>
                  {(opening.width_m_matched || opening.width_m_calibrated || opening.width_m) && (
                    <span className={`font-mono text-[10px] font-bold ${opening.width_source?.includes('pdf') ? 'text-green-400' : 'text-zinc-400'}`}>
                      {opening.width_m_matched || opening.width_m_calibrated || opening.width_m} m
                      {opening.height_m_matched && ` × ${opening.height_m_matched} m`}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* ─── WANDGRUPPEN (auto-classified by envelope proximity) ─── */}
          {filteredSvgData?.wall_lines?.length > 0 && (() => {
            const exterior = filteredSvgData.wall_lines.filter(w => w.wall_type === 'exterior');
            const interior = filteredSvgData.wall_lines.filter(w => w.wall_type === 'interior');
            const unknown = filteredSvgData.wall_lines.filter(w => !w.wall_type || w.wall_type === 'unknown');
            const hasClassification = exterior.length > 0 || interior.length > 0;
            const totalLenExt = exterior.reduce((s, w) => s + (w.length_m || w.length_m_calibrated || w.length_m_final || 0), 0).toFixed(1);
            const totalLenInt = interior.reduce((s, w) => s + (w.length_m || w.length_m_calibrated || w.length_m_final || 0), 0).toFixed(1);

            return (
              <div data-testid="wall-groups-section">
                <button onClick={() => toggleSection('wallGroups')} data-testid="toggle-wall-groups"
                  className="w-full px-3 py-2 bg-zinc-800/30 flex items-center justify-between hover:bg-zinc-800/50 transition-colors">
                  <span className="font-mono text-[10px] text-zinc-500 uppercase tracking-wider">
                    Wandgruppen
                  </span>
                  {collapsedSections.wallGroups
                    ? <ChevronRight className="w-3.5 h-3.5 text-zinc-500" />
                    : <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />}
                </button>
                {!collapsedSections.wallGroups && (
                  <div className="px-3 py-2 space-y-2">
                    <button
                      data-testid="auto-classify-btn"
                      onClick={async () => {
                        try {
                          const resp = await fetch(`${API_URL}/api/analyses/${result.analysis_id}/classify-walls`, { method: 'POST' });
                          if (resp.ok) {
                            // Reload analysis
                            const reloadResp = await fetch(`${API_URL}/api/analyses/${result.analysis_id}`);
                            if (reloadResp.ok) {
                              setResult(await reloadResp.json());
                            }
                          }
                        } catch (e) {
                          console.error('Classification failed:', e);
                        }
                      }}
                      className="w-full py-1.5 px-2 rounded text-[11px] font-mono bg-amber-500/20 text-amber-300 border border-amber-500/30 hover:bg-amber-500/30 transition-colors">
                      {hasClassification ? 'Neu klassifizieren' : 'Automatisch klassifizieren'}
                    </button>

                    <button
                      data-testid="calibrate-thickness-btn"
                      onClick={async () => {
                        try {
                          const resp = await fetch(`${API_URL}/api/analyses/${result.analysis_id}/calibrate-thickness`, { method: 'POST' });
                          if (resp.ok) {
                            const data = await resp.json();
                            if (data.status === 'no_texts') {
                              modal.alert('Keine PDF-Textdaten verfügbar für diese Analyse.', { title: 'Hinweis', type: 'info' });
                            } else {
                              // Reload analysis to show updated thicknesses
                              const reloadResp = await fetch(`${API_URL}/api/analyses/${result.analysis_id}`);
                              if (reloadResp.ok) {
                                setResult(await reloadResp.json());
                              }
                            }
                          }
                        } catch (e) {
                          console.error('Thickness calibration failed:', e);
                        }
                      }}
                      className="w-full py-1.5 px-2 rounded text-[11px] font-mono bg-teal-500/20 text-teal-300 border border-teal-500/30 hover:bg-teal-500/30 transition-colors">
                      Wanddicke kalibrieren
                    </button>

                    {/* Außenwände */}
                    <div className={`rounded border transition-colors ${
                      exterior.length > 0 && exterior.every(w => selectedElements.some(el => el.id === w.id))
                        ? 'border-orange-400/50 bg-orange-500/15' : 'border-orange-500/20 bg-orange-500/5'}`}>
                      <div className="flex items-center">
                        <button
                          data-testid="select-exterior-group"
                          onClick={() => {
                            const allSelected = exterior.every(w => selectedElements.some(el => el.id === w.id));
                            if (allSelected) {
                              setSelectedElements([]);
                            } else {
                              setSelectedElements(exterior.map(w => ({
                                ...w, type: 'wall', name: w.name || w.id, label: w.name || w.id, color: '#3b82f6',
                              })));
                            }
                          }}
                          className="flex-1 px-2 py-1.5 flex items-center gap-2 text-left hover:bg-orange-500/10 transition-colors rounded-tl"
                          title="Alle Außenwände markieren">
                          <div className="w-2.5 h-0.5 bg-orange-400 rounded-full"></div>
                          <span className="font-mono text-[11px] text-orange-300 font-medium">Außenwände ({exterior.length})</span>
                        </button>
                        <span className="font-mono text-[10px] text-orange-400/70 px-2">{totalLenExt} m</span>
                        <button onClick={() => toggleSection('extWalls')}
                          className="p-1.5 hover:bg-orange-500/10 transition-colors rounded-tr"
                          data-testid="toggle-ext-walls">
                          {collapsedSections.extWalls
                            ? <ChevronRight className="w-3 h-3 text-orange-400/50" />
                            : <ChevronDown className="w-3 h-3 text-orange-400/50" />}
                        </button>
                      </div>
                      {!collapsedSections.extWalls && exterior.length > 0 && (
                        <div className="border-t border-orange-500/10 max-h-32 overflow-y-auto">
                          {exterior.map(w => (
                            <button key={w.id}
                              onClick={(e) => handleElementSelect({
                                ...w, type: 'wall', name: w.name || w.id, label: w.name || w.id, color: '#3b82f6',
                              }, e)}
                              className={`w-full flex items-center gap-2 px-2 py-1 text-left text-[10px] font-mono transition-colors
                                ${selectedElements.some(el => el.id === w.id) ? 'bg-orange-500/15' : 'hover:bg-orange-500/5'}`}>
                              <span className="text-zinc-400 truncate flex-1">{w.name || w.id}</span>
                              <span className="text-orange-400/70">{w.length_m || w.length_m_calibrated || w.length_m_final || '?'} m</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Innenwände */}
                    <div className={`rounded border transition-colors ${
                      interior.length > 0 && interior.every(w => selectedElements.some(el => el.id === w.id))
                        ? 'border-blue-400/50 bg-blue-500/15' : 'border-blue-500/20 bg-blue-500/5'}`}>
                      <div className="flex items-center">
                        <button
                          data-testid="select-interior-group"
                          onClick={() => {
                            const allSelected = interior.every(w => selectedElements.some(el => el.id === w.id));
                            if (allSelected) {
                              setSelectedElements([]);
                            } else {
                              setSelectedElements(interior.map(w => ({
                                ...w, type: 'wall', name: w.name || w.id, label: w.name || w.id, color: '#3b82f6',
                              })));
                            }
                          }}
                          className="flex-1 px-2 py-1.5 flex items-center gap-2 text-left hover:bg-blue-500/10 transition-colors rounded-tl"
                          title="Alle Innenwände markieren">
                          <div className="w-2.5 h-0.5 bg-blue-400 rounded-full"></div>
                          <span className="font-mono text-[11px] text-blue-300 font-medium">Innenwände ({interior.length})</span>
                        </button>
                        <span className="font-mono text-[10px] text-blue-400/70 px-2">{totalLenInt} m</span>
                        <button onClick={() => toggleSection('intWalls')}
                          className="p-1.5 hover:bg-blue-500/10 transition-colors rounded-tr"
                          data-testid="toggle-int-walls">
                          {collapsedSections.intWalls
                            ? <ChevronRight className="w-3 h-3 text-blue-400/50" />
                            : <ChevronDown className="w-3 h-3 text-blue-400/50" />}
                        </button>
                      </div>
                      {!collapsedSections.intWalls && interior.length > 0 && (
                        <div className="border-t border-blue-500/10 max-h-32 overflow-y-auto">
                          {interior.map(w => (
                            <button key={w.id}
                              onClick={(e) => handleElementSelect({
                                ...w, type: 'wall', name: w.name || w.id, label: w.name || w.id, color: '#3b82f6',
                              }, e)}
                              className={`w-full flex items-center gap-2 px-2 py-1 text-left text-[10px] font-mono transition-colors
                                ${selectedElements.some(el => el.id === w.id) ? 'bg-blue-500/15' : 'hover:bg-blue-500/5'}`}>
                              <span className="text-zinc-400 truncate flex-1">{w.name || w.id}</span>
                              <span className="text-blue-400/70">{w.length_m || w.length_m_calibrated || w.length_m_final || '?'} m</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Unklassifiziert */}
                    {unknown.length > 0 && (
                      <div className="rounded border border-zinc-700/30 bg-zinc-800/20">
                        <div className="px-2 py-1.5 flex items-center justify-between">
                          <span className="font-mono text-[11px] text-zinc-500">Unklassifiziert ({unknown.length})</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Image info */}
          <div className="mt-auto border-t border-zinc-800 px-3 py-3">
            <span className="font-mono text-[10px] text-zinc-500 uppercase tracking-wider">Bild-Info</span>
            <div className="mt-2 space-y-1 font-mono text-xs">
              <div className="flex justify-between">
                <span className="text-zinc-500">Größe</span>
                <span className="text-zinc-300">{result?.image_size?.width}x{result?.image_size?.height}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Datei</span>
                <span className="text-zinc-300 truncate max-w-[120px]">{result?.filename}</span>
              </div>
            </div>
          </div>
        </aside>
      </div>

      {showHistory && <HistorySidebar analyses={analyses} onSelect={loadAnalysis} onClose={() => setShowHistory(false)} onDelete={deleteAnalysis} />}
      {editingElement && (
        <ElementEditModal element={editingElement} onSave={handleSaveElementEdit} onClose={() => setEditingElement(null)} svgCalibration={result?.svg_data?.calibration} />
      )}
      <LVEditorModal
        open={lvEditorOpen}
        onClose={() => setLvEditorOpen(false)}
        analysisId={result?.analysis_id || result?.id}
        elementIds={lvEditorElementIds}
      />
    </div>
  );
}
